'use client';

// ПРАВАЯ ШТОРКА ШАГА чат-сценария (MINIMAL-PRODUCT В5-ОДИН-В-ОДИН, образец — ChatPlace):
// вместо формы узла n8n («Ресурс», «Операция», «Разметка ответа») клиент правит СМЫСЛ —
// текст сообщения и кнопки, ОДНО условие, ОДНО действие, что уйдёт в заявку CRM, какой
// товар выставить к оплате, когда запускать сценарий. Канал не спрашиваем: он выбран
// запуском сценария (В5.2 правило 2). Маппинг — чистый @scriptera/ui/scenario/chat-step.ts.
//
// ЭКРАНА ЦЕЛОГО БЛОКА «Условие»/«Действие» ЗДЕСЬ НЕТ (владелец 2026-08-12): их карточки
// правятся плашками — тап открывает настройку именно того, во что тапнули (CondRowEditor —
// ОДНО условие шага «Если»; ActionRowEditor — один шаг цепочки). Группа условий
// («Все условия / Любое из условий» и «Добавить условие» в неё) правится прямо в пузыре
// пилюли НА КАРТОЧКЕ (У16, step-content-view), а каталог открывает use-draft-editor
// (openRowCatalog) — модалки не наслаиваются одна на другую.
//
// Эталоны вида: data/chatplace/ui-recon/17-condition-catalog.png и 18-condition-rows.png
// (форма условия + каталог «+ Условие»), 19-action-catalog.png и 20-action-rows.png
// (форма действия + каталог «+ Действие»), 13-start-conditions.png (условия запуска).
//
// «ТЕХНИЧЕСКИХ НАСТРОЕК» здесь НЕТ (приказ владельца 30.07, повторён трижды): полная форма
// узла остаётся только у шагов ВНЕ чат-набора — их открывает NodeSettings как раньше.
import {
  createContext,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
  CHANNEL_RULES,
  IG_BUBBLE_BUTTONS_MAX,
  CHAT_LEAD_RECEIVERS,
  CHAT_PAY_PROVIDERS,
  CHAT_REACTIONS,
  CHAT_TG_ACTIONS,
  chatLeadReceiverMeta,
  chatLeadReceiverSpec,
  chatPayProviderMeta,
  chatPayProviderOfTrigger,
  chatPayProviderSpec,
  readChatPayLink,
  writeChatPayLink,
  chatAttachLimit,
  chatAttachPlace,
  chatContentNameKey,
  chatEditorKind,
  CHAT_ACTION_KINDS,
  chatPayPriceNumber,
  chatScreenKey,
  chatPayIssue,
  chatPayModelOf,
  chatStartKindOf,
  chatUntilValue,
  IG_START_EVENTS,
  igStartEventRule,
  BROADCAST_MODES,
  BROADCAST_UI_MODES,
  broadcastModeRule,
  readBroadcastStart,
  writeBroadcastStart,
  type BroadcastMode,
  chatUntilWallClock,
  chatUrlBroken,
  chatValueOf,
  condOpIsFilled,
  condOpIsNumeric,
  condOpsFor,
  condValueRow,
  condRowDaysError,
  condRowLegacy,
  condRowPhoneWait,
  condRowNameError,
  condRowNumberError,
  condRowPaidError,
  condPillIndex,
  condRowIndex,
  condRowsForCanon,
  condRowRangeError,
  condRowWhenError,
  condRowIssue,
  CHAT_MSG_TEXT_OPS,
  condRowValueError,
  condTimeAllDay,
  condWhenRow,
  chatCondsFor,
  condPickNameKey,
  condPickRow,
  condRowPickId,
  dayText,
  newCondRow,
  readAiModelName,
  readChatAction,
  readChatAi,
  readChatSwitch,
  writeChatSwitch,
  chatHttpBodyError,
  readChatContacts,
  isTagsCheckNode,
  readChatDelay,
  readChatEditMsg,
  readChatHttp,
  readChatLead,
  readChatMessage,
  readChatPay,
  readChatReaction,
  readChatSaveMsg,
  readChatScreen,
  readChatStart,
  readChatSubscribe,
  readPhoneMode,
  writePhoneMode,
  isPhoneWaitNode,
  phoneModesFor,
  type ChatPhoneMode,
  tgChannelAddress,
  chatButtonKinds,
  writeChatAction,
  writeChatAi,
  writeChatContacts,
  writeChatHttp,
  writeChatDelay,
  writeChatEditMsg,
  writeChatLead,
  writeChatMessage,
  writeChatPay,
  writeChatReaction,
  writeChatSaveMsg,
  writeChatScreen,
  writeChatStart,
  writeChatSubscribe,
  botAccountLabel,
  botAccountState,
  credRefById,
  bodyTopEntries,
  customServiceCredType,
  CUSTOM_SERVICE_METHODS,
  readChatValue,
  readResponseFields,
  readServiceValues,
  responseFieldsParams,
  serviceEditorKind,
  serviceHttpCredId,
  serviceHttpParams,
  writeChatValue,
  type CustomServiceMeta,
  type CustomServiceOutput,
  type ChatAiModel,
  type ChatAttachKind,
  type ChatButton,
  type ChatChannelId,
  type ChatContactsModel,
  type ChatContactsOp,
  type ChatCondModel,
  condRemoveRow,
  type CondRemoveResult,
  type ChatCondPickId,
  type ChatCondGroupId,
  type ChatSwitchModel,
  type ChatCondOp,
  type ChatCondRow,
  type CondIssue,
  type ChatCondWhen,
  type ChatCondChannel,
  type ChatPayModel,
  type ChatEditMsgModel,
  type ChatSaveMsgModel,
  type ChatDelayMode,
  type ChatDelayModel,
  type ChatDelayUnit,
  type ChatHttpHeader,
  type ChatHttpMethod,
  type ChatHttpModel,
  type ChatLeadModel,
  type ChatLeadReceiver,
  type ChatLeadRow,
  type ChatPayLinkModel,
  type ChatPayProvider,
  type ChatScreenButton,
  type ChatScreenButtonKind,
  type ChatScreenField,
  type ChatScreenFieldKind,
  type ChatScreenModel,
  type ChatMessageModel,
  type ChatSlot,
  type ChatStartKind,
  type ChatStartModel,
  type ChatValue,
  type ChatVarOp,
  type IgStartEvent,
  type InstanceCredRef,
  type Lang,
  type StepAttachment,
  type StepRowIcon,
  type WorkflowNode,
} from '@scriptera/ui/scenario';
import { SmartSelect, type SmartOption } from '@scriptera/ui';
import { usePersistentData } from '@/lib/use-persistent-data';
import { mimeWords } from '@/lib/mime-words';
import { AddBlue, COND_HINT_KEYS } from './chat-card-edit';
import { condRowNote } from './cond-note';
import { ConfirmInline } from './confirm-inline';
import { PayProductPicker, ProductSelect } from './pay-product-picker';
import { refreshCustomServices } from './custom-service-form';
import { CUSTOM_SERVICE_DEF } from './connection-catalog';
import { translitRefCode } from '@/lib/bot-ref-core';
import { wizLinkReadyText } from '@/lib/template-wizards/core';
import { IgMediaChosen, IgMediaPicker } from './ig-media-picker';
import { BottomSheet } from './bottom-sheet';
import { CopyBtn } from './copy-btn';
import {
  FBlock,
  FChoiceBlock,
  FChoiceManyBlock,
  FError,
  FFormError,
  FField,
  FHint,
  FIn,
  FNotifyList,
  FPane,
  KBareButton,
  KButton,
  KInput,
  KSwitch,
} from './form-kit';
import { useSlidePanel } from './native-layer-host';
import { usePhoneLook } from './layout-mode';
import { DateRange, DateTimePick, TimePick } from './date-range';
import { CondValuePicker, VariablePicker, VarInsertMenu } from './variable-select';
import { TagPicker } from './tag-picker';
// Старых обёрток поля (DField) и вкладок (DSegments) в файле больше НЕТ: формы шагов
// собраны из набора пузырей (form-kit) — FORM-DEBT 1.3.
import { DError, DGroup, DHint, DList, DRow, DWarn } from './drawer-kit';
import { PlusGlyph } from './plus-glyph';
import { AttachGlyph, RowGlyph } from './step-content-view';
// Мастер настройки запуска (CABINET-UX-FIXLIST п.6): хиро и нижняя планка — ОБЩАЯ
// оболочка мастеров, та же, что у мастера быстрой настройки. Своей копии не заводим.
import { WizFoot, WizHero } from './wizard-shell';
// РАССЫЛКА (ТЗ 12): «кому» — общий набор фильтров аудитории и общий выбор метки
// (audience-screen, MESSAGE-COMPOSER И3), кнопки запуска — общий набор (broadcast-launch).
// Своих копий ни того, ни другого здесь нет.
import { TagPickRow } from './audience-filter-sheet';
import { AudienceRow } from './audience-screen';
import { audienceOfRaw, audienceQuery, type AudienceValue } from './audience-value';
import { BroadcastActs, useBroadcastLaunch } from './broadcast-launch';
import { nativeBridge, nativeMethod } from '@/lib/native-app';

/** Когда диспетчер отдаёт сообщение этому сценарию (настройка черновика, не узла). */
export interface ChatStartMeta {
  /**
   * 'exact' — «Определённое сообщение» (совпадение целиком), 'keyword' — «содержит»,
   * 'link' — «Переход по ссылке» (К37 А4): сценарий запускает не текст, а собственный
   * код ссылки t.me/<бот>?start=<код> из реестра — так разные ссылки ведут в разные
   * сценарии одного бота.
   */
  kind: 'message' | 'start' | 'keyword' | 'exact' | 'link';
  keywords: string[];
  /**
   * Не запускать сценарий повторно столько минут (0 — без ограничения). Раньше поле жило
   * в «О сценарии» вторым стейтом того же значения (владелец поймал рассинхрон на живых
   * скринах) — теперь ЕДИНСТВЕННОЕ место настройки запуска здесь, в шаге «Старт».
   */
  antiRepeatMin: number;
  /**
   * «Работать без прерывания» (К11): пока человек ведёт этот сценарий, другие сценарии
   * бота его не перехватывают. holdTimeoutMin — выход по молчанию (по умолчанию сутки):
   * вечного замка не бывает.
   */
  holdDialog: boolean;
  holdTimeoutMin: number;
  /**
   * Выбранные публикации Instagram (К37 Z4, В1): под какими постами слушать комментарии.
   * Пусто = под всеми. Выбор возможен ТОЛЬКО у триггера-комментария (правило ЧП 10500036),
   * поэтому смена события чистит список — мёртвых настроек не держим.
   */
  mediaIds: string[];
}

/**
 * КАКОЕ УСЛОВИЕ карточки правим (владелец 13.08, У16). Ни экрана целого блока «Условие»,
 * ни панели пилюли-ГРУППЫ нет: модалка открывается тапом по строке условия в пузыре
 * пилюли (или сразу после выбора в каталоге) и знает ровно одно условие. Метки-id
 * надёжнее номеров: соседнее условие или пилюлю могли убрать, и номер промахнулся бы.
 */
export interface ChatCondRowRef {
  /** Номер пилюли (группы условий со своим выходом) на карточке. */
  pill: number;
  /** id первого условия пилюли — надёжная метка группы. */
  pillId?: string;
  /** id самого условия внутри группы. */
  rowId?: string;
  /** Номер условия в группе — запасной путь, когда id ещё нет (условие только что
   *  вписано в шаг и своего id из канона пока не получило). */
  row?: number;
  /**
   * Модалка ЗАВОДИТ новое условие этого ПУНКТА каталога в конце группы (выбор из
   * каталога). В шаге его пока нет: у пустого условия не бывает формулы, поэтому в
   * сценарий (и на карточку) оно попадает, как только человек его дописал. Бросил на
   * полпути — мусора не осталось.
   */
  add?: ChatCondPickId;
  /**
   * Готовая формула нового условия (У19): её нельзя собрать «на месте» — она смотрит в
   * событие шага запуска, а имя шага знает только редактор («Прислал номер кнопкой»).
   * Пусто — формулу пишет сам ряд по своим полям.
   */
  addExpr?: string;
  /**
   * ПУТЬ к тексту входящего для нового «Текста сообщения» — та же история, что у формулы
   * выше: имя шага запуска и поле события знает только редактор (condPickAt).
   */
  addAt?: string;
  /**
   * Модалка заводит НОВУЮ ПИЛЮЛЮ (свою ветку карточки) — её в схеме ещё нет (У21,
   * владелец 14.08). Пилюля со своим выходом появится в каноне только вместе с
   * дописанным условием, одним структурным ходом по галочке; закрыл модалку не
   * сохранив — в схеме не осталось ни пустой пилюли, ни лишней ветки.
   */
  newPill?: true;
  /**
   * ОКНО ГРУППЫ каталога (Е1, владелец 13.08 вечер): «Дата и время» и «Состояние
   * диалога» настраиваются не по одному условию, а всей группой сразу — три секции
   * (время суток, даты, дни недели) и строки с галочками. Задано — модалка открывает
   * общее окно группы этой пилюли: отмеченные секции/строки и есть её условия, а
   * галочка шапки пишет их в схему (1–3 ряда одной правкой). Не задано — обычная
   * модалка ОДНОГО условия, как раньше.
   */
  group?: ChatCondGroupId;
}

/** Состояние, которое ждёт панель настроек (use-draft-editor): имя шага + параметры узла. */
export interface ChatStepState {
  name: string;
  params: Record<string, unknown>;
  /** «Старт»: когда запускать — уезжает отдельным запросом (это настройка сценария). */
  meta?: ChatStartMeta;
  /** «Старт»: аккаунт, от чьего имени работает сценарий (К34). undefined — шаг аккаунт
   *  не трогает, слепок держит прежний (аккаунт правят ТОЛЬКО на «Старте»). Исключение —
   *  «Интеграция с API»: выбор сервиса тоже вешает его доступ на шаг. */
  credentialId?: string | null;
  /** «Интеграция с API»: параметры шага-довеска «Разбор ответа» (custom-service.ts):
   *  объект — довесок нужен с такими параметрами, null — убрать, undefined — не трогать.
   *  Вставку/правку/удаление довеска делает commit в use-draft-editor. */
  companion?: Record<string, unknown> | null;
  /** За КАКИМ шагом стоит этот довесок (Э1: в карточке-стопке запрос — не обязательно
   *  голова). Не задан — за самим шагом карточки. */
  companionFor?: string;
  /**
   * Правки ОСТАЛЬНЫХ шагов карточки-стопки действий (Э1): id шага → его параметры.
   * Голова карточки едет обычным `params`; сюда попадают все, кого человек тронул.
   */
  stackParams?: Record<string, Record<string, unknown>>;
  /** «Старт», запуск «Переход по ссылке» в шторке СОЗДАНИЯ (черновика ещё нет,
   *  владелец 2026-08-04, IMG_1261): подпись ссылки — по «Создать» из неё транслитом
   *  заводится ссылка реестра (/api/cabinet/ref-links). В конструкторе не участвует —
   *  там ссылки правит StartLinkList на живом черновике. */
  linkCode?: string;
  /** Шаг настроен НЕВЕРНО — галочка шапки обязана оставить шторку открытой (владелец
   *  2026-08-06: «должна быть валидация, я могу нажать галочку, она закрывается»). */
  hasIssues?: boolean;
  /**
   * Пилюли карточки «Условие» так, как их видит модалка (У21). Нужны, когда модалка
   * заводит НОВУЮ пилюлю: в схеме её ещё нет, и записать её можно только структурной
   * правкой (пилюля + её выход), а не параметрами шага. Разбирать `params` обратно для
   * этого нельзя: у карточки-«Если» они уже в форме развилки, а тип шага пока прежний —
   * читать их «как развилку» или «как Если» одинаково нечестно, и любая ошибка разбора
   * молча съедала бы правку. Поэтому модель едет отдельным полем, как есть.
   */
  condPills?: ChatCondModel[];
  /** Зовётся, когда по галочке шаг НЕ прошёл проверку: шторка показывает ошибку сверху
   *  и подсвечивает поле. До первой попытки сохранить ошибок человек не видит. */
  onInvalid?: () => void;
}

/** Общий тип «правки ушли в канон»: имя шага не меняем — его правят в полной форме. */
type Push = (params: Record<string, unknown>) => void;

/** Язык кабинета — на нём подписаны заглушки подстановок в тексте (chat-text.ts). */
function useLang(): Lang {
  return useLocale() === 'en' ? 'en' : 'ru';
}

/**
 * Тип запуска Инстаграма у ЭТОГО сценария (К37-Z5). Держим контекстом, а не пропом: каталог
 * «+ Действие» живёт в самом низу пяти разных редакторов, и протаскивать через каждый ещё
 * одно поле — верный способ где-нибудь его забыть и показать мёртвый пункт.
 */
const IgEventCtx = createContext<readonly IgStartEvent[]>([]);

/**
 * Формы шагов, ПЕРЕЕХАВШИЕ на набор пузырей (form-kit): их корень — FPane, и вид у них
 * ровно тот же, что у мастера шаблона. Список только РАСТЁТ — по мере перевода
 * редакторов; убирать из него вид, оставив в нём пузыри, нельзя: без .fk-pane пузыри
 * не красятся.
 */
const FORM_KIT_KINDS = new Set<string>([
  'start',
  'message',
  'condition',
  'contacts',
  'subscribe',
  // Э3: правка отправленного пузыря и закладка на него собраны из тех же пузырей.
  'editMsg',
  'saveMsg',
  // Заявка CRM и оплата (FORM-DEBT 1.1): пузыри набора, без старых обёрток DField.
  'crmLead',
  'pay',
  // Ряды стопки действий: запрос к сервису и вопрос модели (FORM-DEBT 1.2).
  'http',
  'ai',
  // Страница формы, пауза, «печатает…» и реакция — последние формы файла (FORM-DEBT 1.3).
  'screen',
  'delay',
  'chatAction',
  'reaction',
]);

export function ChatStepSettings({
  node,
  stackNodes,
  onState,
  checkNode,
  modelNode,
  modelNodes,
  onOpenNode,
  startMeta,
  condRow,
  onRemoveCond,
  actionChannel,
  condChannel,
  condStartName,
  hasPhoneAsk,
  hasPayOffer,
  payProducts,
  onStartKind,
  startKinds,
  draftId,
  onAddContent,
  onPickFile,
  creds,
  igEvents,
  botCredId,
  onSwitchStep,
  onConnect,
  onConnectAccount,
  onFinish,
  finishLabel,
  onWizStage,
  services,
  companionNode,
}: {
  node: WorkflowNode;
  /**
   * ВСЕ шаги карточки по порядку (первый — сам node): по ним «Сообщение» находит свою
   * вклеенную пометку «ждём телефон» (У9). Не задан — карточка из одного шага.
   */
  stackNodes?: readonly WorkflowNode[];
  onState: (s: ChatStepState) => void;
  /**
   * Шаг-проверка подписки, вклеенный в эту карточку условия (chat-merge.ts): своей
   * карточки у него нет, поэтому «на какой канал проверяем» открывается отсюда.
   */
  checkNode?: WorkflowNode | null;
  /** Шаг МОДЕЛИ в гнезде «Запроса к ИИ» (К6): его имя видно в редакторе помощника. */
  modelNode?: WorkflowNode | null;
  /** Э1: шаги моделей по id шага-помощника — в стопке «Запросов к ИИ» может быть больше одного. */
  modelNodes?: Record<string, WorkflowNode>;
  /** Открыть настройки другого шага (вклеенной проверки, шага модели). */
  onOpenNode?: (nodeId: string) => void;
  /** «Старт»: текущие настройки запуска сценария на боте (ЕДИНСТВЕННОЕ место). */
  startMeta?: ChatStartMeta;
  /**
   * Карточка «Условие»: КАКОЕ её условие правим (владелец 13.08, У16). Модалка настраивает
   * ровно одно условие — ни экрана целого блока, ни панели группы не существует, поэтому
   * без этой метки условие не открывается вовсе.
   */
  condRow?: ChatCondRowRef | null;
  /**
   * Убрать ЭТО условие (У21): корзина модалки. Стоит В ТЕЛЕ, а не в шапке — в шапке
   * кабинета живут только крестик и галочка (владелец 2026-08-11), и любой onTrash там
   * молча не рисуется.
   *
   * Что останется на карточке, считает САМА МОДАЛКА по своей модели и отдаёт готовым
   * решением (`condRemoveRow`): у неё на руках ровно то, что человек видит на экране.
   * Прежде кабинет искал условие заново по меткам в живом каноне — и если метка не
   * находилась (страница отстала, пилюли ещё нет в схеме), корзина МОЛЧА закрывала
   * модалку, не послав ни одного запроса. Молчания больше нет: null — убирать нечего
   * (условие в схему ещё не уехало), объект — пишем.
   */
  onRemoveCond?: (plan: CondRemoveResult | null) => void;
  /**
   * Канал сценария целиком (свод правил каналов) — от него зависит СОСТАВ каталога
   * действий: шаги Телеграма («Запросить номер телефона», «печатает…») в сценарии формы,
   * чата на сайте и Инстаграма не показываются (К6).
   */
  actionChannel?: ChatChannelId | null;
  /**
   * Канал сценария (по шагу запуска) — от него зависит, есть ли в каталоге условий
   * «Подписка»: у чата на сайте и формы подписки не существует, пункт не показываем.
   */
  condChannel?: ChatCondChannel | null;
  /**
   * Имя шага запуска сценария ('' — шага запуска нет). Нужно окну группы «Состояние
   * диалога» (Е1): формулу «Прислал номер кнопкой» нельзя собрать на месте — она смотрит
   * в событие шага запуска, а его имя знает только сама схема.
   */
  condStartName?: string;
  /**
   * Есть ли в схеме действие «Запросить номер телефона» (владелец 13.08). Условие
   * «человек отвечает на просьбу о номере» без него не сработает никогда — панель
   * говорит об этом красной строкой (тем же текстом, что линт запуска).
   */
  hasPhoneAsk?: boolean;
  /**
   * Товары, которые предлагаются к покупке В ЭТОЙ схеме (Э3, владелец 14.08): их id из
   * живых шагов «Оплата» (payOfferProductIds). Условие «Оплата прошла» выбирает только из
   * них; пустой список — продажи в схеме нет вовсе, и условие не сработает никогда.
   */
  payProducts?: readonly string[];
  /**
   * Есть ли в схеме ПРОДАЖА (Э3.2): шаг «Оплата» или кнопка «Переход к оплате». Нет —
   * условию «Оплата прошла» взяться неоткуда, и окно говорит об этом красной строкой.
   */
  hasPayOffer?: boolean;
  /** «Старт»: сменить ТИП запуска плиткой (сообщение ↔ успешная оплата, К8). */
  onStartKind?: (kind: ChatStartKind) => void;
  /** Черновик — нужен галочке «Также запускать при переходе по ссылке» (К18). */
  draftId?: string;
  /** Какие плитки запуска доступны этому сценарию (оплата — не во всяком канале). */
  startKinds?: readonly ChatStartKind[];
  /** Подключённые доступы клиента — из них «Старт» выбирает аккаунт сценария (К34). */
  creds?: readonly InstanceCredRef[];
  /**
   * Вложение из редактора сообщения в канале-МЕССЕНДЖЕРЕ: файл уходит отдельным
   * сообщением, поэтому за этим шагом встаёт новый шаг-вложение (структурная правка —
   * её делает use-draft-editor, здесь только повод).
   */
  onAddContent?: (kind: ChatAttachKind, url: string) => void;
  /** Выбрать файл и залить его в наше хранилище; отдаёт ссылку нашего домена. */
  onPickFile?: (kind: ChatAttachKind) => Promise<{ url: string; name: string } | null>;
  /**
   * ВХОДЫ Инстаграма у бота (ТЗ 02.09 §3.4) — от них зависит, есть ли в каталоге
   * действий ответы на комментарий: без входа-комментария отвечать не на что.
   */
  igEvents?: readonly IgStartEvent[] | null;
  /**
   * Бот сценария (доступ telegramApi из «Старта», К34) — у НЕГО ищутся каналы для
   * «Проверки подписки» (Б3). null = бот не выбран: шаг честно говорит об этом, а не
   * показывает пустую выпадашку.
   */
  botCredId?: string | null;
  /**
   * Ф7: сменить ТИП узла этого шага с переносом параметров (селект «Куда» у «Отправить
   * заявку», платёжка у «Оплаты» и «Старта») — замену узла делает use-draft-editor
   * (struct replaceNode + запись параметров), по образцу смены типа запуска.
   */
  onSwitchStep?: (nodeType: string, params: Record<string, unknown> | null) => void;
  /** Ф7.3: «Подключить» из жёлтой плашки — уводит в раздел «Подключения». */
  onConnect?: () => void;
  /** «Старт»: подключить аккаунт канала прямо из формы (владелец 2026-08-11) —
   *  открывает форму подключения на месте (openConnect в use-draft-editor). */
  onConnectAccount?: () => void;
  /** «Старт» в виде МАСТЕРА (CABINET-UX-FIXLIST п.6): галочка последнего шага. Делает
   *  ровно то же, что подтверждение в шапке окна, — сохранить и закрыть. */
  onFinish?: () => void;
  /** Д5: галочка шапки — только на последнем шаге мастера «Старта» (см. StartEditor). */
  onWizStage?: (last: boolean | null) => void;
  /** Подпись финальной галочки мастера — та же, что у подтверждения в шапке окна. */
  finishLabel?: string;
  /** «Свои сервисы» клиента (custom-service.ts) — для шага «Интеграция с API»:
   *  null/undefined — список ещё не приехал (сервисный шаг не открываем вслепую). */
  services?: CustomServiceMeta[] | null;
  /** Шаг-довесок «Разбор ответа» этого запроса (спрятан в его карточку, chat-merge). */
  companionNode?: WorkflowNode | null;
}) {
  // Сборка своего сервиса не проходит readChatHttp (у неё авторизация кредом) —
  // редактор «Интеграции с API» открывается по привязанному креду сервиса.
  const kind = useMemo(
    () =>
      chatEditorKind(node) ??
      serviceEditorKind(node, services ? new Set(services.map((s) => s.credId)) : null),
    [node, services],
  );
  const push: Push = (params) => onState({ name: node.name, params });
  const chan = actionChannel ?? null;

  // «Условие» правит ДВА шага сразу (Э2): само «Если» и вклеенный в его карточку
  // шаг-проверку меток. Состояние панели одно, поэтому копим оба и отдаём вместе —
  // иначе последняя правка затирала бы предыдущую.
  const condParams = useRef<Record<string, unknown> | null>(null);
  const condCheck = useRef<Record<string, unknown> | null>(null);
  // У21: пилюли карточки, как их видит модалка. Новая пилюля живёт только в ней, и
  // записать её можно лишь структурной правкой — по этому списку (см. ChatStepState).
  const condPills = useRef<ChatCondModel[] | null>(null);
  // Э7.1: окно условия подключено к той же механике, что «Старт» — галочка шапки зовёт
  // применение, и пока условию чего-то не хватает, окно ОСТАЁТСЯ и поднимает флаг
  // «попытка была». До первой попытки красного в окне нет вовсе (владелец 14.08).
  const condIssue = useRef<CondIssue | null>(null);
  const [condTried, setCondTried] = useState(false);
  const emitCond = (): void =>
    onState({
      name: node.name,
      params: condParams.current ?? node.parameters ?? {},
      ...(condPills.current ? { condPills: condPills.current } : {}),
      ...(condCheck.current && checkNode ? { stackParams: { [checkNode.id]: condCheck.current } } : {}),
      hasIssues: condIssue.current !== null,
      onInvalid: () => setCondTried(true),
    });
  const pushCond = (params: Record<string, unknown>, pills?: ChatCondModel[]): void => {
    condParams.current = params;
    if (pills) condPills.current = pills;
    emitCond();
  };
  const pushCheckNode: Push = (params) => {
    condCheck.current = params;
    emitCond();
  };

  // У9: «Запросить номер телефона» — карточка-просьба плюс вклеенная в неё ПОМЕТКА
  // «ждём телефон» (в ней записан режим приёма ответа). Правятся оба шага сразу, поэтому
  // копим их вместе — иначе правка текста затирала бы только что выбранный режим.
  const phoneNode = (stackNodes ?? []).find((n) => isPhoneWaitNode(n)) ?? null;
  const msgParams = useRef<Record<string, unknown> | null>(null);
  const msgPhone = useRef<Record<string, unknown> | null>(null);
  const emitMsg = (): void =>
    onState({
      name: node.name,
      params: msgParams.current ?? node.parameters ?? {},
      ...(msgPhone.current && phoneNode ? { stackParams: { [phoneNode.id]: msgPhone.current } } : {}),
    });
  const pushMsg: Push = (params) => {
    msgParams.current = params;
    emitMsg();
  };
  const pushPhone: Push = (params) => {
    msgPhone.current = params;
    emitMsg();
  };

  // Карточка «Действие» — стопка плашек, но НАСТРАИВАЕТСЯ ОДНА (владелец 12.08): панель
  // открывается тапом по плашке и знает ровно её шаг. Общего экрана блока с лентой рядов
  // и «Добавить действие» внизу больше нет — добавляют с самой карточки.
  const actionRow = kind !== null && (CHAT_ACTION_KINDS as readonly string[]).includes(kind);

  const human = actionRow ? (
      <ActionRowEditor
        node={node}
        onState={onState}
        services={services ?? null}
        companionNode={companionNode ?? null}
        modelNode={modelNodes?.[node.id] ?? modelNode ?? null}
        onOpenNode={onOpenNode}
      />
    ) :
    kind === 'message' ? (
      <MessageEditor
        node={node}
        push={pushMsg}
        channel={chan}
        onAddContent={onAddContent}
        onPickFile={onPickFile}
        phoneNode={phoneNode}
        pushPhone={pushPhone}
      />
    ) : kind === 'screen' ? (
      <ScreenEditor node={node} push={push} channel={chan} onPickFile={onPickFile} />
    ) : kind === 'condition' ? (
      /* Экрана целого блока «Условие» и панели группы НЕ СУЩЕСТВУЕТ (У16): без метки
         условия открывать нечего — с карточки модалка приходит всегда с ней. */
      condRow?.group ? (
        /* Е1/Е2: у «Даты и времени» и «Состояния диалога» окно ОБЩЕЕ на группу — там
           секции и галочки, а не одно условие. */
        <CondGroupEditor
          node={node}
          row={condRow}
          group={condRow.group}
          push={pushCond}
          condChannel={condChannel ?? null}
          startName={condStartName ?? ''}
          hasPhoneAsk={hasPhoneAsk !== false}
          onRemove={onRemoveCond}
        />
      ) : condRow ? (
        <CondRowEditor
          node={node}
          row={condRow}
          push={pushCond}
          checkNode={checkNode ?? null}
          pushCheck={pushCheckNode}
          condChannel={condChannel ?? null}
          hasPhoneAsk={hasPhoneAsk !== false}
          hasPayOffer={hasPayOffer !== false}
          payProducts={payProducts ?? []}
          botCredId={botCredId ?? null}
          creds={creds ?? []}
          onConnect={onConnect}
          onRemove={onRemoveCond}
          tried={condTried}
          onIssue={(issue) => {
            condIssue.current = issue;
            emitCond();
          }}
        />
      ) : null
    ) : kind === 'subscribe' ? (
      <SubscribeEditor node={node} push={push} botCredId={botCredId ?? null} />
    ) : kind === 'start' ? (
      <StartEditor
        node={node}
        onState={onState}
        startMeta={
          startMeta ?? {
            kind: 'message',
            keywords: [],
            antiRepeatMin: 0,
            holdDialog: false,
            holdTimeoutMin: 1440,
            mediaIds: [],
          }
        }
        channel={chan}
        creds={creds ?? []}
        {...(draftId ? { draftId } : {})}
        {...(onStartKind ? { onStartKind } : {})}
        {...(startKinds ? { startKinds } : {})}
        {...(onSwitchStep ? { onSwitchStep } : {})}
        {...(onConnect ? { onConnect } : {})}
        {...(onConnectAccount ? { onConnectAccount } : {})}
        {...(onFinish ? { onFinish } : {})}
        {...(finishLabel ? { finishLabel } : {})}
        {...(onWizStage ? { onWizStage } : {})}
      />
    ) : kind === 'crmLead' ? (
      <LeadEditor node={node} push={push} creds={creds ?? []} botCredId={botCredId ?? null} onSwitchStep={onSwitchStep} onConnect={onConnect} />
    ) : kind === 'pay' ? (
      <PayEditor node={node} onState={onState} creds={creds ?? []} onSwitchStep={onSwitchStep} onConnect={onConnect} />
    ) : null;

  // Шаг вне чат-набора сюда не приходит (use-draft-editor открывает NodeSettings).
  if (!human) return null;
  return (
    <IgEventCtx.Provider value={igEvents ?? []}>
      {/* «Старт» — вид .iosf (владелец 05.08: дизайн боксов «из 2000-х» запрещён,
          новый вид ДОВОДИТСЯ, не откатывается): значение селекта стоит ПОД серой
          подписью на всю ширину — длинные значения влезают целиком. */}
      {/* Ряд действия одевается САМ (виды рядов разные) — общей обёртки у него нет. */}
      {actionRow ? (
        human
      ) : kind && FORM_KIT_KINDS.has(kind) ? (
        /* Переехавшие формы — на наборе пузырей (FPane, владелец 2026-08-11): те же
           правила вида, что у мастера шаблона (kit.css, скоуп .fk-pane).
           «Старт» диалогового канала открыт МАСТЕРОМ (CABINET-UX-FIXLIST п.6) — ему
           нужен корень .wiz-pane: за это имя держатся правила нижней планки и её
           клиренса (cabinet.css: `.dr-body:has(> .wiz-pane)`, `.wiz-pane` padding-bottom).
           Вид формы от этого не меняется — оба имени идут одним набором правил
           (`:is(.wiz-pane, .fk-pane)` в kit.css). */
        <FPane wiz={kind === 'start' && (chan === 'telegram' || chan === 'instagram')}>{human}</FPane>
      ) : (
        <DGroup>{human}</DGroup>
      )}
    </IgEventCtx.Provider>
  );
}

/** Пунктирная кнопка «добавить…» (строки заявки CRM, кнопки сообщения). */
function AddDashed({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="scn-add-dashed" onClick={onClick}>
      <PlusGlyph />
      {label}
    </button>
  );
}

/** Корзина ряда (шторки ЧП 18/20 — справа у названия ряда). Линейный SVG, канон no-emoji. */
function TrashGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6" />
    </svg>
  );
}

/**
 * Шапка ряда настроек в шторке (ЧП 18/20): значок + название вида, справа корзина.
 * Ряды идут стопкой, у каждого — свои поля под шапкой.
 */
function RowHead({
  icon,
  label,
  right,
  onRemove,
  removeLabel,
}: {
  icon: StepRowIcon;
  label: ReactNode;
  right?: ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
}) {
  return (
    <div className="scn-erow-head">
      <span className="scn-erow-ico" aria-hidden>
        <RowGlyph icon={icon} size={16} />
      </span>
      <b className="scn-erow-name">{label}</b>
      {right}
      {onRemove ? (
        <button type="button" className="scn-erow-del" onClick={onRemove} aria-label={removeLabel} title={removeLabel}>
          <TrashGlyph />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Список слов ЧИПАМИ (эталон ЧП 13 — «Срабатывать на … ключевые слова»): каждое слово —
 * плашка с крестиком, новое добавляется по Enter (или запятой), Backspace в пустом поле
 * убирает последнее. Дубли и пустые не заводим.
 */
export function ChipsInput({
  values,
  onChange,
  placeholder,
  ariaLabel,
  removeLabel,
  addLabel,
  invalid = false,
}: {
  values: readonly string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  ariaLabel: string;
  removeLabel: string;
  /** Подпись плюса «добавить ещё слово» (видна только с клавиатуры и читалкой). */
  addLabel: string;
  /** Поле не заполнено, а без него шаг не сохранить — подсветить красным. */
  invalid?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const push = (raw: string): void => {
    const word = raw.trim();
    setDraft('');
    if (word === '' || values.includes(word)) return;
    onChange([...values, word]);
  };
  return (
    <div className={`scn-chips${invalid ? ' err' : ''}`}>
      {values.map((w) => (
        <span key={w} className="scn-chip">
          {w}
          <button
            type="button"
            aria-label={`${removeLabel}: ${w}`}
            title={removeLabel}
            onClick={() => onChange(values.filter((x) => x !== w))}
          >
            <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </span>
      ))}
      {/* Плюс рядом с уже введённым словом (владелец 2026-08-06: «чтобы у человека было
          явное понимание, что там можно нажать» — иначе непонятно, что слов бывает
          несколько). Пустое поле в подсказке не нуждается: там стоит плейсхолдер. */}
      {values.length > 0 ? (
        <button
          type="button"
          className="scn-chip-add"
          aria-label={addLabel}
          title={addLabel}
          onClick={() => inputRef.current?.focus()}
        >
          <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      ) : null}
      <input
        ref={inputRef}
        value={draft}
        maxLength={60}
        // Подсказка («хочу») стоит и при набранных словах — как в родном мастере
        // (WizardScreen.chipsInput, кадр iOS W13-igkw-trigger-final): строка ввода всегда
        // одна и та же, слова кружками под ней.
        placeholder={placeholder}
        aria-label={ariaLabel}
        // Клавиатура айфона: подпись клавиши — «готово», а не перевод строки (владелец
        // 2026-08-06: «нажатие Enter должно фиксировать слово, чтобы писать следующее»).
        // Автозамена и заглавная буква у кодовых слов только мешают — слово должно уйти
        // в чип ровно таким, каким его набрали.
        enterKeyHint="done"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        onChange={(e) => {
          // Запятая тоже разделяет (привычка прежнего поля «через запятую»).
          if (e.target.value.includes(',')) push(e.target.value.replace(/,/g, ''));
          else setDraft(e.target.value);
        }}
        // beforeinput ловит подтверждение НАДЁЖНЕЕ keydown: на iOS клавиша «готово»
        // приходит как insertLineBreak, а keydown в этот момент может нести код
        // подсказки-набора (229) и до ветки Enter не доходить.
        onBeforeInput={(e) => {
          const type = (e.nativeEvent as InputEvent).inputType;
          if (type === 'insertLineBreak' || type === 'insertParagraph') {
            e.preventDefault();
            push(draft);
          }
        }}
        onKeyDown={(e) => {
          // isComposing — идёт подстановка из подсказок клавиатуры: Enter принадлежит ей.
          if (e.key === 'Enter' && !(e.nativeEvent as unknown as { isComposing?: boolean }).isComposing) {
            e.preventDefault();
            push(draft);
          } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
            onChange(values.slice(0, -1));
          }
        }}
        // Слово, набранное и не подтверждённое Enter, не должно потеряться при закрытии.
        onBlur={() => push(draft)}
      />
    </div>
  );
}

/**
 * ПОДСКАЗКА РЯДА — жёлтой плашкой с кнопкой «Готово» (эталон ЧП 18/20, косяк К5: у нас
 * такие предупреждения были серым текстом и терялись). Плашка про то, что человеку НАДО
 * СДЕЛАТЬ (добавить бота в админы канала, подключить интеграцию); «Готово» её убирает —
 * человек отметил, что сделал. Пояснения к ПОЛЯМ остаются серой подписью, как у ЧП.
 */
function RowNote({ children, action, noteId }: { children: ReactNode; action?: ReactNode; noteId?: string }) {
  const t = useTranslations('cabinet.scenario');
  // «Готово» ДОЛЖНО запоминаться: панель пересоздаётся после каждого сохранения (в её ключе
  // версия черновика), и локальное состояние возвращало плашку обратно — человек жал
  // «Готово» снова и снова (приёмка К5). Отметка живёт в браузере, ключ — шаг + вид ряда.
  const key = noteId ? `scn-note-done:${noteId}` : '';
  const [done, setDone] = useState(() => {
    if (!key || typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const markDone = (): void => {
    setDone(true);
    if (!key || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      /* приватный режим — отметка просто не переживёт перезагрузку */
    }
  };
  if (done) return null;
  return (
    <DWarn
      actions={
        <>
          {action}
          <button type="button" className="btn sm primary" onClick={markDone}>
            {t('chatNoteDone')}
          </button>
        </>
      }
    >
      {children}
    </DWarn>
  );
}

/**
 * Ф7.3: единая плашка «сервис не подключён» с кнопкой в «Подключения» — у приёмников
 * заявки и платёжек. Текст говорит, ЧТО подключить; кнопка уводит в раздел подключений.
 */
/** Тело листа выбора публикаций (владелец 2026-08-04, IMG_1264): лист в приложении —
 *  родной слой со статичным body, живые пропсы туда не доезжают — выбор живёт своим
 *  состоянием, каждое изменение сразу уезжает в форму через onPick. Кнопка ОК — только
 *  на веб-листе (BottomSheet); в приложении подтверждает галочка нативной шапки. */
function PostsSheetBody({
  credId,
  initial,
  onPick,
  onOk,
}: {
  credId: string | null;
  initial: string[];
  onPick: (ids: string[]) => void;
  onOk?: () => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const [ids, setIds] = useState<string[]>(initial);
  return (
    <div className="ig-pick-sheet">
      <IgMediaPicker
        credId={credId}
        selected={ids}
        onChange={(next) => {
          setIds(next);
          onPick(next);
        }}
      />
      {onOk ? (
        <button type="button" className="btn primary ig-pick-ok" onClick={onOk}>
          {t('chatStartPostsOk')}
        </button>
      ) : null}
    </div>
  );
}

export function ConnectNote({ text, onConnect }: { text: string; onConnect?: (() => void) | undefined }) {
  const t = useTranslations('cabinet.scenario');
  return (
    <DWarn
      actions={
        onConnect ? (
          <button type="button" className="btn sm primary" onClick={onConnect}>
            {t('chatConnectGo')}
          </button>
        ) : undefined
      }
    >
      <p>{text}</p>
    </DWarn>
  );
}

/**
 * Подсказка под полем. Есть заглушки «{…}» — объясняем, что это данные, которые бот
 * подставит сам (переставить и убрать можно, текст вокруг — править свободно).
 * Выражение, которого мы не разобрали, показано как есть — предупреждаем про «{{ }}».
 */
function ValueHint({ value }: { value: ChatValue }) {
  const t = useTranslations('cabinet.scenario');
  if (value.slots.length > 0) return <p className="scn-help-field">{t('chatMsgSlotHint')}</p>;
  return value.isExpression ? <p className="scn-help-field">{t('chatMsgExprHint')}</p> : null;
}

// ── «Сообщение»: текст + кнопки под ним ──────────────────────────────────────────────

/**
 * ВЛОЖЕНИЕ В РЕДАКТОРЕ СООБЩЕНИЯ (Р-медиа, владелец 31.07: «у тебя медиа где-то отдельно,
 * а когда создаёшь новое сообщение — там нет медиа»). Человек правит сообщение — значит и
 * файл он ищет ЗДЕСЬ, между текстом и кнопками. Пунктир «Добавить контент» на карточке
 * остаётся (это путь ЧП для стопки): два входа — один смысл.
 *
 * Канал решает всё (свод правил channels.ts): состав видов, предел размера, форматы и —
 * главное — КУДА ляжет файл:
 *  • мессенджеры (attachPlace='step') — файл уходит ОТДЕЛЬНЫМ сообщением, поэтому из
 *    редактора текста он создаёт новый шаг сразу за этим (об этом прямо написано плашкой);
 *    открыт сам шаг-вложение — правим его же файл;
 *  • наши мини-аппы (attachPlace='message') — файл ложится полем ТОГО ЖЕ шага.
 * Канал вложений не умеет (или канал неизвестен) — блока нет вовсе, мёртвого не рисуем.
 */
function AttachField({
  channel,
  attachment,
  attachUrl,
  attachName,
  onChange,
  onAddStep,
  onPickFile,
}: {
  channel: ChatChannelId | null;
  attachment: StepAttachment | null;
  attachUrl: string | null;
  attachName: string | null;
  /** Правка вложения ЭТОГО шага: kind=null — снять. */
  onChange: (kind: StepAttachment | null, url: string | null, name?: string | null) => void;
  /** Мессенджеры: создать шаг-вложение сразу за этим (файл уже загружен). */
  onAddStep?: (kind: ChatAttachKind, url: string) => void;
  /** Выбрать и залить файл; null — человек передумал или залить не вышло. */
  onPickFile?: (kind: ChatAttachKind) => Promise<{ url: string; name: string } | null>;
}) {
  const t = useTranslations('cabinet.scenario');
  const kinds = channel ? CHANNEL_RULES[channel].attachKinds : [];
  const [want, setWant] = useState<StepAttachment>(attachment ?? kinds[0] ?? 'image');
  const [busy, setBusy] = useState(false);
  if (!channel || kinds.length === 0 || !onPickFile) return null;

  const kind: StepAttachment = attachment ?? want;
  const asStep = chatAttachPlace(channel) === 'step' && attachment === null;

  const pick = (): void => {
    setBusy(true);
    void onPickFile(kind as ChatAttachKind)
      .then((res) => {
        setBusy(false);
        if (!res) return;
        if (asStep) onAddStep?.(kind as ChatAttachKind, res.url);
        // Имя показываем сразу — не дожидаясь, пока оно вычитается из «?name=» ссылки.
        else onChange(kind, res.url, res.name);
      })
      .catch(() => setBusy(false));
  };

  // Пузырь «Вложение» с двумя полями: чем прикладываем и сам файл. Каждое поле несёт свой
  // заголовок (FIn), описания — снизу (правило владельца 2026-08-11). Отдельной карточки
  // под превью нет: пузырь в пузыре запрещён — файл живёт прямо в теле блока.
  return (
    <FBlock label={t('chatMsgAttach')} {...(asStep ? { hint: t('attachSeparateMsg') } : {})}>
      <FIn label={t('chatMsgAttachKind')}>
        <SmartSelect
          className="scn-sched-sel"
          value={kind}
          ariaLabel={t('chatMsgAttachKind')}
          onChange={(v: string) => {
            const next = v as StepAttachment;
            setWant(next);
            // Тип сменили — прежний файл больше не подходит (картинка видео не станет).
            if (attachment !== null) onChange(next, null);
          }}
          options={kinds.map((k) => ({ value: k, label: t(chatContentNameKey(k)) }))}
        />
      </FIn>
      <FIn
        label={t(chatContentNameKey(kind))}
        {...(attachUrl
          ? {}
          : // Файла нет — говорим об этом ЧЕСТНО (владелец видел безымянную иконку и не
            // понял, что это): прямая подпись «файл не выбран» и предел канала.
            { hint: `${t('attachFileNone')} · ${limitHint(channel, kind, t)}` })}
      >
        {attachUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {kind === 'image' ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img decoding="async" loading="lazy"
                src={attachUrl}
                alt={attachName ?? ''}
                className="scn-att-prev"
              />
            ) : null}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <AttachGlyph kind={kind} />
              <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{attachName ?? t('attachFileNoName')}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn sm" disabled={busy} onClick={pick}>
                {t('attachReplace')}
              </button>
              <button type="button" className="btn sm" onClick={() => onChange(null, null)}>
                {t('attachRemove')}
              </button>
            </div>
          </div>
        ) : (
          <AddDashed label={busy ? t('attachUploading') : t('attachPickFile')} onClick={pick} />
        )}
      </FIn>
    </FBlock>
  );
}

/**
 * Форматы и предел размера ЭТОГО канала («png, jpg; до 8 МБ») — из свода правил, не из
 * жёстко вписанной строки: у Телеграма, Инстаграма и наших мини-аппов пределы разные, а
 * одна общая подсказка врала бы трём каналам из четырёх.
 */
export function limitHint(
  channel: ChatChannelId,
  kind: StepAttachment,
  t: (key: string, values?: Record<string, string | number>) => string,
): string {
  const limit = chatAttachLimit(channel, kind as ChatAttachKind);
  if (!limit) return '';
  const mb = Math.round(limit.maxBytes / (1024 * 1024));
  // Короткие имена форматов — общий словарь кабинета (lib/mime-words), не свой список.
  const exts = mimeWords(limit.accept);
  const formats = exts !== '' ? exts : t('attachAnyFormat');
  return t('attachLimitHint', { formats, mb });
}

/**
 * ТЕКСТ СООБЩЕНИЯ — один блок на все редакторы сообщений (обычное сообщение, подпись под
 * файлом, правка отправленного пузыря Э3): поле, кнопка «{…}» с вставкой подстановки в
 * позицию курсора и подсказка про заглушки. Второй копии этого поля не заводим (канон
 * UI-TEMPLATE, «Элементы формы»).
 */
function MsgTextBlock({
  label,
  placeholder,
  hint,
  value,
  onChange,
  lang,
}: {
  label: string;
  placeholder: string;
  /** Правило площадки под полем (видимая подсказка, не тултип); нет — блок без неё. */
  hint?: string;
  value: ChatValue;
  onChange: (next: ChatValue) => void;
  lang: Lang;
}) {
  const textRef = useRef<HTMLTextAreaElement>(null);

  // Вставка переменной (Ф4): заглушка «{подпись}» встаёт в текущую позицию курсора, а её
  // выражение — в слоты значения (writeChatValue соберёт подстановку при сохранении).
  // Той же подписи с ДРУГИМ выражением в тексте быть не может: у системных полей подпись
  // фиксированная, у своих переменных выражение выводится из имени.
  const insertSlot = (slot: ChatSlot) => {
    const ta = textRef.current;
    const token = `{${slot.label}}`;
    const cur = value.text;
    const at = ta ? (ta.selectionStart ?? cur.length) : cur.length;
    const to = ta ? (ta.selectionEnd ?? at) : at;
    const text = cur.slice(0, at) + token + cur.slice(to);
    const slots = value.slots.some((s) => s.label === slot.label) ? value.slots : [...value.slots, slot];
    onChange({ ...value, text, slots });
    // Вернуть фокус и поставить курсор сразу за вставленной заглушкой.
    requestAnimationFrame(() => {
      if (!ta) return;
      ta.focus();
      const pos = at + token.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  return (
    <FBlock label={label} {...(hint ? { hint } : {})}>
      <textarea
        ref={textRef}
        value={value.text}
        onChange={(e) => onChange({ ...value, text: e.target.value })}
        rows={5}
        placeholder={placeholder}
      />
      {/* Кнопка «{…}» (Ф4): вставить данные человека или свою переменную в текущую
          позицию. У неразобранного выражения кнопки нет — текст там правится как
          формула, и заглушка «{…}» его бы сломала. */}
      {!value.isExpression ? (
        <div className="scn-var-insert">
          <VarInsertMenu lang={lang} onInsert={insertSlot} />
        </div>
      ) : null}
      {/* Подстановки шаблона (имя человека, цена товара) человек видит заглушками «{…}» —
          объясняем, что это данные, а не текст; неразобранное выражение — как раньше. */}
      <ValueHint value={value} />
    </FBlock>
  );
}

/**
 * КНОПКИ ПОД СООБЩЕНИЕМ — один блок на все редакторы сообщений (обычное сообщение и
 * правка отправленного пузыря Э3). ОДИН пузырь «Кнопки» на все кнопки: карточек-пузырей
 * на каждую кнопку больше нет (владелец 2026-08-11 — «пузырь в пузыре» запрещён). Кнопки
 * идут стопкой полей, у каждого поля свой заголовок; конец кнопки отбивает её «Убрать».
 */
function MsgButtonsBlock({
  node,
  model,
  channel,
  onChange,
  hint,
}: {
  node: WorkflowNode;
  model: ChatMessageModel;
  channel: ChatChannelId | null;
  onChange: (buttons: ChatButton[]) => void;
  /** Правило площадки под блоком (видимая подсказка); нет — блок без неё. */
  hint?: string;
}) {
  const t = useTranslations('cabinet.scenario');
  const patchButton = (i: number, patch: Partial<ChatButton>) =>
    onChange(model.buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  return (
    <FBlock label={t('chatMsgButtons')} {...(hint ? { hint } : {})}>
      {model.buttons.map((b, i) => (
        <Fragment key={i}>
          <FIn label={t('chatMsgBtnText')}>
            <input
              value={b.text}
              onChange={(e) => patchButton(i, { text: e.target.value })}
              // Предел подписи — правило площадки: быстрая кнопка Инстаграма — до 20
              // символов (узел молча резал бы длиннее), у Телеграма оставляем 64.
              maxLength={channel === 'instagram' ? 20 : 64}
              placeholder={t('chatMsgBtnTextPh')}
              aria-label={t('chatMsgBtnText')}
            />
          </FIn>
          {/* Типы кнопки — по своду правил канала (аудит Ф5.2): у Инстаграма
              «Ссылка» — кнопка внутри пузыря (шаблон Меты, IG-MESSAGE-LOOK Э1);
              быстрый ответ, которому выбрали «Ссылку», переезжает в пузырь при
              записи (chat-step.ts). Выбор шире (оплата, телефон) живёт
              в шторке кнопки на карточке (chat-card-edit) — здесь только пара. */}
          <FIn label={t('chatMsgBtnKind')}>
            <SmartSelect
              className="scn-sched-sel"
              value={b.kind}
              ariaLabel={t('chatMsgBtnKind')}
              onChange={(v: string) =>
                patchButton(i, {
                  kind: v === 'link' ? 'link' : 'reply',
                  ...(v === 'reply' ? { url: chatValueOf() } : {}),
                })
              }
              options={[
                { value: 'reply', label: t('chatMsgBtnReply') },
                ...(chatButtonKinds(node, model, i).includes('link')
                  ? [{ value: 'link', label: t('chatMsgBtnLink') }]
                  : []),
              ]}
            />
          </FIn>
          {b.kind === 'link' ? (
            /* Кнопка-ссылка без адреса молча сохранилась бы обычной кнопкой —
               говорим красным, пока адрес не вписан (Ф9). Явный брак формата
               (нет http(s), пробел) валит отправку ВСЕГО сообщения — тоже красным. */
            <FIn
              label={t('chatMsgBtnUrl')}
              {...(b.url.text.trim() === ''
                ? { error: t('chatMsgBtnUrlErr') }
                : chatUrlBroken(b.url.text)
                  ? { error: t('chatMsgBtnUrlFmtErr') }
                  : {})}
            >
              <input
                value={b.url.text}
                onChange={(e) => patchButton(i, { url: { ...b.url, text: e.target.value } })}
                placeholder={t('chatMsgBtnUrlPh')}
                aria-label={t('chatMsgBtnUrl')}
              />
              <ValueHint value={b.url} />
            </FIn>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button"
              className="btn sm"
              onClick={() => onChange(model.buttons.filter((_, idx) => idx !== i))}
            >
              {t('chatMsgBtnRemove')}
            </button>
          </div>
        </Fragment>
      ))}
      {/* Предел числа кнопок — из паспорта канала: лишнюю кнопку площадка не
          покажет, поэтому «добавить» за пределом не рисуем (Ф5.2). У Инстаграма
          новая кнопка идёт ВНУТРЬ пузыря (шаблон Меты, IG-MESSAGE-LOOK Э1) — там
          свой предел 3; старые быстрые ответы (quick) в него не считаются. */}
      {(() => {
        const igBubbleFull =
          channel === 'instagram' &&
          model.buttons.filter((b) => b.quick !== true).length >= IG_BUBBLE_BUTTONS_MAX;
        const max = channel ? CHANNEL_RULES[channel].buttons?.max ?? null : null;
        return igBubbleFull || (max !== null && model.buttons.length >= max) ? null : (
          <AddDashed
            label={t('chatMsgBtnAdd')}
            onClick={() => onChange([...model.buttons, { text: '', kind: 'reply', url: chatValueOf() }])}
          />
        );
      })()}
    </FBlock>
  );
}

function MessageEditor({
  node,
  push,
  channel,
  onAddContent,
  onPickFile,
  phoneNode,
  pushPhone,
}: {
  node: WorkflowNode;
  push: Push;
  channel: ChatChannelId | null;
  onAddContent?: (kind: ChatAttachKind, url: string) => void;
  onPickFile?: (kind: ChatAttachKind) => Promise<{ url: string; name: string } | null>;
  /** У9: шаг-пометка «ждём телефон», вклеенный в эту карточку (действие «Запросить номер
   *  телефона»); null — обычное сообщение. */
  phoneNode?: WorkflowNode | null;
  /** Записать параметры пометки (в ней живёт режим приёма ответа). */
  pushPhone?: Push;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatMessage(node, lang), [node, lang]);
  const [m, setM] = useState<ChatMessageModel | null>(initial);
  if (!initial || !m) return null;

  const apply = (patch: Partial<ChatMessageModel>) => {
    const next = { ...m, ...patch };
    setM(next);
    push(writeChatMessage(node, next));
  };

  // Ответы на комментарий Инстаграма (К37-Z5): у площадки для них СВОИ правила, и молчать
  // о них нельзя — один ответ, 7 дней, окно переписки открывает только нажатие кнопки.
  const igOp = String(node.parameters?.operation ?? '');
  const igReplyPrivate = channel === 'instagram' && igOp === 'replyPrivate';
  const igReplyComment = channel === 'instagram' && igOp === 'replyComment';

  // Правила площадки для ответов на комментарий Инстаграма — ПОДСКАЗКОЙ ПОД ТЕКСТОМ, а не
  // вводным абзацем под заголовком (владелец 2026-08-11: «под заголовком модалки ничего не
  // пишем», описание элемента живёт снизу). Разом их не бывает: операция у шага одна.
  const igTextHint = igReplyPrivate
    ? t('chatMsgIgPrivateHint')
    : igReplyComment
      ? t('chatMsgIgPublicHint')
      : null;

  return (
    <>
      {/* Вводного описания под заголовком НЕТ (владелец 2026-08-11): пузыри и их названия
          говорят сами за себя. Условные предупреждения остаются — это не описание. */}
      {/* Без кнопки разговор оборвётся на первом сообщении — это не «мелочь оформления»,
          поэтому не серой строкой, а жёлтой плашкой (канон К5). */}
      {igReplyPrivate && m.buttons.length === 0 ? <RowNote>{t('chatMsgIgNoButtonWarn')}</RowNote> : null}
      {/* Голый адрес в тексте Инстаграм разворачивает серой превью-карточкой — предлагаем
          кнопку-«Ссылку» (IG-MESSAGE-LOOK Э2.2). Подсказка видима всегда, не тултип
          (правило видимых подсказок); публичный ответ без кнопок — там молчим. */}
      {channel === 'instagram' && m.supportsButtons && /https?:\/\//i.test(m.text.text) ? (
        <RowNote>{t('chatMsgIgLinkPreviewWarn')}</RowNote>
      ) : null}
      {/* Шаг-вложение (К1): текст тут — ПОДПИСЬ под файлом, так и подписываем поле.
          У вложения Инстаграма подписи не бывает вовсе — поля тоже нет (не обманываем). */}
      {m.supportsText ? (
        <MsgTextBlock
          label={t(m.attachment ? 'chatMsgCaption' : 'chatMsgText')}
          placeholder={t(m.attachment ? 'chatMsgCaptionPh' : 'chatMsgTextPh')}
          {...(igTextHint ? { hint: igTextHint } : {})}
          value={m.text}
          onChange={(text) => apply({ text })}
          lang={lang}
        />
      ) : null}

      {/* Варианты публичного ответа (IG-MESSAGE-LOOK Э3): по фразе на строку, бот берёт
          случайную из текста и вариантов. Подсказка видима всегда (не тултип). */}
      {igReplyComment ? (
        <FBlock label={t('chatMsgIgVariants')} hint={t('chatMsgIgVariantsHint')}>
          <textarea
            value={m.commentVariants ?? ''}
            onChange={(e) => apply({ commentVariants: e.target.value })}
            rows={3}
            placeholder={t('chatMsgIgVariantsPh')}
          />
        </FBlock>
      ) : null}

      {/* Вложение — между текстом и кнопками: человек правит сообщение и файл ищет здесь. */}
      <AttachField
        channel={channel}
        attachment={m.attachment}
        attachUrl={m.attachUrl}
        attachName={m.attachName}
        onChange={(kind, url, name) => apply({ attachment: kind, attachUrl: url, attachName: name ?? null })}
        onAddStep={onAddContent}
        onPickFile={onPickFile}
      />

      {m.supportsButtons ? (
        <MsgButtonsBlock node={node} model={m} channel={channel} onChange={(buttons) => apply({ buttons })} />
      ) : null}

      {/* У9: как принимать номер. Стоит ПОСЛЕДНИМ блоком — сперва человек пишет саму
          просьбу, потом решает, каким ответом её закрывать. */}
      {phoneNode && pushPhone ? (
        <PhoneAskBlock node={phoneNode} push={pushPhone} channel={channel} />
      ) : null}
    </>
  );
}

// ── «Запросить номер телефона»: как принимать ответ (У9, модель владельца) ───────────
// Телеграм умеет спросить номер РОДНОЙ кнопкой — площадка передаёт настоящий номер
// аккаунта, подделать его нельзя. Инстаграм такой кнопки не даёт вовсе, поэтому там
// выбора нет и мы честно об этом пишем, а не рисуем мёртвый переключатель.
// Режим живёт в шаге-пометке «ждём телефон»; ответ бот сам кладёт в карточку человека.

function PhoneAskBlock({
  node,
  push,
  channel,
}: {
  node: WorkflowNode;
  push: Push;
  channel: ChatChannelId | null;
}) {
  const t = useTranslations('cabinet.scenario');
  const modes = phoneModesFor(channel === 'instagram' ? 'instagram' : 'telegram');
  // Режим, записанный в схеме, может не совпасть со списком канала (схему собрали в
  // другом канале). Показываем ближайший ПО СМЫСЛУ, а не пустой выбор: «текстом» в
  // Телеграме принимает и кнопку, и текст — это и есть «Кнопкой или текстом».
  const stored = readPhoneMode(node) ?? 'buttonOrText';
  const initial = modes.includes(stored) ? stored : ((modes[0] === 'text' ? 'text' : 'buttonOrText') as ChatPhoneMode);
  const [mode, setMode] = useState<ChatPhoneMode>(initial);
  const label: Record<ChatPhoneMode, string> = {
    button: t('chatAskPhoneBtnOnly'),
    buttonOrText: t('chatAskPhoneBtnOrText'),
    text: t('chatAskPhoneTextOnly'),
  };
  const hint: Record<ChatPhoneMode, string> = {
    button: t('chatAskPhoneBtnOnlyHint'),
    buttonOrText: t('chatAskPhoneBtnOrTextHint'),
    text: t('chatAskPhoneTextOnlyHint'),
  };
  return (
    <>
      {modes.length > 1 ? (
        <FChoiceBlock
          label={t('chatAskPhoneHow')}
          value={mode}
          onChange={(v) => {
            const next = v as ChatPhoneMode;
            setMode(next);
            push(writePhoneMode(node, next));
          }}
          options={modes.map((v) => ({ value: v, label: label[v], hint: hint[v] }))}
          hint={t('chatAskPhoneSaveHint')}
        />
      ) : (
        /* Инстаграм: выбора нет — только честное пояснение, почему (подсказка видима
           всегда, не тултип). */
        <FHint>
          {t('chatAskPhoneIgHint')} {t('chatAskPhoneSaveHint')}
        </FHint>
      )}
      {/* Как это работает в разговоре: ответ приходит СЛЕДУЮЩИМ сообщением, а по
          кодовому слову сценарий его не услышит — про это молчать нельзя. */}
      <FHint>{t('chatAskPhoneWaitHint')}</FHint>
    </>
  );
}

// ── «Экран формы»: страница, которую человек видит (узлы «Форма» и «Экран») ──────────
// Раньше такие шаги открывались ПРЕЖНЕЙ формой узла («Тип шага», «Предпросмотр»,
// «Заголовок») — брак приёмки (В5-1в1, хвост 1): у чат-шага такой панели быть не должно.
// Правим то, что человек видит на странице: заголовок, текст, подписи и типы полей,
// подписи и действия кнопок. Служебное (имя поля в данных, подсказка в пустом поле,
// картинка экрана) панель не показывает и не задевает — chat-step.ts несёт его как было.

const SCREEN_FIELD_KINDS: ChatScreenFieldKind[] = ['text', 'choice', 'email', 'phone', 'date', 'file'];
const SCREEN_BUTTON_KINDS: ChatScreenButtonKind[] = ['next', 'link', 'download'];

function ScreenEditor({
  node,
  push,
  channel,
  onPickFile,
}: {
  node: WorkflowNode;
  push: Push;
  channel: ChatChannelId | null;
  onPickFile?: (kind: ChatAttachKind) => Promise<{ url: string; name: string } | null>;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatScreen(node, lang), [node, lang]);
  const [m, setM] = useState<ChatScreenModel | null>(initial);
  if (!initial || !m) return null;

  const apply = (patch: Partial<ChatScreenModel>) => {
    const next = { ...m, ...patch };
    setM(next);
    push(writeChatScreen(node, next));
  };
  const patchField = (i: number, patch: Partial<ChatScreenField>) =>
    apply({ fields: m.fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) });
  const patchButton = (i: number, patch: Partial<ChatScreenButton>) =>
    apply({ buttons: m.buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) });

  const fieldKindLabel: Record<ChatScreenFieldKind, string> = {
    text: t('screenKindText'),
    choice: t('screenKindChoice'),
    email: t('screenKindEmail'),
    phone: t('screenKindPhone'),
    date: t('screenKindDate'),
    file: t('screenKindFile'),
  };
  const btnKindLabel: Record<ChatScreenButtonKind, string> = {
    next: t('screenBtnNext'),
    link: t('screenBtnLink'),
    download: t('screenBtnDownload'),
  };
  const hasNext = m.buttons.some((b) => b.kind === 'next' && b.label.trim() !== '');

  return (
    <>
      {/* Вводного описания под заголовком шторки НЕТ (владелец 2026-08-11): что это за
          страница и куда уйдут ответы, говорит подсказка ПОД первым пузырём. Одно поле,
          имя которого совпадает с именем пузыря, — без FIn (канон). */}
      <FBlock label={t('screenTitle')} hint={m.first ? t('screenHintFirst') : t('screenHint')}>
        <input
          value={m.title.text}
          onChange={(e) => apply({ title: { ...m.title, text: e.target.value } })}
          placeholder={t('screenTitlePh')}
          aria-label={t('screenTitle')}
        />
        <ValueHint value={m.title} />
      </FBlock>
      <FBlock label={t('screenText')}>
        <textarea
          value={m.text.text}
          onChange={(e) => apply({ text: { ...m.text, text: e.target.value } })}
          rows={4}
          placeholder={t('screenTextPh')}
          aria-label={t('screenText')}
        />
        <ValueHint value={m.text} />
      </FBlock>

      {/* Медиа на экране (Р-медиа): картинка или ролик прямо на странице формы. Экран
          заменяет страницу целиком, поэтому вложение живёт на нём самом, а не шагом. */}
      <AttachField
        channel={channel}
        attachment={m.attachment}
        attachUrl={m.attachUrl}
        attachName={m.attachName}
        onChange={(kind, url, name) => apply({ attachment: kind, attachUrl: url, attachName: name ?? null })}
        onPickFile={onPickFile}
      />

      {/* ОДИН пузырь на все поля страницы (образец — «Кнопки» сообщения и строки заявки):
          карточек-пузырей на каждое поле больше нет («пузырь в пузыре» запрещён, владелец
          2026-08-11). Поля идут стопкой со своими заголовками, конец поля отбивает
          «Убрать поле». */}
      <FBlock label={t('screenFields')}>
        {m.fields.map((f, i) => (
          <Fragment key={f.key || i}>
            <FIn label={t('screenFieldLabel')}>
              <input
                value={f.label}
                onChange={(e) => patchField(i, { label: e.target.value })}
                maxLength={200}
                placeholder={t('screenFieldLabelPh')}
                aria-label={t('screenFieldLabel')}
              />
            </FIn>
            <FIn label={t('screenFieldKind')}>
              <SmartSelect
                className="scn-sched-sel"
                value={f.kind}
                ariaLabel={t('screenFieldKind')}
                onChange={(v: string) => patchField(i, { kind: v as ChatScreenFieldKind })}
                options={SCREEN_FIELD_KINDS.map((k) => ({ value: k, label: fieldKindLabel[k] }))}
              />
            </FIn>
            {/* Поле «Выбор из вариантов» без вариантов бесполезно — правим их здесь же.
                Заголовок «Варианты» стоит ОДИН на весь список, а не над каждой строкой. */}
            {f.kind === 'choice' ? (
              <FIn label={t('screenOptions')}>
                {f.options.map((o, oi) => (
                  <div key={o.value || oi} style={{ display: 'flex', gap: 8 }}>
                    <input
                      value={o.label}
                      onChange={(e) =>
                        patchField(i, {
                          options: f.options.map((x, xi) => (xi === oi ? { ...x, label: e.target.value } : x)),
                        })
                      }
                      placeholder={t('screenOptionPh')}
                      aria-label={t('screenOptions')}
                    />
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => patchField(i, { options: f.options.filter((_, xi) => xi !== oi) })}
                    >
                      {t('screenOptionRemove')}
                    </button>
                  </div>
                ))}
                <AddDashed
                  label={t('screenOptionAdd')}
                  onClick={() =>
                    patchField(i, {
                      options: [
                        ...f.options,
                        { label: '', value: chatScreenKey(f.options.map((o) => o.value), 'option') },
                      ],
                    })
                  }
                />
              </FIn>
            ) : null}
            {/* Заполнять обязательно или можно пропустить — настройка вкл/выкл, а значит
                строка с тумблером внутри пузыря (канон формы), не пара вкладок. */}
            <KSwitch
              label={t('screenFieldRequiredOn')}
              checked={f.required}
              onChange={(v) => patchField(i, { required: v })}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => apply({ fields: m.fields.filter((_, idx) => idx !== i) })}
              >
                {t('screenFieldRemove')}
              </button>
            </div>
          </Fragment>
        ))}
        <AddDashed
          label={t('screenFieldAdd')}
          onClick={() =>
            apply({
              fields: [
                ...m.fields,
                {
                  kind: 'text',
                  label: '',
                  // Имя в данных придумываем сами (латиница): человеку его не показываем,
                  // но по нему значение поля уедет в сценарий (fields.<имя>).
                  key: chatScreenKey(m.fields.map((f) => f.key), 'field'),
                  required: false,
                  options: [],
                },
              ],
            })
          }
        />
      </FBlock>

      {/* Кнопка «Продолжить сценарий» — то, чем человек уходит дальше. Её нет: у первого
          экрана это брак (форма ничего не отправит), у следующего — финал прохождения.
          Говорим об этом подсказкой ПОД пузырём кнопок (описание всегда снизу). */}
      <FBlock
        label={t('screenButtons')}
        {...(!hasNext ? { hint: m.first ? t('screenNoNextHint') : t('screenFinalHint') } : {})}
      >
        {m.buttons.map((b, i) => (
          <Fragment key={i}>
            <FIn label={t('chatMsgBtnText')}>
              <input
                value={b.label}
                onChange={(e) => patchButton(i, { label: e.target.value })}
                maxLength={200}
                placeholder={t('chatMsgBtnTextPh')}
                aria-label={t('chatMsgBtnText')}
              />
            </FIn>
            <FIn label={t('screenBtnAction')}>
              <SmartSelect
                className="scn-sched-sel"
                value={b.kind}
                ariaLabel={t('screenBtnAction')}
                onChange={(v: string) =>
                  patchButton(i, {
                    kind: v as ChatScreenButtonKind,
                    ...(v === 'next' && b.id === ''
                      ? { id: chatScreenKey(m.buttons.map((x) => x.id), 'button') }
                      : {}),
                  })
                }
                options={SCREEN_BUTTON_KINDS.map((k) => ({ value: k, label: btnKindLabel[k] }))}
              />
            </FIn>
            {b.kind === 'next' ? null : (
              /* «Открыть ссылку»/«Скачать файл» без адреса — кнопке некуда вести (Ф9);
                 явный брак формата (нет http(s), пробел) — тоже красным. */
              <FIn
                label={b.kind === 'link' ? t('chatMsgBtnUrl') : t('screenBtnFileUrl')}
                {...(b.url.text.trim() === ''
                  ? { error: t('chatMsgBtnUrlErr') }
                  : chatUrlBroken(b.url.text)
                    ? { error: t('chatMsgBtnUrlFmtErr') }
                    : {})}
              >
                <input
                  value={b.url.text}
                  onChange={(e) => patchButton(i, { url: { ...b.url, text: e.target.value } })}
                  placeholder={t('chatMsgBtnUrlPh')}
                  aria-label={b.kind === 'link' ? t('chatMsgBtnUrl') : t('screenBtnFileUrl')}
                />
                <ValueHint value={b.url} />
              </FIn>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => apply({ buttons: m.buttons.filter((_, idx) => idx !== i) })}
              >
                {t('chatMsgBtnRemove')}
              </button>
            </div>
          </Fragment>
        ))}
        <AddDashed
          label={t('chatMsgBtnAdd')}
          onClick={() =>
            apply({
              buttons: [
                ...m.buttons,
                {
                  kind: 'next',
                  label: '',
                  id: chatScreenKey(m.buttons.map((b) => b.id), 'button'),
                  url: chatValueOf(),
                  primary: false,
                },
              ],
            })
          }
        />
      </FBlock>
    </>
  );
}

// ── «Действие»: что запомнить о человеке (узел «Контакты») ───────────────────────────

/**
 * Что человек ВЫБИРАЕТ в действии карточки человека (ACTION-STACK Э2). Порядок — от
 * частого к редкому. «Запомнить про человека» — одно действие вместо прежних «Запомнить
 * значение» и «Записать в карточку»: что именно запоминаем, спрашиваем следующим списком.
 * Кухни (загрузка карточки, проверка меток) в списке НЕТ — её человек не заводит.
 */
type ClientAct = 'addTags' | 'removeTags' | 'remember';

const CLIENT_ACTS: ClientAct[] = ['addTags', 'removeTags', 'remember'];

/** Что запоминаем «Запомнить про человека»: своё значение, имя или телефон. */
type RememberWhat = 'value' | 'name' | 'phone';

const REMEMBER_WHAT: RememberWhat[] = ['value', 'name', 'phone'];

/** Действие списка по операции шага; null — это кухня или ссылка-приглашение (легаси). */
function clientActOf(op: ChatContactsOp): ClientAct | null {
  if (op === 'addTags' || op === 'removeTags') return op;
  return op === 'setVar' || op === 'save' ? 'remember' : null;
}

/** Что запоминает готовый шаг: своё значение (setVar) или поле карточки (save). */
function rememberWhatOf(m: ChatContactsModel): RememberWhat {
  if (m.op !== 'save') return 'value';
  return m.phone.text.trim() !== '' && m.name.text.trim() === '' ? 'phone' : 'name';
}

/** Значок ряда действия по операции карточки человека (тот же, что на карточке шага). */
const CONTACTS_ICON: Record<ChatContactsOp, StepRowIcon> = {
  addTags: 'tag',
  removeTags: 'tag',
  setVar: 'var',
  save: 'card',
  get: 'card',
  check: 'cond',
  refLink: 'card',
};

/**
 * Убрать ЭТОТ ряд из карточки (ACTION-STACK Э1): ряд — свой шаг канона, поэтому уборка
 * структурная (её делает use-draft-editor). Нет колбэка — корзины у ряда нет: карточка
 * из одного действия убирается целиком корзиной шапки.
 */
type RemoveRow = () => void;

/** Корзина ряда — одними и теми же словами во всех формах действий. */
function removeProps(
  onRemove: RemoveRow | undefined,
  t: ReturnType<typeof useTranslations>,
): { onRemove?: RemoveRow; removeLabel?: string } {
  return onRemove ? { onRemove, removeLabel: t('chatActRemove') } : {};
}

/**
 * НАСТРОЙКА ОДНОГО ДЕЙСТВИЯ карточки (владелец 12.08). Карточка «Действие» остаётся
 * стопкой плашек, но экрана целого блока с лентой рядов больше нет: тап по плашке
 * открывает панель ЕЁ шага, «Добавить действие» на карточке открывает каталог. В каноне
 * ряд — свой шаг цепочки, поэтому панель правит ровно его (по id шага).
 */
function ActionRowEditor({
  node,
  onState,
  services,
  companionNode,
  modelNode,
  onOpenNode,
}: {
  /** Шаг этой плашки (он же шаг канона). */
  node: WorkflowNode;
  onState: (s: ChatStepState) => void;
  services: CustomServiceMeta[] | null;
  /** Довесок «Разбор ответа» этого запроса (спрятан в карточку, chat-merge). */
  companionNode: WorkflowNode | null;
  /** Шаг модели «Запроса к ИИ» (висит в гнезде помощника). */
  modelNode: WorkflowNode | null;
  onOpenNode?: (nodeId: string) => void;
}) {
  const svcIds = useMemo(() => (services ? new Set(services.map((s) => s.credId)) : null), [services]);
  // Вид ряда — по его СОБСТВЕННОМУ шагу: запрос к сервису клиента узнаёт serviceEditorKind.
  const kind = chatEditorKind(node) ?? serviceEditorKind(node, svcIds);
  const push: Push = (params) => onState({ name: node.name, params });
  // Корзины ВНУТРИ панели у ряда нет: плашку убирает корзина шапки этой же панели
  // (шаг уходит из цепочки) — второй корзины на один смысл не заводим.
  const row =
    kind === 'contacts' ? (
      <ContactsEditor node={node} push={push} />
    ) : kind === 'delay' ? (
      <DelayEditor node={node} push={push} />
    ) : kind === 'http' ? (
      <HttpEditor
        node={node}
        services={services}
        companion={companionNode}
        onHttp={(params, ex) =>
          onState({
            name: node.name,
            params,
            ...(ex?.credentialId !== undefined ? { credentialId: ex.credentialId } : {}),
            ...(ex?.companion !== undefined ? { companion: ex.companion, companionFor: node.id } : {}),
          })
        }
      />
    ) : kind === 'ai' ? (
      <AiEditor node={node} modelNode={modelNode} {...(onOpenNode ? { onOpenNode } : {})} push={push} />
    ) : kind === 'chatAction' ? (
      <ChatActionEditor node={node} push={push} />
    ) : kind === 'reaction' ? (
      <ReactionEditor node={node} push={push} />
    ) : kind === 'editMsg' ? (
      <EditMsgEditor node={node} push={push} />
    ) : kind === 'saveMsg' ? (
      <SaveMsgEditor node={node} push={push} />
    ) : null;
  if (!row) return null;
  // Обёртка — по виду ряда (список FORM_KIT_KINDS только растёт): без .fk-pane пузыри
  // формы не красятся.
  return FORM_KIT_KINDS.has(kind as string) ? <FPane>{row}</FPane> : <DGroup>{row}</DGroup>;
}

function ContactsEditor({ node, push, onRemove }: { node: WorkflowNode; push: Push; onRemove?: RemoveRow }) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatContacts(node, lang), [node, lang]);
  const [m, setM] = useState<ChatContactsModel | null>(initial);
  // Что запоминает шаг (Э2) — состояние формы, а не канона: в каноне это операция узла
  // (своё значение — setVar, имя и телефон — save).
  const [what, setWhat] = useState<RememberWhat>(initial ? rememberWhatOf(initial) : 'value');
  if (!initial || !m) return null;

  const apply = (patch: Partial<ChatContactsModel>) => {
    const next = { ...m, ...patch };
    setM(next);
    push(writeChatContacts(node, next));
  };
  // Правим только текст поля: заглушки подстановок и исходное значение несём из канона
  // (не тронул текст — в узел уйдёт то же, что было; chat-text.ts).
  const setVal = (key: 'tags' | 'varValue' | 'name' | 'phone', text: string) =>
    apply({ [key]: { ...m[key], text } as ChatValue });

  const opLabel: Record<ChatContactsOp, string> = {
    addTags: t('chatActAddTag'),
    removeTags: t('chatActRemoveTag'),
    setVar: t('chatActSetVar'),
    save: t('chatActSave'),
    get: t('chatActGet'),
    check: t('chatActCheck'),
    refLink: t('chatActRefLink'),
  };
  const actLabel: Record<ClientAct, string> = {
    addTags: t('chatActAddTag'),
    removeTags: t('chatActRemoveTag'),
    remember: t('chatActRemember'),
  };
  const act = clientActOf(m.op);
  // Сменили действие: «Запомнить про человека» ложится той операцией, которую человек
  // уже выбрал вторым списком (своё значение — setVar, имя и телефон — save).
  const setAct = (v: ClientAct) =>
    apply({ op: v === 'remember' ? (what === 'value' ? 'setVar' : 'save') : v });
  const setWhatOp = (v: RememberWhat) => {
    setWhat(v);
    apply({ op: v === 'value' ? 'setVar' : 'save' });
  };
  // Поля карточки показываем и «лишние»: у старого шага бывают записаны сразу имя И
  // телефон — спрятать заполненное поле значило бы молча писать то, чего не видно.
  const showName = m.op === 'save' && (what === 'name' || m.name.text.trim() !== '');
  const showPhone = m.op === 'save' && (what === 'phone' || m.phone.text.trim() !== '');

  return (
    <>
      {/* Вводного описания под заголовком НЕТ (владелец 2026-08-11) — пузыри говорят сами
          за себя. Ряд действия — шапкой со значком и названием (шторка ЧП 20); что именно
          делает шаг, меняется тут же выбором (у нас один шаг = одно действие, удалять ряд
          нечем — удаление шага живёт в меню карточки). */}
      <div className="scn-erow">
        <RowHead
          icon={CONTACTS_ICON[m.op]}
          label={act ? actLabel[act] : opLabel[m.op]}
          {...removeProps(onRemove, t)}
        />
        {/* Три действия — раскрытым списком с галочкой, а не выпадашкой (канон формы:
            выбор до шести вариантов стоит строками отдельным пузырём). Шаг НАШЕЙ кухни
            (загрузка карточки, проверка меток) и ссылка-приглашение выбора не имеют:
            завести их заново нельзя, старая схема открывается и работает как раньше. */}
        {act ? (
          <FChoiceBlock
            label={t('chatActWhat')}
            value={act}
            onChange={(v) => setAct(v as ClientAct)}
            options={CLIENT_ACTS.map((a) => ({ value: a, label: actLabel[a] }))}
          />
        ) : null}

      {/* «Запомнить про человека» (Э2): что именно запоминаем. */}
      {act === 'remember' ? (
        <FChoiceBlock
          label={t('chatActRememberWhat')}
          value={what}
          onChange={(v) => setWhatOp(v as RememberWhat)}
          options={REMEMBER_WHAT.map((w) => ({
            value: w,
            label:
              w === 'value'
                ? t('chatActRememberValue')
                : w === 'name'
                  ? t('chatActRememberName')
                  : t('chatActRememberPhone'),
          }))}
        />
      ) : null}

      {/* «Моя ссылка-приглашение» (К18): настраивать нечего — код на человека заводит наш
          сервер, шаг только объясняет, что появится в карточке. */}
      {m.op === 'refLink' ? <p className="scn-help-field">{t('chatRefLinkHint')}</p> : null}

      {m.op === 'addTags' || m.op === 'removeTags' || m.op === 'check' ? (
        /* Пустое поле = шаг-пустышка: метку ставить/снимать/проверять нечем (Ф9). */
        <FBlock
          label={t('chatActTags')}
          {...(m.tags.text.trim() === ''
            ? { error: t('chatActTagsEmptyErr') }
            : { hint: t('chatActTagsHint') })}
        >
          <input value={m.tags.text} onChange={(e) => setVal('tags', e.target.value)} placeholder={t('chatActTagsPh')} />
          <ValueHint value={m.tags} />
        </FBlock>
      ) : null}

      {m.op === 'check' ? (
        <FChoiceBlock
          label={t('chatActCheckMode')}
          value={m.checkAny ? 'any' : 'all'}
          onChange={(v) => apply({ checkAny: v === 'any' })}
          options={[
            { value: 'all', label: t('chatActCheckAll') },
            { value: 'any', label: t('chatActCheckAny') },
          ]}
        />
      ) : null}

      {m.op === 'setVar' ? (
        <>
          {/* Имя — из единого каталога переменных (Ф4): выбор вместо ручного ввода;
              создание — тут же, пунктом выпадашки. Имя из старой схемы, которого в
              каталоге нет, остаётся в списке как есть (обратная совместимость). */}
          {/* Переменная не выбрана — запоминать не во что: шаг сработает вхолостую (Ф9). */}
          <FBlock
            label={t('chatActVarName')}
            {...(m.varName.trim() === '' ? { error: t('chatActVarNameErr') } : {})}
          >
            <VariablePicker
              value={m.varName}
              ariaLabel={t('chatActVarName')}
              onChange={(name) => apply({ varName: name })}
            />
          </FBlock>
          {/* «Установить новое / Увеличить на / Уменьшить на» — раскрытым списком с
              галочкой (три варианта, канон формы). Счёт ведёт сам шаг: он берёт текущее
              значение из карточки человека и меняет его. */}
          <FChoiceBlock
            label={t('chatActVarOp')}
            value={m.varOp}
            onChange={(v) => apply({ varOp: v as ChatVarOp })}
            options={[
              { value: 'set', label: t('chatActVarSet') },
              { value: 'inc', label: t('chatActVarInc') },
              { value: 'dec', label: t('chatActVarDec') },
            ]}
          />
          {/* Пустое значение = переменную снимают (так в шаблонах закрывают анкету);
              у «увеличить/уменьшить» здесь число, на которое меняем. */}
          <FBlock
            label={m.varOp === 'set' ? t('chatActVarValue') : t('chatActVarDelta')}
            hint={m.varOp === 'set' ? t('chatActVarValueHint') : t('chatActVarDeltaHint')}
          >
            <input
              value={m.varValue.text}
              onChange={(e) => setVal('varValue', e.target.value)}
              placeholder={m.varOp === 'set' ? t('chatActVarValuePh') : t('chatActVarDeltaPh')}
              {...(m.varOp === 'set' ? {} : { inputMode: 'decimal' as const })}
            />
            <ValueHint value={m.varValue} />
          </FBlock>
        </>
      ) : null}

      {showName ? (
        <FBlock label={t('chatActName')}>
          <input value={m.name.text} onChange={(e) => setVal('name', e.target.value)} placeholder={t('chatActAutoPh')} />
          <ValueHint value={m.name} />
        </FBlock>
      ) : null}
      {showPhone ? (
        <FBlock label={t('chatActPhone')}>
          <input value={m.phone.text} onChange={(e) => setVal('phone', e.target.value)} placeholder={t('chatActAutoPh')} />
          <ValueHint value={m.phone} />
        </FBlock>
      ) : null}

      {m.op === 'get' ? <DHint>{t('chatActGetHint')}</DHint> : null}
      </div>
    </>
  );
}

// ── Время в рядах условий (К4): день недели чипами, дата — общим календарём ──────────

/** «ГГГГ-ММ-ДД» → Date (полдень: сдвиг пояса не должен менять день) или null. */
function isoToDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12) : null;
}

/** Date → «ГГГГ-ММ-ДД» (день человека, без часового сдвига). */
function dateToIso(d: Date | null): string {
  if (!d) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Сегодня в том же виде — им заполняется новый ряд «Дата» (пустой ряд не сохранился бы). */
export function todayIso(): string {
  return dateToIso(new Date());
}

/**
 * Что человек выбрал в календаре → «ГГГГ-ММ-ДДTЧЧ:ММ» (те самые цифры). Пояс к ним
 * приписывает chatUntilValue — московский, как у условий времени: без пояса узел считал
 * бы момент по часам сервера (живая проба 30.07 на дев-контейнере дала сдвиг на 4 часа).
 */
function wallClock(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Момент отправки рассылки «в дату» (ТЗ 12) → Date для календаря: те же цифры, что
 * человек выбрал. На узле он лежит БЕЗ пояса («2026-09-01T10:00»), а сам пояс — соседним
 * полем: так его читает движок запуска (broadcastDueAt), пересчитывая момент в UTC.
 */
function runAtToDate(runAt: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(runAt.trim());
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])) : null;
}

/** Пояс, в котором человек называет время отправки, — его собственный (канон LOCALE). */
function ownTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/** Момент из узла → Date для календаря: показываем ТЕ ЖЕ цифры, что человек выбрал. */
function untilToDate(until: string): Date | null {
  const wc = chatUntilWallClock(until);
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(wc);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])) : null;
}

/**
 * Дни недели чипами (1 = понедельник … 7 = воскресенье). Подписи — из ЛОКАЛИ кабинета
 * через Intl (канон locale-calendar: месяцы и дни не хардкодим), порядок — тоже локали:
 * в ru неделя начинается с понедельника, в en — с воскресенья.
 */
function WeekdayPick({ days, onChange }: { days: number[]; onChange: (next: number[]) => void }) {
  const locale = useLocale();
  const order = useMemo(() => {
    const info = (Intl as unknown as {
      Locale: new (l: string) => { weekInfo?: { firstDay?: number }; getWeekInfo?: () => { firstDay?: number } };
    }).Locale;
    let first = 1;
    try {
      const l = new info(locale);
      first = l.weekInfo?.firstDay ?? l.getWeekInfo?.().firstDay ?? 1;
    } catch {
      /* движок без weekInfo — понедельник */
    }
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 2021-08-02 — понедельник: день недели i (1…7) = 2021-08-(1+i).
    return Array.from({ length: 7 }, (_, i) => {
      const day = ((first - 1 + i) % 7) + 1;
      const label = fmt.format(new Date(2021, 7, 1 + day));
      return { day, label: label.charAt(0).toUpperCase() + label.slice(1).replace(/\.$/, '') };
    });
  }, [locale]);
  return (
    <div className="scn-days">
      {order.map((d) => {
        const on = days.includes(d.day);
        return (
          <KBareButton
            key={d.day}
            className={`scn-day${on ? ' on' : ''}`}
            aria-pressed={on}
            onClick={() => onChange(on ? days.filter((x) => x !== d.day) : [...days, d.day])}
          >
            {d.label}
          </KBareButton>
        );
      })}
    </div>
  );
}

// ── Вкладки «После / Между / До» рядов «Время» и «Дата» (У4/У5) ──────────────────────
// Порядок и слова — как у ЧП (скрин 20, вкладки над полями ряда). Вкладка живёт в самом
// ряду (chat-cond.ts): пока в календаре выбран только первый день, она обязана стоять на
// «Между», а не прыгать на «После» от того, что второй границы ещё нет.

const COND_WHEN_TABS: readonly ChatCondWhen[] = ['after', 'between', 'before'];

/**
 * У «Даты» вкладок четыре (заказ владельца 14.08): к ним добавлен РОВНО ЭТОТ ДЕНЬ —
 * промежуток «с 12-го по 12-е» человек писать не должен. Порядок — как заказан.
 */
const COND_DATE_WHEN_TABS: readonly ChatCondWhen[] = ['after', 'on', 'between', 'before'];

const WHEN_LABEL_KEY: Record<ChatCondWhen, string> = {
  after: 'chatCondWhenAfter',
  on: 'chatCondWhenOn',
  between: 'chatCondWhenBetween',
  before: 'chatCondWhenBefore',
};

/** Запасные границы «Времени» при смене вкладки — рабочий день (как у нового ряда). */
const TIME_WHEN_DEF = { from: '09:00', to: '18:00' };

/** Что делает выбранная вкладка «Времени» — словами, без часового пояса (он отдельно). */
function timeHint(t: (key: string) => string, r: ChatCondRow): string {
  if (r.when === 'after') return t('chatCondTimeAfterHint');
  if (r.when === 'before') return t('chatCondTimeBeforeHint');
  return condTimeAllDay(r) ? t('chatCondAllDayHint') : t('chatCondTimeHint');
}

/** То же для «Даты». */
function dateHint(t: (key: string) => string, r: ChatCondRow): string {
  if (r.when === 'on') return t('chatCondDateOnHint');
  if (r.when === 'after') return t('chatCondDateAfterHint');
  if (r.when === 'before') return t('chatCondDateBeforeHint');
  return t('chatCondDateHint');
}

// ── «Условие»: модалка настройки ОДНОГО УСЛОВИЯ (владелец 13.08, У16) ───────────────
// Экрана целого блока «Условие» и панели пилюли-ГРУППЫ больше НЕ СУЩЕСТВУЕТ: группа
// (переключатель «Все условия / Любое из условий» и «Добавить условие» в неё) правится
// ПРЯМО В ПУЗЫРЕ ПИЛЮЛИ НА КАРТОЧКЕ, а сюда открывается ровно одно условие — тапом по
// его строке в пузыре или сразу после выбора в каталоге. Поэтому здесь нет ни «Добавить
// условие», ни «всем/любому»: только поля этого условия, пояснение «Условие выполнено,
// если …» и корзина (она в шапке модалки — её ставит use-draft-editor).
//
// Модалка ПРОСТОРНАЯ: каждое поле — свой пузырь набора (FBlock/FChoiceBlock), а не строка
// в общем пузыре условия, как было в панели группы.

/**
 * Карточка целиком для записи в канон: из каждой пилюли уходят только ДОПИСАННЫЕ условия
 * (condRowsForCanon) — недописанное молча было бы всегда ложным (а «Является» с пустыми
 * полями всегда истинным), поэтому в сценарий оно не едет и живёт только на экране.
 */
const forCanon = (m: ChatSwitchModel, ctx?: { subscribeNoChannel?: boolean }): ChatSwitchModel => ({
  groups: m.groups.map((g) => ({ ...g, rows: condRowsForCanon(g.rows, ctx) })),
});

/**
 * Поля условия «Время» (У4). Отдельным куском, потому что их показывают ДВА окна: своя
 * модалка условия и общее окно группы «Дата и время» (Е1) — копии полей завести нельзя,
 * они разъедутся первой же правкой.
 *
 * У «Времени» и «Даты» сверху ВКЛАДКИ «После / Между / До» (эталон ЧП 20: у «Задержки»
 * так же переключаются «Длительность | Дата») — они меняют сами поля под собой, поэтому
 * это вкладки, а не список с галочкой. Равные границы у «Между» — это ВЕСЬ ДЕНЬ (условие
 * истинно круглые сутки): человек должен видеть это словами, а не гадать (приёмка К4).
 * Пара полей стоит в строку — каждое обёрнуто своей ячейкой сетки, иначе воздух между
 * полями сдвинул бы правое вниз. Часовой пояс назван ЧЕСТНО: формула прибивает московский.
 */
function CondTimeFields({
  r,
  patch,
  tried,
}: {
  r: ChatCondRow;
  patch: (p: Partial<ChatCondRow>) => void;
  /** Э7: красное — только после попытки применить. */
  tried: boolean;
}) {
  const t = useTranslations('cabinet.scenario');
  const fields = (
    <>
      {r.when === 'between' ? (
        <div className="scn-duo-fields">
          <div>
            <FIn label={t('chatCondFrom')}>
              <TimePick
                value={r.from}
                ariaLabel={t('chatCondFrom')}
                onChange={(v) => patch({ from: v })}
              />
            </FIn>
          </div>
          <div>
            <FIn label={t('chatCondTo')}>
              <TimePick value={r.to} ariaLabel={t('chatCondTo')} onChange={(v) => patch({ to: v })} />
            </FIn>
          </div>
        </div>
      ) : r.when === 'after' ? (
        <FIn label={t('chatCondTimeAfterIn')}>
          <TimePick
            value={r.from}
            ariaLabel={t('chatCondTimeAfterIn')}
            onChange={(v) => patch({ from: v })}
          />
        </FIn>
      ) : (
        <FIn label={t('chatCondTimeBeforeIn')}>
          <TimePick
            value={r.to}
            ariaLabel={t('chatCondTimeBeforeIn')}
            onChange={(v) => patch({ to: v })}
          />
        </FIn>
      )}
    </>
  );
  // Э5.2/5.3 (владелец 14.08): ЧТО проверяем — раскрытым списком с галочкой в своём
  // пузыре, а поля выбранного варианта — в СОСЕДНЕМ пузыре «Настройки». Прежние вкладки
  // над полями внутри одного пузыря владелец назвал бардаком: три вида управления
  // (тумблеры, вкладки, поля) стояли в одной коробке.
  return (
    <>
      <FChoiceBlock
        label={t('chatCondWhen')}
        value={r.when}
        options={COND_WHEN_TABS.map((w) => ({ value: w, label: t(WHEN_LABEL_KEY[w]) }))}
        onChange={(v) => patch(condWhenRow(r, v as ChatCondWhen, TIME_WHEN_DEF))}
      />
      <FBlock
        label={t('condSettingsLbl')}
        {...(tried && condRowWhenError(r)
          ? { error: t('chatCondTimeErr') }
          : { hint: `${timeHint(t, r)} ${t('chatCondTz')}` })}
      >
        {fields}
      </FBlock>
    </>
  );
}

/**
 * Поля условия «Дата» (У5) — те же два окна, что у «Времени» (см. CondTimeFields).
 * Начало позже конца: такой промежуток не подошёл бы никогда — условие красное и в
 * сценарий не уходит, пока даты не поменяют местами (Ф9). Промежуток («Между»)
 * выбирается ОДНИМ нашим календарём (DateRange): пока выбран только первый день,
 * условие честно красное и не сохраняется.
 */
function CondDateFields({
  r,
  patch,
  tried,
}: {
  r: ChatCondRow;
  patch: (p: Partial<ChatCondRow>) => void;
  /** Э7: красное — только после попытки применить. */
  tried: boolean;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const fields = (
    <>
      {r.when === 'on' ? (
        /* Ровно этот день (14.08): одна дата, целые сутки — второй границы нет. */
        <FIn label={t('chatCondDateOnIn')}>
          <DateTimePick
            withTime={false}
            clearable={false}
            value={isoToDate(r.from)}
            onChange={(d) => patch({ from: dateToIso(d) })}
          />
        </FIn>
      ) : r.when === 'between' ? (
        <DateRange
          presets={false}
          label={
            r.from && r.to
              ? `${dayText(r.from, lang)} — ${dayText(r.to, lang)}`
              : t('chatCondDatePickPh')
          }
          value={{ from: isoToDate(r.from), to: isoToDate(r.to) }}
          onChange={(rng) => patch({ from: dateToIso(rng.from), to: dateToIso(rng.to) })}
        />
      ) : r.when === 'after' ? (
        <FIn label={t('chatCondDateAfterIn')}>
          <DateTimePick
            withTime={false}
            clearable={false}
            value={isoToDate(r.from)}
            onChange={(d) => patch({ from: dateToIso(d) })}
          />
        </FIn>
      ) : (
        <FIn label={t('chatCondDateBeforeIn')}>
          <DateTimePick
            withTime={false}
            clearable={false}
            value={isoToDate(r.to)}
            onChange={(d) => patch({ to: dateToIso(d) })}
          />
        </FIn>
      )}
    </>
  );
  const err = !tried
    ? null
    : condRowRangeError(r)
      ? t('chatCondDateErr')
      : condRowWhenError(r)
        ? t('chatCondDateEmptyErr')
        : null;
  // Э5.2/5.3: список «После / В этот день / Между / До» — свой пузырь, поля выбранного
  // варианта — соседний пузырь «Настройки» (вкладок над полями больше нет).
  return (
    <>
      <FChoiceBlock
        label={t('chatCondWhen')}
        value={r.when}
        options={COND_DATE_WHEN_TABS.map((w) => ({ value: w, label: t(WHEN_LABEL_KEY[w]) }))}
        onChange={(v) => patch(condWhenRow(r, v as ChatCondWhen, { from: todayIso(), to: todayIso() }))}
      />
      <FBlock
        label={t('condSettingsLbl')}
        {...(err ? { error: err } : { hint: `${dateHint(t, r)} ${t('chatCondTzDay')}` })}
      >
        {fields}
      </FBlock>
    </>
  );
}

/**
 * Поля условия «День недели» (У6) — те же два окна. Ни одного дня: формулы у условия нет,
 * при сохранении оно молча пропало бы — говорим об этом прямо (Ф9). День недели тоже
 * считается по московскому времени (формула смотрит на те же часы) — говорим и это.
 */
function CondWeekdayFields({
  r,
  patch,
  tried,
}: {
  r: ChatCondRow;
  patch: (p: Partial<ChatCondRow>) => void;
  /** Э7: красное — только после попытки применить. */
  tried: boolean;
}) {
  const t = useTranslations('cabinet.scenario');
  const body = <WeekdayPick days={r.days} onChange={(days) => patch({ days })} />;
  // Э5.4: чипы дней остаются как были; у пузыря — своё описание (что проверяем и по
  // каким часам считаем).
  return (
    <FBlock
      label={t('chatCondDays')}
      {...(tried && condRowDaysError(r)
        ? { error: t('chatCondDaysErr') }
        : { hint: `${t('chatCondDaysHint')} ${t('chatCondTzDay')}` })}
    >
      {body}
    </FBlock>
  );
}

function CondRowEditor({
  node,
  row: rowRef,
  push,
  checkNode,
  pushCheck,
  condChannel,
  hasPhoneAsk,
  hasPayOffer,
  payProducts,
  botCredId,
  creds,
  onConnect,
  onRemove,
  tried,
  onIssue,
}: {
  node: WorkflowNode;
  /** Какое условие карточки настраиваем (У16). */
  row: ChatCondRowRef;
  /** Параметры шага + пилюли карточки, как их видит модалка (У21: новая пилюля живёт
   *  только здесь, и записать её можно лишь структурной правкой по этому списку). */
  push: (params: Record<string, unknown>, pills?: ChatCondModel[]) => void;
  checkNode: WorkflowNode | null;
  /** Э2: записать параметры вклеенного шага-данных (метки условия «Назначенные метки»). */
  pushCheck?: Push;
  condChannel: ChatCondChannel | null;
  /**
   * Есть ли в схеме действие «Запросить номер телефона» (владелец 13.08): условие
   * «человек отвечает на просьбу о номере» без него не сработает никогда.
   */
  hasPhoneAsk: boolean;
  /**
   * Товары, продаваемые В ЭТОЙ схеме (Э3): из них выбирает «Оплата прошла». Пусто —
   * продажи в схеме нет, и окно говорит об этом словами.
   */
  payProducts: readonly string[];
  /** Есть ли в схеме продажа — шаг «Оплата» или кнопка «Переход к оплате» (Э3.2). */
  hasPayOffer: boolean;
  /** У1: бот сценария — у него спрашиваем каналы для «Подписки». */
  botCredId: string | null;
  /** У2: подключения кабинета — есть ли касса (иначе оплате взяться неоткуда). */
  creds: readonly InstanceCredRef[];
  /** У2: увести в «Подключения» из жёлтой плашки. */
  onConnect?: (() => void) | undefined;
  /** Корзина условия (У21) — есть у КАЖДОГО вида, в том числе у видов без полей.
   *  Модалка отдаёт ГОТОВОЕ решение по своей модели; null — условия в схеме ещё нет. */
  onRemove?: ((plan: CondRemoveResult | null) => void) | undefined;
  /** Э7: человек уже жал галочку, а условие не применилось — с этого мига показываем
   *  красное. До первой попытки его нет ни строкой, ни подсветкой поля. */
  tried: boolean;
  /** Э7: что мешает применить условие прямо сейчас (null — всё дописано). По этой
   *  причине галочка шапки решает, закрывать окно или оставить. */
  onIssue: (issue: CondIssue | null) => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const initial = useMemo(() => readChatSwitch(node), [node]);
  const lang = useLang();
  // Э9.2: «спросить перед уборкой» отдаёт наружу спрятанный ConfirmInline — жмут нашу
  // кнопку набора, а разбор «родное окно / окошко сайта» остаётся у общего компонента.
  const askRemove = useRef<(() => void) | null>(null);
  const check = useMemo(() => (checkNode ? readChatSubscribe(checkNode, lang) : null), [checkNode, lang]);
  // Э4.1: канал «Подписки» не выбран — условие не подойдёт никогда (площадка ответит
  // «канала нет»). В сценарий такой ряд не уходит, а на экране краснеет. Инстаграма это
  // не касается: аккаунт там берётся из настроек сценария, выбирать нечего (Э4.2).
  const noChannel = check?.where === 'telegramChannel' && check.channel.text.trim() === '';
  const canonCtx = { subscribeNoChannel: noChannel };
  // У2: касса — любое подключённое приёмное подключение (Робокасса, ЮKassa, Stripe…):
  // счета выставляет и проверяет любая из них, а условию важно, что оплате есть откуда
  // взяться. Не подключено ни одной — жёлтая плашка с ходом в «Подключения».
  const payConnected = CHAT_PAY_PROVIDERS.some((p) => creds.some((c) => c.type === p.credType));
  // Э2: шаг-проверка МЕТОК вклеен в эту же карточку — метки правятся прямо в условии,
  // отдельной карточки «Проверить метки» у клиента больше нет.
  const tagsNode = checkNode && isTagsCheckNode(checkNode) ? checkNode : null;
  const tagsInitial = useMemo(
    () => (tagsNode ? readChatContacts(tagsNode, lang) : null),
    [tagsNode, lang],
  );
  const [tagsM, setTagsM] = useState<ChatContactsModel | null>(tagsInitial);
  // В какой пилюле живёт условие (по id её первого условия, номер — запасной путь).
  const gi0 = initial ? condPillIndex(initial.groups, { index: rowRef.pill, ...(rowRef.pillId ? { id: rowRef.pillId } : {}) }) : -1;
  // Новое условие из каталога живёт в модалке, пока его дописывают: формулы у него ещё нет,
  // а без формулы шаг такое условие не хранит (writeChatSwitch). Дописали — оно ушло в
  // сценарий и появилось в пузыре пилюли на карточке.
  const [m, setM] = useState<ChatSwitchModel | null>(() => {
    if (!initial || !rowRef.add) return initial;
    const row = newCondRow(rowRef.add, rowRef.addExpr ?? '', todayIso(), rowRef.addAt ?? '');
    // У21: НОВАЯ пилюля (своя ветка карточки) тоже живёт только здесь — в схеме её ещё
    // нет, поэтому дописываем её к прочитанным пилюлям сами; в канон она уедет вместе с
    // условием одним структурным ходом (галочка модалки).
    if (rowRef.newPill) return { groups: [...initial.groups, { rows: [row], any: false }] };
    if (gi0 < 0) return initial;
    return {
      groups: initial.groups.map((g, j) => (j === gi0 ? { ...g, rows: [...g.rows, row] } : g)),
    };
  });
  // Где в модели лежит настраиваемое условие: сперва по id (соседнее могли убрать —
  // номер после уборки промахнулся бы), номер — запасной путь. Считаем ДО ранних
  // return'ов: причину «чего не хватает» (Э7) нужно знать на каждом ходу, а вычислять её
  // после return нельзя — там уже нельзя звать хуки.
  const spot = ((): { gi: number; ri: number; row: ChatCondRow } | null => {
    if (!m) return null;
    const gi = condPillIndex(m.groups, { index: rowRef.pill, ...(rowRef.pillId ? { id: rowRef.pillId } : {}) });
    const g = m.groups[gi];
    if (!g) return null;
    // Только что заведённое условие ещё без id — оно последнее в своей группе.
    const ri = rowRef.add
      ? g.rows.length - 1
      : condRowIndex(g.rows, { index: rowRef.row ?? -1, ...(rowRef.rowId ? { id: rowRef.rowId } : {}) });
    const row = g.rows[ri];
    return row ? { gi, ri, row } : null;
  })();
  // Э7: одна причина, мешающая применить условие (считает ядро). По ней окно рисует
  // красную строку под заголовком, а галочка шапки решает — закрыться или остаться.
  const issue = spot
    ? condRowIssue(spot.row, {
        subscribeNoChannel: noChannel,
        tagsEmpty: !!tagsM && tagsM.tags.text.trim() === '',
        payHasProducts: payProducts.length > 0,
      })
    : null;
  useEffect(() => {
    onIssue(issue);
    // Причина сменилась — сказали шапке; сам onIssue приходит новой стрелкой каждый ход.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue]);
  // Новое условие кладём в правки СРАЗУ: у «Времени», «Даты», «Дня недели» и «Значения в
  // карточке» значения готовы с рождения — человек вправе ничего не менять и просто нажать
  // галочку, и строка условия обязана появиться. Пустые условия отбор всё равно не пропустит.
  useEffect(() => {
    if (rowRef.add && m) push(writeChatSwitch(node, forCanon(m, canonCtx)), forCanon(m, canonCtx).groups);
    // Один раз на открытие модалки: дальше правки идут через apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Э4.1: канал «Подписки» правят в СОСЕДНЕМ поле (шаг-проверка), а решает про канон сам
  // ряд — поэтому при появлении/исчезновении канала карточку надо переписать. Без этого
  // условие, выброшенное из канона пустым каналом, обратно уже не вернулось бы.
  const canonRef = useRef(noChannel);
  useEffect(() => {
    if (canonRef.current === noChannel) return;
    canonRef.current = noChannel;
    if (m) push(writeChatSwitch(node, forCanon(m, canonCtx)), forCanon(m, canonCtx).groups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noChannel]);
  if (!initial || !m || !spot) return null;
  const { gi, ri, row: r } = spot;

  const applyTags = (patch: Partial<ChatContactsModel>) => {
    if (!tagsNode || !tagsM) return;
    const next = { ...tagsM, ...patch };
    setTagsM(next);
    pushCheck?.(writeChatContacts(tagsNode, next));
  };

  // Правка идёт в СВОЁ условие; остальные условия и пилюли карточки уходят обратно как были.
  const patchRow = (patch: Partial<ChatCondRow>) => {
    const next = {
      groups: m.groups.map((x, j) =>
        j === gi ? { ...x, rows: x.rows.map((y, k) => (k === ri ? { ...y, ...patch } : y)) } : x,
      ),
    };
    setM(next);
    // Недописанное условие в сценарий НЕ уходит (condRowsForCanon): оно было бы молча
    // всегда ложным. На экране оно остаётся с красной подсказкой — человек дописывает.
    push(writeChatSwitch(node, forCanon(next, canonCtx)), forCanon(next, canonCtx).groups);
  };

  const opLabel: Record<ChatCondOp, string> = {
    eq: t('condOpEq'),
    ne: t('condOpNe'),
    contains: t('condOpContains'),
    notContains: t('condOpNotContains'),
    // «Начинается с» — действие ряда «Текст сообщения» (значению карточки не предлагается,
    // condOpsFor): так читается вход по команде «/start» с хвостом-приглашением.
    startsWith: t('condOpStartsWith'),
    // «Заполнено» / «Не заполнено» (Э1.2) — прежнее условие «В карточке есть» стало
    // действием этого же списка: спрашиваем не «чему равно», а «есть ли оно вообще».
    filled: t('condOpFilled'),
    notFilled: t('condOpNotFilled'),
    gt: t('condOpGt'),
    lt: t('condOpLt'),
  };

  // ЧУЖАЯ формула — единственное условие без полей и без своего пункта в каталоге (У19):
  // её писали не в кабинете, править её здесь нельзя (правка головы молча поменяла бы
  // смысл), поэтому человек видит саму формулу и может только убрать условие корзиной.
  // Наши смешанные формулы шаблонов («и это не команда») с 14.08 сюда НЕ относятся: они
  // открываются обычной формой своего вида, а хвост сохраняется сам (chat-cond.ts).
  const legacy = condRowLegacy(r);
  // Условие на служебную пометку кухни (владелец 13.08) — «Ждём номер»: человеческое имя,
  // никаких полей — выбирать в нём нечего. Просьбы о номере в схеме нет — красная строка:
  // ветка не сработает никогда, и починить это может только клиент (запрет №7 CLAUDE.md).
  const phoneWait = condRowPhoneWait(r);
  // Инстаграм: подписку на аккаунт клиента площадка отдаёт сама — полей у условия нет.
  const igSub = r.kind === 'subscribed' && !!check && check.where !== 'telegramChannel';
  // Условие БЕЗ ПОЛЕЙ (У19, владелец 14.08): вместо пустого экрана и строки «настраивать
  // здесь нечего» — одно человеческое объяснение пузырём набора. Строки «нечего
  // настраивать» больше не существует: она ничего не объясняла и пугала.
  const aboutKey = phoneWait
    ? 'condPhoneWaitAbout'
    : igSub
      ? 'condSubscribedIgAbout'
      : r.kind === 'sharedPhone'
        ? 'condSharedPhoneAbout'
        : r.kind === 'stepOk'
          ? 'condStepOkAbout'
          : null;

  // Э7.2: короткая строка «чего не хватает». «Укажите время» и «Выберите дату» — одна
  // причина ядра (граница не выбрана), но слова разные: у «Времени» и «Даты» человек
  // выбирает разное.
  const ISSUE_KEY: Record<CondIssue, string> = {
    value: 'condIssueValue',
    compare: 'condIssueCompare',
    number: 'condIssueNumber',
    range: 'condIssueRange',
    when: r.kind === 'date' ? 'condIssueDate' : 'condIssueTime',
    days: 'condIssueDays',
    tags: 'condIssueTags',
    product: 'condIssueProduct',
    channel: 'condIssueChannel',
  };

  return (
    <>
      {/* Э7.2: что мешает применить условие — ОДНА короткая красная строка по центру,
          первой строкой тела (в приложении заголовок рисует натив, писать в него нельзя,
          поэтому строка живёт здесь — визуально сразу под заголовком). Появляется только
          после попытки нажать галочку: до неё красного в окне нет вовсе. */}
      {tried && issue ? <FFormError>{t(ISSUE_KEY[issue])}</FFormError> : null}

      {/* «Оплата прошла» (У2): кассы нет — жёлтая плашка с ходом в «Подключения»
          (эталон ЧП 18) первой строкой: без кассы счёт не выставить и проверять нечего. */}
      {r.kind === 'paid' && !legacy && !payConnected ? (
        <ConnectNote text={t('chatCondPayNoCred')} onConnect={onConnect} />
      ) : null}

      {/* В схеме нет шага, который просит номер: ветка не сработает никогда. */}
      {phoneWait && !hasPhoneAsk ? <FError>{t('condPhoneWaitNoAsk')}</FError> : null}

      {/* Э3.2: в схеме никому ничего не продают — оплате взяться неоткуда, и условие не
          сработает никогда. Виноват клиент (он собирает схему), поэтому говорим словами
          и галочку не запрещаем — ровно как у соседа «Ждём номер». */}
      {r.kind === 'paid' && !legacy && !hasPayOffer ? (
        <FError>{t('condPaidNoOffer')}</FError>
      ) : null}

      {/* Подписка (У1): канал называется прямо здесь — шаг-проверка вклеен в карточку
          условия (chat-merge.ts), и гонять человека в отдельную шторку «настроить
          проверку» незачем. Поле, список каналов бота и подсказка «добавьте бота
          администратором» живут в одном месте на весь кабинет — SubscribeEditor.
          У Инстаграма полей нет вовсе — там пузырь-объяснение ниже. */}
      {r.kind === 'subscribed' && check && checkNode && pushCheck && !legacy && !igSub ? (
        <>
          <SubscribeEditor node={checkNode} push={pushCheck} botCredId={botCredId} />
          {/* Э4.1: канала нет — площадка ответит «канала нет», и условие не подойдёт
              никогда. Молчать нельзя: выбрать канал может только клиент. */}
          {tried && noChannel ? <FError>{t('condSubscribeNoChannel')}</FError> : null}
        </>
      ) : null}

      {/* «Оплата прошла» (У2): за какой продукт ждём оплату. Продукт не выбран —
          условие подойдёт на ЛЮБОЙ оплаченный счёт: формулу такого условия мы не
          выбрасываем (готовые схемы писали именно её), но человеку говорим прямо,
          что выбор за ним. */}
      {r.kind === 'paid' && !legacy ? (
        <FBlock
          label={t('chatCondProduct')}
          {...(tried && condRowPaidError(r)
            ? { error: t('chatCondProductErr') }
            : { hint: t('chatCondProductHint') })}
        >
          {/* Э3.3: выбираем ТОЛЬКО из того, что предлагается к покупке в этой схеме —
              за товар, которого здесь не продают, оплата не придёт никогда. Ранее
              выбранный товар, которого в схеме уже нет, пикер оставляет первой строкой
              и подписывает — молчать про него нельзя. */}
          <ProductSelect
            value={r.name}
            ariaLabel={t('chatCondProduct')}
            emptyHint={t('chatCondProductEmpty')}
            only={payProducts}
            onChange={(id) => patchRow({ name: id })}
          />
        </FBlock>
      ) : null}

      {/* «Назначенные метки» (Э2): какие метки ищем. Шаг-проверка вклеен в эту карточку
          (chat-merge), человеку он не показывается. У3: метки ВЫБИРАЮТ чипами из своих
          же (общий инструмент tag-picker) — печать руками плодила опечатки. */}
      {r.kind === 'tagsMatched' && tagsM && !legacy ? (
        <>
          <FBlock
            label={t('chatActTags')}
            {...(tried && tagsM.tags.text.trim() === ''
              ? { error: t('chatCondTagsEmptyErr') }
              : { hint: t('chatCondTagsHint') })}
          >
            <TagPicker
              value={tagsM.tags.text}
              ariaLabel={t('chatActTags')}
              onChange={(text) => applyTags({ tags: { ...tagsM.tags, text } })}
            />
            <ValueHint value={tagsM.tags} />
          </FBlock>
          {/* «Является / Не является» (эталон ЧП 18): раньше это был разовый выбор в
              шапке пузыря условия; в своей модалке он — обычный выбор из двух, значит
              раскрытый список с галочкой (канон формы). */}
          <FChoiceBlock
            label={t('chatCondOp')}
            value={r.not ? 'not' : 'is'}
            onChange={(v) => patchRow({ not: v === 'not' })}
            options={[
              { value: 'is', label: t('chatCondIs') },
              { value: 'not', label: t('chatCondIsNot') },
            ]}
          />
          <FChoiceBlock
            label={t('chatActCheckMode')}
            value={tagsM.checkAny ? 'any' : 'all'}
            onChange={(v) => applyTags({ checkAny: v === 'any' })}
            options={[
              { value: 'all', label: t('chatActCheckAll') },
              { value: 'any', label: t('chatActCheckAny') },
            ]}
          />
        </>
      ) : null}

      {r.kind === 'var' && !legacy ? (
        <>
          {/* Что проверяем — ОДНИМ списком всего, что бот про человека знает (У7):
              поля карточки по каналу, переменные каталога «Данные» и имена, которые
              запоминают шаги ботов. Список РАЗВЁРНУТ, и у каждого пункта своё
              пояснение (владелец 13.08). Значение не выбрано — условие в сценарий не
              уходит: молча оно сравнивало бы пустоту с пустотой и всегда подходило. */}
          {/* Э8.3: про общую карточку человека («одна на аккаунт, её видят все боты») не
              было сказано нигде — говорим прямо под списком значений. */}
          <FBlock
            label={t('condValLabel')}
            hint={t('condValCardHint')}
            {...(tried && condRowNameError(r) ? { error: t('chatCondVarNameErr') } : {})}
          >
            {/* Э2.4: сменили значение на текстовое, а выбрано было «Больше» — действие
                откатывается на «Является» (считает ядро, condValueRow): условия, которого
                нет ни в одном списке на экране, в каноне не остаётся. */}
            <CondValuePicker
              value={{ name: r.name, sys: r.sys }}
              channel={condChannel}
              ariaLabel={t('condValLabel')}
              onChange={(ref) => {
                const next = condValueRow(r, ref);
                patchRow({ name: next.name, sys: next.sys, op: next.op });
              }}
            />
          </FBlock>
          {/* «Выберите действие» (эталон ЧП 18): как сравнивать значение — раскрытым
              списком с галочкой, как велит канон формы. Состав списка зависит от
              выбранного значения (Э2.2): полю карточки «Больше»/«Меньше» не предлагаются
              — числа в имени и @логине не бывает. */}
          <FChoiceBlock
            label={t('chatCondOp')}
            value={r.op}
            onChange={(v) => patchRow({ op: v as ChatCondOp })}
            options={condOpsFor({ name: r.name, sys: r.sys }, r.op).map((op) => ({
              value: op,
              label: opLabel[op],
            }))}
          />
          {/* Не число при «больше/меньше» — условие НЕ уходит в сценарий (иначе оно молча
              было бы всегда ложным). Пустое поле — тоже: «содержит пусто» подходит ВСЕГДА.
              У «Заполнено»/«Не заполнено» поля нет вовсе (Э2.3): сравнивать нечего. */}
          {condOpIsFilled(r.op) ? null : (
            <FBlock
              label={t('chatActVarValue')}
              {...(!tried
                ? {}
                : condRowNumberError(r)
                  ? { error: t('chatCondNumErr') }
                  : condRowValueError(r)
                    ? { error: t('chatCondValueErr') }
                    : {})}
              {...(condOpIsNumeric(r.op) ? { hint: t('chatCondNumHint') } : {})}
            >
              <KInput
                value={r.value}
                onChange={(e) => patchRow({ value: e.target.value })}
                placeholder={condOpIsNumeric(r.op) ? t('chatCondNumPh') : t('chatActVarValuePh')}
                bad={tried && (condRowNumberError(r) || condRowValueError(r))}
                {...(condOpIsNumeric(r.op) ? { inputMode: 'decimal' as const } : {})}
              />
            </FBlock>
          )}
          {/* «Чувствительность к регистру» — только у сравнений текста: у «больше/
              меньше» обе стороны считаются числом, а «Заполнено» не сравнивает вовсе.
              Э8.2: тумблер стоит В ПУЗЫРЕ со своим заголовком — голым он лежал прямо на
              полотне окна единственной строкой без белого фона и без имени (канон формы:
              имя тумблера остаётся при нём, пузырю даётся имя группы). */}
          {condOpIsNumeric(r.op) || condOpIsFilled(r.op) ? null : (
            <FBlock label={t('condCompareLbl')}>
              <KSwitch
                label={t('chatCondCase')}
                hint={t('chatCondCaseHint')}
                checked={r.caseSensitive}
                onChange={(v) => patchRow({ caseSensitive: v })}
              />
            </FBlock>
          )}
        </>
      ) : null}

      {/* «Текст сообщения»: что человек написал боту прямо сейчас — подпись нажатой
          кнопки, кодовое слово, любая фраза. Поля те же, что у «Значения в карточке»
          (действие + значение + регистр), но выбирать НЕЧЕГО: текст один, а откуда его
          брать, знает сама схема (путь к событию шага запуска живёт в ряду). */}
      {r.kind === 'msgText' && !legacy ? (
        <>
          <FChoiceBlock
            label={t('chatCondOp')}
            value={r.op}
            onChange={(v) => patchRow({ op: v as ChatCondOp })}
            options={CHAT_MSG_TEXT_OPS.map((op) => ({ value: op, label: opLabel[op] }))}
          />
          <FBlock
            label={t('condMsgTextValue')}
            {...(tried && condRowValueError(r)
              ? { error: t('chatCondValueErr') }
              : { hint: t('condMsgTextValueHint') })}
          >
            <KInput
              value={r.value}
              onChange={(e) => patchRow({ value: e.target.value })}
              placeholder={t('condMsgTextValuePh')}
              bad={tried && condRowValueError(r)}
            />
          </FBlock>
          <FBlock label={t('condCompareLbl')}>
            <KSwitch
              label={t('chatCondCase')}
              hint={t('chatCondCaseHint')}
              checked={r.caseSensitive}
              onChange={(v) => patchRow({ caseSensitive: v })}
            />
          </FBlock>
        </>
      ) : null}

      {/* Категория «Время» (К4, формы — У4–У6): время суток, дни, дни недели.
          У «Времени» и «Даты» сверху ВКЛАДКИ «После / Между / До» (эталон ЧП 20:
          у «Задержки» так же переключаются «Длительность | Дата») — они меняют
          сами поля под собой, поэтому это вкладки, а не список с галочкой.
          Равные границы у «Между» — это ВЕСЬ ДЕНЬ (условие истинно круглые сутки):
          человек должен видеть это словами, а не гадать (приёмка К4). Пара полей
          стоит в строку — каждое обёрнуто своей ячейкой сетки, иначе воздух между
          полями сдвинул бы правое вниз. Часовой пояс назван ЧЕСТНО: формула
          прибивает московский (chat-cond.ts). */}
      {r.kind === 'time' && !legacy ? <CondTimeFields r={r} patch={patchRow} tried={tried} /> : null}

      {r.kind === 'date' && !legacy ? <CondDateFields r={r} patch={patchRow} tried={tried} /> : null}

      {r.kind === 'weekday' && !legacy ? <CondWeekdayFields r={r} patch={patchRow} tried={tried} /> : null}

      {/* «В карточке есть» (У8): список значений ТОТ ЖЕ, что у «Запомненного
          значения», — смысл условия «поле заполнено, неважно чем». Условие на
          служебную пометку кухни («человек отвечает на просьбу о номере») поля
          выбора НЕ получает вовсе: выбирать в нём нечего (владелец 13.08). */}
      {r.kind === 'contactHas' && !legacy && !phoneWait ? (
        <FBlock
          label={t('condValLabel')}
          {...(tried && condRowNameError(r) ? { error: t('chatCondVarNameErr') } : {})}
        >
          <CondValuePicker
            value={{ name: r.name, sys: r.sys }}
            channel={condChannel}
            ariaLabel={t('condValLabel')}
            onChange={(ref) => patchRow({ name: ref.name, sys: ref.sys })}
          />
        </FBlock>
      ) : null}

      {/* ЧУЖАЯ формула (У19): показываем её как есть — это единственное, что о ней можно
          сказать честно, — и объясняем, откуда она взялась и что с ней можно сделать. */}
      {legacy ? (
        <FBlock label={t('condRawFormula')} hint={t('condRawAbout')}>
          <FHint>{r.expr}</FHint>
        </FBlock>
      ) : null}

      {/* Условие БЕЗ ПОЛЕЙ (У19): одно человеческое объяснение пузырём набора — и всё.
          Ни «настраивать здесь нечего», ни второй фразы про то же самое: распорядиться
          таким условием можно корзиной в шапке модалки. */}
      {aboutKey ? (
        <FBlock label={t('condAboutLbl')}>
          <FHint>{t(aboutKey)}</FHint>
        </FBlock>
      ) : null}

      {/* Пояснение «Условие выполнено, если …» (У0, образец — синий баннер ЧП 18).
          Собирается из выбранного (cond-note.ts), поэтому не врёт после правки. У условий
          без полей его нет: там всё сказано пузырём-объяснением выше, а две фразы об одном
          и том же — это и есть «простыня», которую владелец запретил. */}
      {aboutKey || legacy ? null : <FHint>{condRowNote(t, r, lang)}</FHint>}

      {/* КОРЗИНА УСЛОВИЯ (У21, живая прокликка 14.08). Стоит у КАЖДОГО вида — и у тех, где
          настраивать нечего («Ждём номер», «Прислал номер кнопкой», «Прошлый шаг сработал»,
          подписка Инстаграма, чужая формула): иначе такое условие нельзя убрать вообще.
          Место — ТЕЛО модалки, а не шапка: в шапке кабинета живут только крестик и галочка
          (владелец 2026-08-11), и корзина оттуда просто не рисуется. Уборку считает
          use-draft-editor: ушло последнее условие пилюли — уходит и пилюля со своим выходом. */}
      {onRemove ? (
        <div className="cond-del">
          <KButton tone="danger" size="sm" onClick={() => askRemove.current?.()}>
            {t('chatCondRemove')}
          </KButton>
          {/* Э9.2: убирать условие — только после подтверждения. Спрашивает штатный
              ConfirmInline (в приложении родное окно iOS, на сайте — окошко по центру
              этого же окна): своего диалога-копии здесь нет. Триггер компонента спрятан,
              вопрос задаёт наша кнопка через registerAsk — вид кнопки набора сохранён. */}
          <span style={{ display: 'none' }}>
            <ConfirmInline
              label=""
              question={t('chatCondRemoveQ')}
              confirmLabel={t('chatCondRemove')}
              // Решение считаем ЗДЕСЬ, по модели самой модалки: искать условие заново по
              // меткам в живом каноне нельзя — не нашлось, и корзина молча закрывалась.
              // Новое условие, ещё не уехавшее в схему, даёт null: убирать нечего.
              onConfirm={() => onRemove(condRemoveRow(m.groups, gi, ri, checkNode?.name ?? ''))}
              registerAsk={(fn) => {
                askRemove.current = fn;
              }}
            />
          </span>
        </div>
      ) : null}
    </>
  );
}

/**
 * ОБЩЕЕ ОКНО ГРУППЫ каталога условий (Е1/Е2, владелец 13.08, вечер). Две группы
 * настраиваются не по одному условию, а целиком — так их и ставят:
 *  • «Дата и время» — три секции (время суток, даты, дни недели); включённые секции и
 *    есть условия пилюли, галочка шапки пишет их сразу (1–3 ряда одной правкой);
 *  • «Состояние диалога» — строки с галочками (Ждём номер, Прислал номер кнопкой,
 *    Прошлый шаг сработал); отметить можно несколько, каждая отмеченная — свой ряд.
 *
 * Состав строк по каналу считает то же чистое ядро, что и каталог (chatCondsFor):
 * у Инстаграма строк две, у чата на сайте и формы — одна. Мёртвых строк не бывает.
 *
 * ЗАПИСЬ — ТОЛЬКО ПО ГАЛОЧКЕ (механика этапа Д): окно правит свою модель и отдаёт её
 * наверх (push), а в схему всё уезжает одной правкой из шапки. Закрыл окно, ничего не
 * отметив, — в каноне не осталось ни ряда, ни новой пилюли (У21).
 *
 * Условия ДРУГИХ групп в этой же пилюле окно не трогает: оно работает только со своими
 * строками, остальные ряды уходят обратно как были.
 */
function CondGroupEditor({
  node,
  row: rowRef,
  group,
  push,
  condChannel,
  startName,
  hasPhoneAsk,
  onRemove,
}: {
  node: WorkflowNode;
  /** Какую пилюлю карточки настраиваем (У16: номер + метка группы). */
  row: ChatCondRowRef;
  group: ChatCondGroupId;
  /** Параметры шага + пилюли карточки, как их видит окно (У21: новая пилюля живёт здесь). */
  push: (params: Record<string, unknown>, pills?: ChatCondModel[]) => void;
  condChannel: ChatCondChannel | null;
  /** Имя шага запуска: из него собирается формула «Прислал номер кнопкой» (У19). */
  startName: string;
  /** Есть ли в схеме действие «Запросить номер телефона» (владелец 13.08). */
  hasPhoneAsk: boolean;
  /** Корзина: убрать ВСЕ условия этой группы из пилюли; null — их в схеме ещё нет. */
  onRemove?: ((plan: CondRemoveResult | null) => void) | undefined;
}) {
  const t = useTranslations('cabinet.scenario');
  const initial = useMemo(() => readChatSwitch(node), [node]);
  // Э9.2: «спросить перед уборкой» — тот же приём, что у окна одного условия.
  const askRemove = useRef<(() => void) | null>(null);
  const [m, setM] = useState<ChatSwitchModel | null>(() => {
    if (!initial) return null;
    // У21: НОВАЯ пилюля (своя ветка карточки) живёт только здесь — пока не отмечена ни
    // одна строка, в схему не уходит ничего: ни пустой пилюли, ни лишнего выхода.
    return rowRef.newPill ? { groups: [...initial.groups, { rows: [], any: false }] } : initial;
  });
  if (!initial || !m) return null;
  const gi = condPillIndex(m.groups, {
    index: rowRef.pill,
    ...(rowRef.pillId ? { id: rowRef.pillId } : {}),
  });
  const g = m.groups[gi];
  if (!g) return null;

  const picks = chatCondsFor(condChannel, group).map((c) => c.id);
  const rowOf = (pick: ChatCondPickId): ChatCondRow | null =>
    g.rows.find((r) => condRowPickId(r) === pick) ?? null;

  const setRows = (rows: ChatCondRow[]): void => {
    const next = { groups: m.groups.map((x, j) => (j === gi ? { ...x, rows } : x)) };
    setM(next);
    // Недописанное условие в сценарий НЕ уходит (condRowsForCanon) — на экране оно
    // остаётся с красной подсказкой, человек его дописывает.
    push(writeChatSwitch(node, forCanon(next)), forCanon(next).groups);
  };
  /**
   * Отмеченные строки списка (Ж8): список отдаёт весь набор целиком — приводим к нему
   * ряды пилюли. Порядок новых рядов — каталожный, чужие ряды (условия других групп)
   * остаются на месте.
   */
  const setPicks = (vals: readonly ChatCondPickId[]): void => {
    const keep = g.rows.filter((r) => {
      const pick = condRowPickId(r);
      return pick === null || !picks.includes(pick);
    });
    const mine = picks
      .filter((pick) => vals.includes(pick))
      .map((pick) => rowOf(pick) ?? condPickRow(pick, startName, todayIso()));
    setRows([...keep, ...mine]);
  };
  /**
   * Корзина группы: убираем ВСЕ её условия из пилюли — по одному тем же чистым ядром
   * (condRemoveRow), что и корзина одного условия. Своего правила уборки здесь нет:
   * второе разъехалось бы с первым (пилюля без условий уходит вместе со своим выходом,
   * последняя пилюля карточки остаётся пустой).
   */
  const removePlan = (): CondRemoveResult | null => {
    // Пилюли ещё нет в схеме (окно завело её только что, У21) — убирать нечего: закрытие
    // окна и есть отказ от неё. Писать «уборку» несуществующей ветки нельзя — её номер
    // сдвинул бы выходы соседних.
    if (rowRef.newPill) return null;
    let groups: ChatCondModel[] = m.groups;
    let removedPill: number | null = null;
    let hit = false;
    for (;;) {
      const rows = groups[gi]?.rows ?? [];
      const at = rows.findIndex((r) => {
        const pick = condRowPickId(r);
        return pick !== null && picks.includes(pick);
      });
      if (at < 0) break;
      const res = condRemoveRow(groups, gi, at);
      if (!res) break;
      hit = true;
      groups = res.groups;
      if (res.removedPill !== null) {
        removedPill = res.removedPill;
        break;
      }
    }
    // Шага-данных у этих групп не бывает (пары — только «Подписка» и «Метки»).
    return hit ? { groups, removedPill, dropCheck: false } : null;
  };

  // Ж8 (разбор владельца 13.08): строки окна собраны пузырями набора — раскрытым списком
  // с галочками (FChoiceManyBlock), тот самый штатный вид «отметить можно несколько».
  //
  // Э5.5 (владелец 14.08): ОБЩЕЕ окно осталось ТОЛЬКО у «Состояния диалога» — там условия
  // и правда ставят пачкой. «Дата и время» открывается подменю, и каждый её пункт имеет
  // своё окно, как у соседних групп (chat-catalog: open: 'menu').
  const chosen = picks.filter((pick) => rowOf(pick) !== null);

  return (
    <>
      <FChoiceManyBlock
        label={t('condGroupWhat')}
        values={chosen}
        options={picks.map((pick) => ({
          value: pick,
          label: t(condPickNameKey(pick)),
          hint: t(COND_HINT_KEYS[pick]),
        }))}
        onChange={(vals) => setPicks(vals as ChatCondPickId[])}
        {...(chosen.length === 0 ? { hint: t('condGroupDialogEmpty') } : {})}
      />
      {/* В схеме нет шага, который просит номер: ветка не сработает никогда. */}
      {chosen.includes('phoneWait') && !hasPhoneAsk ? (
        <FError>{t('condPhoneWaitNoAsk')}</FError>
      ) : null}

      {/* КОРЗИНА (У21): у окна группы она уносит все её условия сразу — по отдельности
          их снимают галочками выше. Место — ТЕЛО окна: в шапке кабинета живут только
          крестик и галочка (владелец 2026-08-11). */}
      {onRemove ? (
        <div className="cond-del">
          <KButton tone="danger" size="sm" onClick={() => askRemove.current?.()}>
            {t('chatCondRemoveGroup')}
          </KButton>
          {/* Э9.2: подтверждение — то же штатное (см. окно одного условия). */}
          <span style={{ display: 'none' }}>
            <ConfirmInline
              label=""
              question={t('chatCondRemoveGroupQ')}
              confirmLabel={t('chatCondRemoveGroup')}
              onConfirm={() => onRemove(removePlan())}
              registerAsk={(fn) => {
                askRemove.current = fn;
              }}
            />
          </span>
        </div>
      ) : null}
    </>
  );
}

// ── «Проверка подписки»: на какой канал смотрим ──────────────────────────────────────
// Своей карточки у шага нет — он вклеен в карточку условия (chat-merge.ts); открывается
// из неё («Настроить проверку») и из полной формы узла.

/** Ответ реестра каналов бота (Б3). */
interface BotChannelsResp {
  ok?: boolean;
  /** '@имя_бота' — кого просить добавить в канал; '' = имя спросить не вышло. */
  bot?: string;
  items?: Array<{
    chatId: string;
    username: string;
    title: string;
    chatType: string;
    botStatus: string;
    canCheck: boolean;
    /** Что пишется в шаг: '@логин' у публичного канала, id — у приватного. */
    address: string;
  }>;
}

/** Пункт выпадашки «назвать другой канал» (не адрес — служебное значение). */
const OTHER = '__other__';

function SubscribeEditor({
  node,
  push,
  botCredId,
  inRow,
}: {
  node: WorkflowNode;
  push: Push;
  botCredId: string | null;
  /**
   * Редактор стоит ВНУТРИ пузыря условия «Подписка» (владелец 13.08): тогда он не свой
   * пузырь, а поле в чужом — «пузырь в пузыре» запрещён. Своим шагом («Проверить
   * подписку» полной формой) он по-прежнему рисуется пузырём.
   */
  inRow?: boolean;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatSubscribe(node, lang), [node, lang]);
  const [channel, setChannel] = useState<ChatValue>(initial?.channel ?? chatValueOf());

  // Список каналов бота. ПОЧЕМУ СПИСОК НАШ, А НЕ ИЗ ТЕЛЕГРАМА: метода «верни чаты, где
  // состоит бот» у Bot API нет — каналы копятся из событий «бота добавили в канал»
  // (my_chat_member) и постов канала, плюс то, что человек назвал руками.
  const { data, reload } = usePersistentData<BotChannelsResp>(
    `cabinet:bot-channels:${botCredId ?? 'none'}`,
    botCredId ? `/api/cabinet/bot-channels?credId=${encodeURIComponent(botCredId)}` : '',
    { accept: (raw) => !!(raw as BotChannelsResp)?.ok },
  );
  const items = data?.items ?? [];
  const botName = data?.bot ?? '';

  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const [other, setOther] = useState(false);

  if (!initial) return null;

  // Инстаграм: проверяем подписку на аккаунт самого клиента — выбирать нечего.
  if (initial.where !== 'telegramChannel') return <DHint>{t('chatSubIgHint')}</DHint>;

  // Своим шагом редактор — пузырь набора, внутри условия — поле в его пузыре.
  const Wrap = inRow ? FIn : FBlock;

  const apply = (text: string): void => {
    const next = { ...channel, text };
    setChannel(next);
    push(writeChatSubscribe(node, { ...initial, channel: next }));
  };

  const same = (a: string, b: string): boolean =>
    a.replace(/^@/, '').toLowerCase() === b.replace(/^@/, '').toLowerCase();
  const chosen = items.find((i) => same(i.address, channel.text)) ?? null;
  // Канал в шаге записан, но нам не знаком (сценарий из шаблона, перенос) — это «другой»:
  // человек видит своё значение и кнопку проверки, а не пустую выпадашку.
  const unknown = channel.text.trim() !== '' && !chosen;
  const otherMode = other || unknown;

  const check = async (raw: string): Promise<void> => {
    // У1: адрес приводим к виду САМИ («t.me/name», «name» → «@name»): площадка понимает
    // только «@логин» или числовой id, и на прочем проверка молча отвечала бы «нет канала».
    const address = tgChannelAddress(raw);
    if (!botCredId || busy || address === '') return;
    setBusy(true);
    setFail(null);
    try {
      const res = await fetch('/api/cabinet/bot-channels', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ credId: botCredId, address }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        reason?: string;
        channel?: { address: string };
      };
      if (!res.ok) {
        setFail(t('chatSubErrNetwork'));
        return;
      }
      if (!json.ok) {
        setFail(
          t(`chatSubErr${(json.reason ?? 'network').replace(/^./, (c) => c.toUpperCase())}` as
            'chatSubErrNetwork', { bot: botName || t('chatSubYourBot') }),
        );
        // Канал назван, но негодный — записать его в шаг всё равно записываем: человек
        // видит, ЧТО он ввёл, и почему это пока не работает. Включение отобьёт линт.
        apply(address);
        reload();
        return;
      }
      apply(json.channel?.address ?? address);
      setOther(false);
      reload();
    } catch {
      setFail(t('chatSubErrNetwork'));
    } finally {
      setBusy(false);
    }
  };

  const options: SmartOption[] = [
    ...items.map((i) => ({
      value: i.address,
      label: i.title || i.address,
      desc: `${i.username ? '@' + i.username + ' · ' : ''}${
        i.canCheck ? t('chatSubIsAdmin') : t('chatSubNotAdmin')
      }`,
    })),
    { value: OTHER, label: t('chatSubOther') },
  ];

  return (
    /* Вводного описания под заголовком НЕТ (владелец 2026-08-11) — что делает шаг, видно
       по названию пузыря. Подсказка «добавьте бота администратором канала» ВИДНА ВСЕГДА
       (У1): без прав бота проверка подписки не работает ни у одного канала, а прятать
       пояснения полей запрещено. */
    <Wrap
      label={t('chatSubChannel')}
      hint={t('chatSubAddBotHint', { bot: botName || t('chatSubYourBot') })}
    >
      <>
        {/* Бот сценария не выбран — искать каналы не у кого; честно говорим куда идти. */}
        {!botCredId ? (
          <FError>{t('chatSubErrNoBot')}</FError>
        ) : (
          <>
            {/* Каналов у бота бывает много — здесь список длинный, значит SmartSelect
                (раскрытые строки с галочкой канон оставляет спискам до шести). */}
            <SmartSelect
              className="selbox"
              options={options}
              value={otherMode ? OTHER : (chosen?.address ?? '')}
              onChange={(v) => {
                setFail(null);
                if (v === OTHER) {
                  setOther(true);
                  setManual(channel.text);
                  return;
                }
                setOther(false);
                apply(v);
              }}
              placeholder={t('chatSubPick')}
              ariaLabel={t('chatSubChannel')}
            />

            {otherMode ? (
              <FIn label={t('chatSubOther')}>
                <div className="scn-sub-add">
                  {/* Поле набора — элемент набора (У1): человек пишет как привычно
                      («@имя», «t.me/имя», ссылкой), к виду площадки адрес приводит
                      tgChannelAddress при проверке. */}
                  <KInput
                    value={manual || (unknown ? channel.text : '')}
                    onChange={(e) => {
                      setManual(e.target.value);
                      setFail(null);
                    }}
                    onBlur={(e) => setManual(tgChannelAddress(e.target.value))}
                    placeholder={t('chatSubChannelPh')}
                  />
                  <KButton
                    tone="primary"
                    size="sm"
                    disabled={busy || tgChannelAddress(manual || channel.text) === ''}
                    onClick={() => void check(manual || channel.text)}
                  >
                    {busy ? t('chatSubChecking') : t('chatSubCheck')}
                  </KButton>
                </div>
              </FIn>
            ) : null}

            {/* Список пуст — объясняем, откуда каналы вообще берутся (метода «покажи все
                чаты бота» у Телеграма нет, ждать нечего: надо добавить бота или назвать
                канал руками). */}
            {items.length === 0 && !otherMode ? (
              <p className="scn-help-field">
                {t('chatSubEmpty', { bot: botName || t('chatSubYourBot') })}
              </p>
            ) : null}

            {/* канон: UI-TEMPLATE-CANON «Ошибка всего окна» (П128) — здесь ошибка ПОЛЯ
                (про выбранный канал), значит FError под полем. */}
            {fail ? <FError>{fail}</FError> : null}

            {/* Выбран известный канал: честный статус бота прямо здесь, а не «молча
                сломается на первом посетителе» (образец — «подключите кассу»). */}
            {chosen && !fail ? (
              chosen.canCheck ? (
                <p className="scn-help-field">{t('chatSubOk', { title: chosen.title || chosen.address })}</p>
              ) : (
                <FError>{t('chatSubErrNotAdmin', { bot: botName || t('chatSubYourBot') })}</FError>
              )
            ) : null}

            {chosen ? (
              <button
                type="button"
                className="btn sm"
                disabled={busy}
                onClick={() => void check(chosen.address)}
              >
                {busy ? t('chatSubChecking') : t('chatSubRecheck')}
              </button>
            ) : null}
          </>
        )}
        <ValueHint value={channel} />
      </>
    </Wrap>
  );
}

// ── «Старт»: где работает сценарий и когда запускается (шторка ЧП 13) ────────────────
// Среда не выбирается — она задана самим запуском (В5.2 правило 2), её показываем словами.
// Правится: что считается началом у Инстаграма (директ/комментарий), когда бот отдаёт
// сообщение этому сценарию («Срабатывать на» у ЧП: любое сообщение / команда /start /
// кодовое слово) и «Запретить повторный запуск» с окном в минутах.
//
// ЭТО ЕДИНСТВЕННОЕ МЕСТО настроек запуска (приказ владельца 30.07): из «О сценарии» они
// убраны — там остаются картинка, название, описание. Раньше одно и то же значение вели
// два стейта в разных шторках и расходились.

const START_HOLD_DEFAULT_MIN = 1440; // сутки — тайм-аут молчания по умолчанию (К11)

/** Ответ реестра ссылок (К18) — читаем общим кэшем раздела. */
interface RefLinksResp {
  ok?: boolean;
  items?: Array<{
    id: string;
    code: string;
    url: string;
    title: string;
    enabled: boolean;
    personal: boolean;
    source: string;
    scenarioId: string;
    clicks: number;
    runs: number;
  }>;
}

/**
 * Галочка «Также запускать при переходе по ссылке» (К18б, идея владельца): система сама
 * делает из кодового слова латинский хвост, заводит ссылку в реестре и показывает готовую
 * рядом. Снятая галочка ссылку НЕ удаляет, а выключает — счётчики переходов остаются,
 * включат обратно, и статистика источника продолжится.
 */
function StartRefLink({ draftId, word }: { draftId: string; word: string }) {
  const t = useTranslations('cabinet.scenario');
  const code = useMemo(() => translitRefCode(word), [word]);
  const { data, reload } = usePersistentData<RefLinksResp>('cabinet:ref-links', '/api/cabinet/ref-links', {
    accept: (raw) => !!(raw as RefLinksResp)?.ok,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Своя ссылка — та, что завела ЭТА галочка (source='keyword'): рукотворные из раздела
  // «Ссылки» галочка не трогает, даже если хвост совпал.
  const link =
    (data?.items ?? []).find((i) => i.scenarioId === draftId && i.source === 'keyword' && i.code === code) ?? null;
  const on = !!link?.enabled;

  // Слово не превращается в латиницу (одни эмодзи/знаки) — обещать ссылку нечем.
  if (!code) return null;

  const send = async (body: unknown, method: 'POST' | 'PATCH'): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/api/cabinet/ref-links', {
        method,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        // Занятый хвост — честно: это не «что-то пошло не так», а другое слово.
        setErr(
          json.error === 'code_taken'
            ? t('chatStartLinkTaken', { code })
            : json.error === 'no_bot'
              ? t('chatStartLinkNoBot')
              : t('chatStartLinkErr'),
        );
        return;
      }
      reload();
    } catch {
      setErr(t('chatStartLinkErr'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => {
    if (busy) return;
    if (link) void send({ id: link.id, enabled: !on }, 'PATCH');
    else void send({ draftId, code, title: word, source: 'keyword' }, 'POST');
  };

  return (
    /* Живёт ВНУТРИ пузыря «Кодовые слова»: строка-тумблер и поле готовой ссылки —
       набором (KSwitch + FIn), своей разметки нет (FORM-DEBT этап 0). */
    <>
      <KSwitch
        checked={on}
        onChange={() => toggle()}
        label={t('chatStartLink')}
        hint={t('chatStartLinkHint')}
      />
      {on ? (
        <FIn label={t('chatStartLinkReady')}>
          {link?.url ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={link.url} readOnly aria-label={t('chatStartLinkReady')} />
              <CopyBtn value={link.url} label={t('chatStartLinkCopy')} />
            </div>
          ) : (
            // Имя бота спрашивают у Телеграма: пока доступ не подключён, показываем хвост.
            <p className="scn-help-field">{t('chatStartLinkNoName', { code })}</p>
          )}
        </FIn>
      ) : null}
      {err ? <FError>{err}</FError> : null}
    </>
  );
}

/**
 * Список ссылок сценария в «Старте» — тип запуска «Переход по ссылке» (К37 А4, заказ
 * владельца 31.07: «разные ссылки ведут в разные сценарии»). У ЧП это отдельный раздел
 * «Реферальные ссылки», у нас — прямо в настройках запуска: человек видит, чем этот
 * сценарий открывается, не уходя со «Старта».
 *
 * Реестр общий с К18 (bot_ref_links, source='manual'): свои ссылки заводит здесь,
 * а раздел «Ссылки» показывает те же строки со статистикой. Крестик ссылку НЕ удаляет,
 * а выключает — счётчики переходов остаются, включат обратно, статистика источника
 * продолжится (и уже разосланная ссылка не превращается в чужую).
 */
function StartLinkList({ draftId, botHandle = null }: { draftId: string; botHandle?: string | null }) {
  const t = useTranslations('cabinet.scenario');
  const { data, reload } = usePersistentData<RefLinksResp>('cabinet:ref-links', '/api/cabinet/ref-links', {
    accept: (raw) => !!(raw as RefLinksResp)?.ok,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');

  // Личные ссылки людей (их заводит шаг сценария «Моя ссылка-приглашение») здесь не
  // показываем: это ссылки-источники владельца бота, а не карточки его клиентов.
  const items = (data?.items ?? []).filter((i) => i.scenarioId === draftId && !i.personal);

  const send = async (body: unknown, method: 'POST' | 'PATCH'): Promise<boolean> => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/api/cabinet/ref-links', {
        method,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        // Занятый хвост — честная подсказка «измените подпись», а не «что-то пошло не так».
        setErr(
          json.error === 'code_taken'
            ? t('chatStartLinkTakenTitle', { code: translitRefCode(title) })
            : json.error === 'no_bot'
              ? t('chatStartLinkNoBot')
              : t('chatStartLinkErr'),
        );
        return false;
      }
      reload();
      return true;
    } catch {
      setErr(t('chatStartLinkErr'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (): Promise<void> => {
    if (busy) return;
    const name = title.trim();
    // Хвост делаем из подписи транслитом; подпись из одних знаков хвоста не даёт.
    const code = translitRefCode(name);
    if (!code) {
      setErr(t('chatStartLinkBadTitle'));
      return;
    }
    if (await send({ draftId, title: name, code, source: 'manual' }, 'POST')) {
      setTitle('');
      setAdding(false);
    }
  };

  return (
    /* Пузырь набора (FORM-DEBT этап 0): название над пузырём, внутри список ссылок,
       добавление и подсказка. */
    <FBlock label={t('chatStartLinks')}>
      {items.length > 0 ? (
        <DList>
          {items.map((i) => (
            <DRow
              key={i.id}
              name={i.title || i.code}
              meta={
                <>
                  {i.url || t('chatStartLinkNoName', { code: i.code })}
                  {' · '}
                  {t('chatStartLinkStats', { clicks: i.clicks, runs: i.runs })}
                  {i.enabled ? '' : ` · ${t('chatStartLinkOffMark')}`}
                </>
              }
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {i.url ? <CopyBtn value={i.url} label={t('chatStartLinkCopy')} /> : null}
                {i.enabled ? (
                  <button
                    type="button"
                    className="btn-ghost"
                    aria-label={t('chatStartLinkOff')}
                    title={t('chatStartLinkOff')}
                    aria-busy={busy}
                    onClick={() => void send({ id: i.id, enabled: false }, 'PATCH')}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width={14}
                      height={14}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.2}
                      strokeLinecap="round"
                      aria-hidden
                    >
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-ghost"
                    aria-busy={busy}
                    onClick={() => void send({ id: i.id, enabled: true }, 'PATCH')}
                  >
                    {t('chatStartLinkOn')}
                  </button>
                )}
              </div>
            </DRow>
          ))}
        </DList>
      ) : (
        <p className="scn-help-field">{t('chatStartLinksEmpty')}</p>
      )}

      {adding ? (
        /* Добавление — ЭТАЛОННЫМИ полями «Проверки подписки» (владелец 11.08.2026:
           «эталон на проверке подписки, подключи компонент»): подпись + готовая
           ссылка, написанная заранее с многоточием вместо хвоста. */
        <>
          <StartLinkInner
            value={title}
            onChange={setTitle}
            botHandle={botHandle}
            autoFocus
            onEnter={() => void add()}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn-ghost" aria-busy={busy} onClick={() => void add()}>
              {t('chatStartLinkCreate')}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setAdding(false);
                setErr(null);
              }}
            >
              {t('chatStartLinkCancel')}
            </button>
          </div>
        </>
      ) : (
        <AddBlue
          label={t('chatStartLinkAdd')}
          onClick={() => {
            setErr(null);
            setAdding(true);
          }}
        />
      )}
      <p className="scn-help-field">{t('chatStartLinksHint')}</p>
      {err ? <FError>{err}</FError> : null}
    </FBlock>
  );
}

/**
 * Ф7.2: платёжная система запуска «Успешная оплата». Выбор — из ПОДКЛЮЧЁННЫХ платёжек
 * (+ текущая, даже неподключённая — селект не врёт); смена меняет ТРИГГЕР на
 * провайдерский (замена узла, параметры триггерам оплаты не нужны). Одна подключённая
 * и она же текущая — строка-состояние вместо селекта (выбор из одного — не выбор, К8).
 */
function StartPayProvider({
  node,
  creds,
  onSwitchStep,
  onConnect,
}: {
  node: WorkflowNode;
  creds: readonly InstanceCredRef[];
  onSwitchStep?: ((nodeType: string, params: Record<string, unknown> | null) => void) | undefined;
  onConnect?: (() => void) | undefined;
}) {
  const t = useTranslations('cabinet.scenario');
  const provider = chatPayProviderOfTrigger(node.type);
  if (!provider) return null;
  const options = CHAT_PAY_PROVIDERS.filter(
    (p) => p.id === provider || creds.some((c) => c.type === p.credType),
  );
  const connected = creds.some((c) => c.type === chatPayProviderMeta(provider).credType);
  return (
    <>
      {!connected ? <ConnectNote text={t('chatPayNoProvider')} onConnect={onConnect} /> : null}
      {options.length > 1 && onSwitchStep ? (
        /* Платёжек не больше шести — выбор стоит раскрытым списком с галочкой
           отдельным пузырём (канон формы), а не выпадашкой. */
        <FChoiceBlock
          label={t('chatPayProvider')}
          value={provider}
          onChange={(v: string) => {
            if (v === provider) return;
            // Параметры триггерам оплаты не нужны — чистая замена типа узла.
            onSwitchStep(chatPayProviderMeta(v as ChatPayProvider).triggerType, null);
          }}
          options={options.map((p) => ({ value: p.id, label: t(p.nameKey) }))}
        />
      ) : connected ? (
        /* Выбирать не из чего (К8) — пузырь показывает выбранную платёжку читаемым
           значением; строке «ключ — значение» (DKV) в форме не место. */
        <FBlock label={t('chatPayProvider')}>{t(chatPayProviderMeta(provider).nameKey)}</FBlock>
      ) : null}
    </>
  );
}

const START_ANTI_REPEAT_DEFAULT = 60; // минут — окно по умолчанию, когда тумблер включают

// Экспорт — для страницы «Вручную» (/app/bots/manual): там старт настраивается ДО
// создания черновика ТЕМ ЖЕ редактором (заказ владельца 2026-08-03, близнецов не заводим).
/** Поля ссылки-приглашения «?start=»: подпись ссылки + готовая ссылка с копированием.
 *  INVARIANT(scriptera): блок ОДИН на весь кабинет — им пользуются и мастер шаблона
 *  (template-wizard), и настройка запуска. Причина (владелец 2026-08-08): ссылки уже
 *  сделаны и приняты в мастере; второй, «свой» вид ссылок на другой странице заводить
 *  нельзя — это тот самый зоопарк одинаковых по смыслу форм. Копию не делать: нужен
 *  другой вид — меняется здесь, и меняется у всех. */
export function StartLinkFields({
  value,
  onChange,
  botHandle,
  warn,
  credId,
  chain,
}: {
  value: string;
  onChange: (v: string) => void;
  botHandle: string | null;
  warn?: string;
  /** Доступ бота-хозяина ссылки — по нему собираются ссылки из других ботов кабинета. */
  credId?: string;
  /** Ссылка ведёт в цепочку, а не в бота: меняется одно слово подводки. */
  chain?: boolean;
}) {
  const t = useTranslations('cabinet.scenario');
  // ТЕЛЕФОН В БРАУЗЕРЕ — ТРИ ОТДЕЛЬНЫЕ КАРТОЧКИ, КАК В iOS (ТЗ WEB-PARITY-3 М74, кадр
  // W33-tg-trigger-link): «Ваша ссылка» (ссылка + кнопка-копия), «Откуда придут
  // пользователи» (поле), «Ссылки из ваших ботов»; подписи и подсказки снаружи.
  const phoneWeb = usePhoneLook();
  if (phoneWeb) {
    return (
      <>
        <StartLinkReadyRow botHandle={botHandle} tail={translitRefCode(value)} chain={chain} block />
        <FBlock label={t('chatStartLinkWhere')} hint={t('chatStartLinkWhereHint')} {...(warn ? { error: warn, bad: true } : {})}>
          <KInput
            value={value}
            maxLength={80}
            placeholder={t('chatStartLinkWherePh')}
            aria-label={t('chatStartLinkWhere')}
            onChange={(e) => onChange(e.target.value)}
          />
        </FBlock>
        <BotLinksPick credId={credId ?? ''} onPick={onChange} {...(chain ? { chain: true } : {})} block />
      </>
    );
  }
  return (
    /* ПОРЯДОК ПО ЗАКАЗУ ВЛАДЕЛЬЦА (К17.4 п.6, П7 06.09.2026: «нажимаешь „Переход по
       ссылке“ — и никаких настроек нет… напиши слово — и из-за этого генерируется
       ссылка: флоу непонятный и нелогичный»): сперва САМА ССЫЛКА с копированием и
       объяснением, под ней второстепенное «Где стоит ссылка», ниже — ссылки, которые
       уже отдают другие боты кабинета. */
    <FBlock label={t('chatStartLinkKind')}>
      <StartLinkReadyRow botHandle={botHandle} tail={translitRefCode(value)} chain={chain} />
      <StartLinkLabelRow
        value={value}
        onChange={onChange}
        label={t('chatStartLinkWhere')}
        hint={t('chatStartLinkWhereHint')}
        {...(warn ? { warn } : {})}
      />
      <BotLinksPick credId={credId ?? ''} onPick={onChange} {...(chain ? { chain: true } : {})} />
    </FBlock>
  );
}

/** Внутренности эталона «Переход по ссылке» БЕЗ пузыря — для мест, где пузырь уже
 *  есть (список ссылок «Старта»): подпись + готовая ссылка. Готовая ссылка написана
 *  ЗАРАНЕЕ (владелец 11.08.2026): хвост стоит многоточием, пока подпись не введена. */
export function StartLinkInner({
  value,
  onChange,
  botHandle,
  warn,
  autoFocus,
  onEnter,
}: {
  value: string;
  onChange: (v: string) => void;
  botHandle: string | null;
  warn?: string;
  autoFocus?: boolean;
  onEnter?: () => void;
}) {
  const t = useTranslations('cabinet.scenario');
  return (
    <>
      <StartLinkLabelRow
        value={value}
        onChange={onChange}
        label={t('chatStartLinkTitle')}
        hint={t('chatStartLinkNameHint')}
        {...(warn ? { warn } : {})}
        {...(autoFocus ? { autoFocus } : {})}
        {...(onEnter ? { onEnter } : {})}
      />
      <StartLinkReadyRow botHandle={botHandle} tail={translitRefCode(value)} />
    </>
  );
}

/** Поле подписи источника. Подпись и пояснение приходят снаружи: у входа в анкете это
 *  «Где стоит ссылка» (К17.4 п.6 б), в списке ссылок «Старта» — «Откуда ссылка». */
function StartLinkLabelRow({
  value,
  onChange,
  label,
  hint,
  warn,
  autoFocus,
  onEnter,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  hint: string;
  warn?: string;
  autoFocus?: boolean;
  onEnter?: () => void;
}) {
  return (
    <FIn label={label} hint={hint} {...(warn ? { error: warn } : {})}>
      <input
        value={value}
        maxLength={80}
        placeholder={label}
        aria-label={label}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
      />
    </FIn>
  );
}

/**
 * САМА ССЫЛКА С КОПИРОВАНИЕМ. Видна СРАЗУ (владелец 11.08.2026): вместо будущего
 * хвоста — многоточие. Подводка объясняет, что с ней делать (К17.4 п.6 а).
 *
 * ОДНОЙ СТРОКОЙ, ОБРЕЗКА ПОСЕРЕДИНЕ (К18.1 п.4 п.3, владелец 06.09.2026: «не видно самую
 * важную часть ссылки, хвост с кодом обрезан»; ссылку в две строки он отверг). Приём
 * Telegram и Safari: начало и ХВОСТ С КОДОМ видны всегда, прячется середина — поэтому
 * строка собрана из двух половин, а не одним полем с `text-overflow` справа. Полная
 * ссылка — по кнопке копирования и по наведению (`title`).
 */
export function StartLinkReadyRow({
  botHandle,
  tail,
  chain,
  block,
  hint,
}: {
  botHandle: string | null;
  tail: string;
  chain?: boolean;
  /** Своей карточкой (телефон в браузере, М74), а не строкой внутри общего пузыря. */
  block?: boolean;
  /** Своя подводка под ссылкой (пустая — без подводки). Нет ключа — подводка входа
   *  «Переход по ссылке». Так же строка стоит у «Ссылки в Telegram-бот» (UT 14.10 п.6). */
  hint?: string;
}) {
  const t = useTranslations('cabinet.scenario');
  const locale = useLocale();
  const head = botHandle ? `https://t.me/${botHandle}` : null;
  const codePart = `?start=${tail || '…'}`;
  const url = head ? `${head}${codePart}` : null;
  const Wrap = block ? FBlock : FIn;
  return (
    <Wrap
      label={t('chatStartLinkYours')}
      {...(hint === undefined
        ? { hint: t(chain ? 'chatStartLinkYoursChain' : 'chatStartLinkYoursHint') }
        : hint
          ? { hint }
          : {})}
      {...(block ? { className: 'scn-link-blk' } : {})}
    >
      {url ? (
        <div className="scn-link-ready">
          <span className="scn-link-cut" title={url}>
            <span className="scn-link-cut__head">{head}</span>
            <span className="scn-link-cut__tail">{codePart}</span>
          </span>
          {tail ? <CopyBtn value={url} label={t('chatStartLinkCopy')} /> : block ? <CopyBtn value="" label={t('chatStartLinkCopy')} disabled /> : null}
        </div>
      ) : block ? (
        // Бота ещё нет — те же слова, что на родной странице (`wizLinkReadyText`), и бледная
        // кнопка-копия на месте (кадр W33-tg-trigger-link).
        <div className="scn-link-ready">
          <span className="scn-link-cut" title="">
            <span className="scn-link-cut__head">{wizLinkReadyText(locale === 'en' ? 'en' : 'ru', botHandle, tail)}</span>
          </span>
          <CopyBtn value="" label={t('chatStartLinkCopy')} disabled />
        </div>
      ) : (
        <p className="scn-help-field">
          {tail ? t('chatStartLinkNoName', { code: tail }) : t('chatStartLinkKindHint')}
        </p>
      )}
    </Wrap>
  );
}

/** Ответ ручки «ссылки на этого бота из других ботов кабинета» (К17.4 п.6 в). */
interface BotLinksResp {
  ok?: boolean;
  items?: Array<{
    draftId: string;
    title: string;
    url: string;
    code: string;
    known: boolean;
    where: 'button' | 'text';
  }>;
}

/**
 * ССЫЛКИ ИЗ ВАШИХ БОТОВ (заказ владельца 06.09.2026, П7: «ты должен собирать все ссылки
 * с ботов, которые отдают ссылку, автоматически… ты должен её автоопределять, как
 * продолжение»). Собирает их сервер по схемам черновиков (/api/cabinet/bot-links).
 *
 * Выбор строки ставит код этой ссылки подписью источника — вход встаёт ровно на неё.
 * Голая `t.me/<бот>` без хвоста входом стать не может (отличить источник нечем): такая
 * строка стоит с объяснением словами и не нажимается.
 */
function BotLinksPick({
  credId,
  onPick,
  chain,
  block,
}: {
  credId: string;
  onPick: (code: string) => void;
  /** Ссылка ведёт в цепочку: меняется последняя фраза подводки (К18.1 п.4 п.4). */
  chain?: boolean;
  /** Пустое состояние — своей карточкой (телефон в браузере, М74). */
  block?: boolean;
}) {
  const t = useTranslations('cabinet.scenario');
  const hint = t(chain ? 'chatStartFromBotsHintChain' : 'chatStartFromBotsHint');
  const { data } = usePersistentData<BotLinksResp>(
    `cabinet:bot-links:${credId}`,
    credId ? `/api/cabinet/bot-links?cred=${encodeURIComponent(credId)}` : '',
    { accept: (raw) => !!(raw as BotLinksResp)?.ok },
  );
  if (!credId) return null;
  const items = data?.items ?? [];
  if (items.length === 0) {
    const Wrap = block ? FBlock : FIn;
    return (
      <Wrap label={t('chatStartFromBots')} hint={hint}>
        <p className="scn-help-field">{t('chatStartFromBotsEmpty')}</p>
      </Wrap>
    );
  }
  return (
    <FNotifyList
      label={t('chatStartFromBots')}
      hint={hint}
      items={items.map((i) => ({
        key: `${i.draftId}-${i.url}`,
        label: t('chatStartFromBotRow', { name: i.title }),
        // Ссылка с меткой читается сама собой; голая — со словами, почему её нельзя взять.
        summary: i.code ? i.url : t('chatStartFromBotBare'),
        chevron: !!i.code,
        ...(i.code ? { onOpen: () => onPick(i.code) } : {}),
      }))}
    />
  );
}

/** Настройки вида рассылки, живущие в параметрах узла «Начало рассылки» (ТЗ 12). */
interface BcastFields {
  /** Метка-повод (вид «по метке»). */
  tag: string;
  /** Момент отправки «ГГГГ-ММ-ДДTЧЧ:ММ» без пояса (вид «в дату»). */
  runAt: string;
  /** Пояс, в котором назван момент (IANA) — по нему движок пересчитает его в UTC. */
  tz: string;
  /** Условия отбора людей (виды «вручную» и «в дату»); пусто — все живые контакты. */
  audience: AudienceValue;
}

/** Д5: сообщает открывателю, финальный ли шаг мастера (галочка шапки — только там).
 *  Отдельный компонент, чтобы хук жил без условий: StartEditor рано возвращается. */
function WizStageProbe({ last, onStage }: { last: boolean; onStage: (last: boolean | null) => void }) {
  const ref = useRef(onStage);
  useEffect(() => {
    ref.current = onStage;
  }, [onStage]);
  useEffect(() => {
    ref.current(last);
    return () => ref.current(null);
  }, [last]);
  return null;
}

export function StartEditor({
  node,
  onState,
  startMeta,
  onStartKind,
  startKinds,
  channel,
  draftId,
  creds,
  onSwitchStep,
  onConnect,
  onConnectAccount,
  onFinish,
  finishLabel,
  onWizStage,
}: {
  node: WorkflowNode;
  onState: (s: ChatStepState) => void;
  startMeta: ChatStartMeta;
  /** Канал сценария: «без прерывания» и ссылки есть только у диалоговых каналов. */
  channel: ChatChannelId | null;
  /** Подключённые доступы — из них выбирается аккаунт сценария (К34). */
  creds: readonly InstanceCredRef[];
  /** Черновик — по нему заводится ссылка реестра для кодового слова (К18). */
  draftId?: string;
  /** Сменить ТИП запуска (плитки ЧП 13) — это замена шага, её делает use-draft-editor. */
  onStartKind?: (kind: ChatStartKind) => void;
  startKinds?: readonly ChatStartKind[];
  /** Ф7.2: сменить платёжку запуска-оплаты — замена триггера на провайдерский. */
  onSwitchStep?: (nodeType: string, params: Record<string, unknown> | null) => void;
  /** Ф7.3: «Подключить» из плашки «нет платёжки». */
  onConnect?: () => void;
  /** Подключить аккаунт канала прямо из формы (владелец 2026-08-11): форма подключения
   *  открывается на месте (openConnect в use-draft-editor); не задан — уводим в
   *  «Подключения» через onConnect. */
  onConnectAccount?: () => void;
  /** МАСТЕР (CABINET-UX-FIXLIST п.6): галочка ПОСЛЕДНЕГО шага. Делает ровно то же, что
   *  подтверждение в шапке хозяина — в конструкторе «сохранить и закрыть», на странице
   *  «Вручную» «создать бота». Своей логики сохранения у формы нет и быть не должно:
   *  слепок шага уезжает наверх через onState на каждую правку. */
  onFinish?: () => void;
  /** Д5: галочка шапки — только на последнем шаге мастера; null — мастера нет. */
  onWizStage?: (last: boolean | null) => void;
  /** Подпись финальной галочки — ТА ЖЕ, что у подтверждения в шапке хозяина («Применить»
   *  в конструкторе, «Создать» на странице «Вручную»): одно действие зовётся одинаково
   *  в обоих местах экрана. Не задана — общее «Сохранить» мастеров. */
  finishLabel?: string;
}) {
  const t = useTranslations('cabinet.scenario');
  // Подписи навигации мастера — ОДНИ на все мастера кабинета (cabinet.automations:
  // wizStepOf/wizBack/wizNext): второго словаря «Шаг N из M» не заводим.
  const ta = useTranslations('cabinet.automations');
  // Слова экрана «Кто получит» — те же, что у мастера рассылки (cabinet.audience).
  const tAud = useTranslations('cabinet.audience');
  const initial = useMemo(() => readChatStart(node), [node]);
  const [m, setM] = useState<ChatStartModel | null>(initial);
  const [meta, setMeta] = useState<ChatStartMeta>(startMeta);
  // ── РАССЫЛКА (ТЗ 12) ───────────────────────────────────────────────────────────────
  // Настройки вида рассылки живут в ПАРАМЕТРАХ узла (метка, момент с поясом, условия
  // отбора) — модель «Старта» везёт только сам вид (mode в поле event). Держим их
  // отдельным состоянием и кладём на узел общим сводом writeBroadcastStart: там же
  // чистятся настройки, которых у выбранного вида нет.
  const bcastInitial = useMemo(() => readBroadcastStart(node), [node]);
  const isBcast = initial?.channel === 'broadcast';
  const [bc, setBc] = useState<BcastFields>(() => ({
    tag: bcastInitial?.tag ?? '',
    runAt: bcastInitial?.runAt ?? '',
    tz: bcastInitial?.tz || ownTimeZone(),
    audience: audienceOfRaw(bcastInitial?.audience ?? null),
  }));
  // Состояние запуска и кнопки «Отправить»/«Остановить» — ОБЩИЕ со страницей рассылки.
  // Пустой черновик (шторка создания) ручку не дёргает: отправлять ещё нечего.
  const launchCtl = useBroadcastLaunch(isBcast && draftId ? draftId : '');
  const [mins, setMins] = useState(String(startMeta.antiRepeatMin || START_ANTI_REPEAT_DEFAULT));
  // Тумблер «Не запускать повторно» — СВОИМ состоянием, не выводом из минут: раньше
  // стёртое поле минут (0) гасило тумблер, и поле исчезало прямо под пальцем — вписать
  // новое число было нельзя (аудит Ф9). Пустое поле теперь остаётся с красной подсказкой.
  const [antiOn, setAntiOn] = useState(startMeta.antiRepeatMin > 0);
  // «Работать без прерывания» (К11): тайм-аут человек видит в часах или минутах — голое
  // «1440» ничего не говорит. Внутрь (и на сервер) уходят минуты.
  const [holdUnit, setHoldUnit] = useState<'hours' | 'minutes'>(() =>
    startMeta.holdTimeoutMin % 60 === 0 ? 'hours' : 'minutes',
  );
  const [holdAmount, setHoldAmount] = useState(() =>
    String(
      (startMeta.holdTimeoutMin || START_HOLD_DEFAULT_MIN) % 60 === 0
        ? (startMeta.holdTimeoutMin || START_HOLD_DEFAULT_MIN) / 60
        : startMeta.holdTimeoutMin || START_HOLD_DEFAULT_MIN,
    ),
  );
  // К34 (заказ владельца 2026-07-31): от чьего имени работает сценарий. Состояние
  // считает ОБЩЕЕ ядро botAccountState — то же, что отбивает включение и привязывает
  // доступ при установке: разъедутся — человек снова увидит один аккаунт, а работать
  // будет другой. Узел один, схема тут не нужна — заворачиваем его в мини-канон.
  const account = botAccountState({ name: '', nodes: [node], connections: {} }, creds);
  const [credId, setCredId] = useState<string | null>(account?.chosenId ?? null);
  // Ф0 (BOT-INTEGRATIONS 0.2): выбранный аккаунт удалили из подключений — на месте выбора
  // стоит пометка «Аккаунт удалён», а не пустое поле и не имя первого попавшегося: молча
  // подменить аккаунт значит сменить лицо сценария незаметно. Судим по ЖИВОМУ выбору
  // (credId), а не по слепку узла — после перевыбора пометка гаснет сразу.
  // Спрятанный двойник учётки (переподключение) — НЕ удалённый аккаунт: он есть в списке
  // под своим каноническим кредом (credRefById), а бот на нём собран и работает.
  const chosenOpt = account ? credRefById(account.options, credId) : null;
  const chosenGone = !!account && credId !== null && !chosenOpt;
  // Выбор публикаций (К37 Z4): «все» / «выбранные» — отдельным состоянием, потому что
  // «выбранные» с ещё пустым списком тоже режим, а не отсутствие выбора.
  const [postsMode, setPostsMode] = useState<'all' | 'some'>(startMeta.mediaIds.length > 0 ? 'some' : 'all');
  const [pickPosts, setPickPosts] = useState(false);
  // Ошибка «добавьте слово» — ТОЛЬКО ПОСЛЕ ПОПЫТКИ СОХРАНИТЬ (владелец 2026-08-06:
  // «не должна появляться ошибка, пока я ещё даже не пытался»). Прежнее «показать сразу,
  // если шаг уже сохранён сломанным» отменено: человек открывал шторку и упирался в
  // красный текст, ничего не сделав. Флаг поднимает onInvalid — его зовёт галочка шапки,
  // когда commit вернул «не готово».
  const [submitTried, setSubmitTried] = useState(false);
  // Ф16.4 (владелец 2026-08-18): «этот аккаунт уже занят другим ВКЛЮЧЁННЫМ ботом» —
  // человек узнаёт об этом ЗДЕСЬ, а не при включении (раньше создание молчало, и два
  // бота на одном боте-аккаунте с одинаковым запуском рождались без единого слова).
  // Подсказка НЕ блокирует: у второго бота может быть смысл, решает человек — счёт
  // ведёт тот же расчёт пересечений, что предупреждает при включении (один на всех).
  const [busyBots, setBusyBots] = useState<string[]>([]);
  const busyWords = meta.keywords.join(',');
  const busyPosts = meta.mediaIds.join(',');
  // Входов у бота бывает несколько — спрашиваем «занято ли» по всем разом (список через
  // запятую; ручка bot-account-busy считает пересечение по каждому).
  const busyEvent = m?.channel === 'instagram' ? m.events.join(',') : '';
  useEffect(() => {
    // У рассылки этого вопроса нет: она никого не слушает и ни с кем за сообщения не
    // спорит — один бот-отправитель спокойно везёт сколько угодно рассылок.
    if (!credId || isBcast) {
      setBusyBots([]);
      return;
    }
    let alive = true;
    // Пауза перед вопросом: слова человек набирает по букве, а ответ меняется только
    // после слова целиком — дёргать сервер на каждый символ незачем.
    const timer = setTimeout(() => {
      const q = new URLSearchParams({ credId, kind: meta.kind, words: busyWords });
      if (busyPosts) q.set('posts', busyPosts);
      if (busyEvent) q.set('event', busyEvent);
      if (draftId) q.set('exclude', draftId);
      fetch(`/api/cabinet/bot-account-busy?${q.toString()}`)
        .then((r) => r.json())
        .then((j: { ok?: boolean; busy?: string[] } | null) => {
          if (alive) setBusyBots(j?.ok ? (j.busy ?? []) : []);
        })
        .catch(() => {
          // Не посчитали — молчим: подсказка не повод пугать («это не ворота»).
          if (alive) setBusyBots([]);
        });
    }, 400);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [credId, isBcast, meta.kind, busyWords, busyPosts, busyEvent, draftId]);
  // «Переход по ссылке» в шторке СОЗДАНИЯ (черновика нет — реестр завести не в кого,
  // владелец 2026-08-04, IMG_1261): подпись ссылки едет в слепке, ссылку заведёт
  // «Создать». Ref — чтобы emit из любых обработчиков видел свежий ввод.
  const [linkCode, setLinkCode] = useState('');
  const linkCodeRef = useRef('');
  // Родной слой для листа публикаций (IMG_1264, канон §11: вложенность — стопкой).
  const postsPanel = useSlidePanel();
  // Аккаунт едет в КАЖДОМ слепке «Старта»: слепок — целиком состояние шага, и если
  // отдать его без аккаунта, следующая правка кодового слова сбросила бы выбор обратно.
  // (Объявлены ДО раннего return: их зовёт эффект автовыбора ниже.)
  // Режим «по слову» без единого слова — сценарий не запустится никогда: это и есть
  // единственная блокирующая ошибка «Старта».
  const wordsMissing = (nextMeta: ChatStartMeta): boolean =>
    (nextMeta.kind === 'keyword' || nextMeta.kind === 'exact') && nextMeta.keywords.length === 0;
  /**
   * ЧТО НЕ ТАК У РАССЫЛКИ — словами и только то, что человек правит сам (запрет №7):
   * не выбран бот-отправитель (без него слать некому и нечем), не выбрана метка-повод,
   * не назван момент отправки или он уже прошёл. Аудитория пустой быть МОЖЕТ — это
   * «все живые контакты», а не ошибка.
   */
  const bcastIssue = (nextNode: ChatStartModel, nextCred: string | null, nextBc: BcastFields): string | null => {
    if (nextNode.channel !== 'broadcast') return null;
    if (!nextCred) {
      return account && account.options.length === 0 ? t('chatStartNoBot') : t('bcastNoBotErr');
    }
    const rule = broadcastModeRule(nextNode.events[0] ?? '');
    if (rule.needsTag && nextBc.tag.trim() === '') return t('bcastTagErr');
    if (rule.needsDate) {
      const at = runAtToDate(nextBc.runAt);
      if (!at) return t('bcastDateErr');
      if (at.getTime() <= Date.now()) return t('bcastDatePastErr');
    }
    return null;
  };
  const emit = (
    nextNode: ChatStartModel,
    nextMeta: ChatStartMeta,
    nextCred: string | null,
    nextBc: BcastFields = bc,
  ) => {
    if (!m) return;
    const bcast = nextNode.channel === 'broadcast';
    onState({
      name: node.name,
      params: bcast
        ? writeBroadcastStart(node, {
            mode: (nextNode.events[0] ?? '') as BroadcastMode,
            tag: nextBc.tag.trim(),
            runAt: nextBc.runAt,
            tz: nextBc.tz,
            // Пустые условия — «все»: на узел уезжает пустой набор, а не «ничего не
            // задано». Строкой — как объявлено поле узла (свод writeBroadcastStart).
            audience: audienceQuery(nextBc.audience),
          })
        : writeChatStart(node, nextNode),
      meta: nextMeta,
      credentialId: nextCred,
      ...(linkCodeRef.current ? { linkCode: linkCodeRef.current } : {}),
      hasIssues: bcast ? bcastIssue(nextNode, nextCred, nextBc) !== null : wordsMissing(nextMeta),
      onInvalid: () => setSubmitTried(true),
    });
  };
  // Слепок с оценкой «готов ли шаг» нужен галочке СРАЗУ — до первой правки её не было бы
  // (emit зовут только обработчики), и шторка закрывалась бы на пустых словах.
  useEffect(() => {
    if (m) emit(m, meta, credId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.kind, meta.keywords.length]);
  const pickAccount = (id: string) => {
    setCredId(id);
    if (m) emit(m, meta, id);
  };
  // Владелец 05.08: «везде, где просят выбрать аккаунт, ставь что-то сразу выбранным» —
  // пустой выбор при живых вариантах молча берёт первый (через pickAccount: выбор сразу
  // уезжает в слепок шага, «Создать» без единой правки его не потеряет).
  useEffect(() => {
    if (credId === null && account && account.options.length > 0) {
      pickAccount(account.options[0]!.id);
    }
  });
  // ── МАСТЕР: на каком шаге стоим, тряска и красная строка (CABINET-UX-FIXLIST п.6) ──
  // Механика — ровно как у мастера быстрой настройки (template-wizard): «Дальше» всегда
  // нажимается, недозаполненный шаг не молчит — поле трясётся, телефон вибрирует, а
  // причина встаёт КРАСНОЙ СТРОКОЙ В ТЕЛЕ шага под вступлением и стоит, пока её не
  // устранили (канон: UI-TEMPLATE-CANON «Ошибка всего окна» (П128); вспышки в планке и
  // таймера на 2.5 секунды больше нет).
  const [wizIdx, setWizIdx] = useState(0);
  const [wizShake, setWizShake] = useState(0);
  const [wizTried, setWizTried] = useState(false);
  if (!initial || !m) return null;
  const startKind = chatStartKindOf(m);
  // Паспорта ОТМЕЧЕННЫХ входов Инстаграма (ТЗ 02.09 §3.4): подписи, оговорки и «есть ли
  // у входа слова» приходят из свода, а не из разметки. Слова показываем, если они есть
  // хотя бы у одного отмеченного входа: иначе бот с личкой и реакцией на историю терял
  // бы кодовое слово из-за реакции. У других каналов слова были и остаются.
  const igRules = m.events.map((e) => igStartEventRule(e));
  const igWords = m.channel !== 'instagram' || igRules.some((r) => r.words);
  const igHasComment = m.channel === 'instagram' && m.events.includes('comment');
  // Свой текущий тип в списке есть всегда (иначе плитки врали бы про то, что открыто).
  const tiles = [...new Set([...(startKinds ?? ['message', 'pay']), startKind])] as ChatStartKind[];

  const setLink = (v: string) => {
    setLinkCode(v);
    linkCodeRef.current = v;
    emit(m, meta, credId);
  };
  // Выбор публикаций — ВЕРТИКАЛЬНЫМ нижним листом (владелец 05.08, финальное:
  // «первое окно — горизонтальной, фотографии — вертикальной»); горизонтальным
  // становится само окно «Старта», пара «лист поверх листа» исчезает с той стороны.
  // Веб — BottomSheet (pickPosts).
  const openPostsSheet = () => {
    if (postsPanel.available) {
      postsPanel.open({
        title: t('chatStartPosts'),
        sheet: true,
        actionLabel: t('chatStartPostsOk'),
        actionIcon: 'checkmark',
        onAction: () => postsPanel.close(),
        body: (
          <PostsSheetBody
            credId={credId}
            initial={meta.mediaIds}
            onPick={(ids) => apply(m, { ...meta, mediaIds: ids })}
          />
        ),
      });
    } else {
      setPickPosts(true);
    }
  };
  const apply = (nextNode: ChatStartModel, nextMeta: ChatStartMeta) => {
    setM(nextNode);
    setMeta(nextMeta);
    emit(nextNode, nextMeta, credId);
  };
  /** Правка настройки рассылки (метка, момент, условия отбора) — тем же путём наверх. */
  const applyBcast = (patch: Partial<BcastFields>, nextNode: ChatStartModel = m) => {
    const next = { ...bc, ...patch };
    setBc(next);
    if (nextNode !== m) setM(nextNode);
    emit(nextNode, meta, credId, next);
  };
  // Подпись поля аккаунта — с именем канала (владелец 05.08, IMG_1330).
  const accLabel = account
    ? account.channel === 'telegram'
      ? t('chatStartBot')
      : account.channel === 'instagram'
        ? t('chatStartAccountIg')
        : t('chatStartAccount')
    : '';
  // Кодовые слова — чипы (шторка ЧП 13): каждое слово отдельной плашкой с крестиком,
  // добавление по Enter. Кап 20 — как у сервера (meta.triggerKeywords).
  const setWords = (list: string[]) => {
    apply(m, { ...meta, keywords: list.slice(0, 20) });
  };
  // Тумблер «Запретить повторный запуск»: выключен = 0 минут (без ограничения); включён —
  // окно из поля. Кламп тот же, что у сервера (0…10080 = неделя).
  const clamp = (raw: string): number => Math.max(0, Math.min(10080, parseInt(raw, 10) || 0));
  // Диспетчер водит человека только в диалоговых каналах: у формы и чата на сайте
  // перехватывать некому — галочку там не показываем (мёртвых настроек не делаем).
  const dialogChannel = channel === 'telegram' || channel === 'instagram';
  const holdMinutes = (amount: string, unit: 'hours' | 'minutes'): number => {
    const n = parseInt(amount, 10) || 0;
    return Math.max(1, Math.min(10080, unit === 'hours' ? n * 60 : n));
  };
  const setMinsText = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, '');
    setMins(digits);
    if (antiOn) apply(m, { ...meta, antiRepeatMin: clamp(digits) });
  };

  // ── КУСКИ ФОРМЫ ───────────────────────────────────────────────────────────────────
  // Те же контролы, что стояли одной простынёй, — со всей начинкой (подсказки, красные
  // строки, листы, ссылки). Мастер (ниже) раскладывает их по шагам, а каналы без мастера
  // (запуск «по оплате», форма и чат на сайте) печатают их подряд, как раньше.
  // ЖЕЛЕЗНОЕ «Control move parity»: контрол переезжает ЦЕЛИКОМ, ничего не упрощая.

  /* ОШИБКА — ПЕРВЫМ ЭЛЕМЕНТОМ ТЕЛА, красным по центру (канон drawer-kit п.1, заказ
     владельца 2026-08-06: «сверху над формой красным посередине должно быть
     написано, что не так»). Появляется только после попытки сохранить: до неё
     человек ничего красного не видит. Поле слов при этом подсвечено.
     Ф16.3 (владелец 2026-08-18): «на карточке горит „!“, а текста ошибки нигде нет» —
     строка сверху называет причину СЛОВАМИ. Правило «чей косяк» (CLAUDE.md запрет
     №7) держится: здесь только то, что человек правит сам (слова запуска, аккаунт,
     занятый аккаунт). Наши внутренние поломки схемы сюда не попадают ни строкой,
     ни значком — они уходят дежурному (reportUiError/центр ошибок) и чиним их мы.
     В мастере строка стоит НАД шагами — она про весь запуск, а не про один экран. */
  const errStrip =
    submitTried && wordsMissing(meta) ? (
      <DError>{t('chatStartWordsErr')}</DError>
    ) : chosenGone ? (
      <DError>{t('chatStartAccGoneErr')}</DError>
    ) : account && account.options.length === 0 ? null : busyBots.length > 0 ? (
      /* Ф16.4: не ошибка настройки, а предупреждение о последствии — но говорим о нём
         той же верхней строкой (заказ владельца) и запуск НЕ держим. */
      <DError>{t('chatStartBusyErr', { names: busyBots.join(', ') })}</DError>
    ) : null;

  const tilesNode = (
    <>
      {/* ПЛИТКИ ТИПА ЗАПУСКА (эталон ЧП 13, К8): разговор начинает человек или успешная
          оплата. Смена типа = замена шага запуска: её везёт use-draft-editor, и канальный
          замок К0 не даст осиротить шаги прежнего канала (отказ человеческим текстом).
          Рисуются ТОЛЬКО когда выбор настоящий (владелец 2026-08-04: радиогруппа из
          одного пункта — мёртвый контрол, копия ЧП, убрать; раньше single рисовался
          «текущим состоянием» — теперь и его нет, тип и так виден по форме). */}
      {onStartKind && tiles.length > 1 ? (
        <div className="scn-tiles" role="radiogroup" aria-label={t('chatStartType')}>
          {tiles.map((k) => {
            const on = startKind === k;
            return (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={on}
                className={`scn-tile${on ? ' on' : ''}`}
                onClick={() => {
                  if (!on) onStartKind(k);
                }}
              >
                <span className="scn-tile-ico" aria-hidden>
                  <RowGlyph icon={k === 'pay' ? 'pay' : 'cond'} size={20} />
                </span>
                <span className="scn-tile-name">
                  {k === 'pay' ? t('chatStartTilePay') : t('chatStartTileMessage')}
                </span>
                <span className={`scn-radio${on ? ' on' : ''}`} aria-hidden />
              </button>
            );
          })}
        </div>
      ) : null}
    </>
  );

  const payNode = (
    <>
      {/* Вводного абзаца «Сценарий начинается, когда…» больше нет (владелец 2026-08-04:
          тонны текста) — он остался только у оплаты, где без него не ясно, что это. */}
      {m.channel === 'pay' ? <DHint>{t('chatStartHintPay')}</DHint> : null}

      {/* Ф7.2: платёжная система запуска-оплаты. Триггер жёстко не Робокасса: смена
          платёжки — замена триггера на провайдерский (тем же путём, что смена типа
          запуска). Одна подключена — выбрана сама, селект не мозолит; ноль — плашка. */}
      {m.channel === 'pay' ? (
        <StartPayProvider node={node} creds={creds} onSwitchStep={onSwitchStep} onConnect={onConnect} />
      ) : null}
    </>
  );

  /* АККАУНТ (или плашка «подключите аккаунт») — ПЕРВЫМ (владелец 2026-08-11, К34).
     Пузыри и их содержимое — ТОЛЬКО компонентами form-kit (замок FORM_BUBBLE_RULES). */
  const accountNode = (
    <>
      {m.channel !== 'pay' && account && account.options.length === 0 ? (
        /* Аккаунта нет ВООБЩЕ — та же канонная плашка, что в мастере шаблона и у оплаты
           (раньше тут был тупик «нужен доступ», а исправить из формы было нечем).
           «Подключить» открывает форму подключения на месте (onConnectAccount →
           openConnect), запасной путь — раздел «Подключения». */
        <ConnectNote
          text={t(account.channel === 'telegram' ? 'chatStartNoBot' : 'chatStartNoIgAccount')}
          onConnect={onConnectAccount ?? onConnect}
        />
      ) : null}
      {m.channel !== 'pay' && account && account.options.length > 0 ? (
        /* Аккаунт — ПЕРВЫМ (К34), РАСКРЫТЫМ списком с галочкой (владелец 2026-08-11,
           эталон — выбор аккаунта в мастере); длинному списку (>6) раскрытые строки не
           помещаются — там SmartSelect в том же пузыре. Привязанный доступ мог УЙТИ
           (Ф0) — красная строка «выберите заново» до перевыбора. */
        account.options.length <= 6 ? (
          <FChoiceBlock
            label={accLabel}
            value={chosenOpt?.id ?? ''}
            onChange={pickAccount}
            options={account.options.map((c) => ({ value: c.id, label: botAccountLabel(c) }))}
            {...(chosenGone ? { error: t('chatStartAccountGone') } : {})}
          />
        ) : (
          <FBlock label={accLabel} {...(chosenGone ? { error: t('chatStartAccountGone') } : {})}>
            <SmartSelect
              wrapValue
              className="selbox"
              value={chosenOpt?.id ?? ''}
              placeholder={chosenGone ? t('chatStartAccountDeleted') : t('chatStartAccountPick')}
              ariaLabel={accLabel}
              onChange={pickAccount}
              options={account.options.map((c) => ({ value: c.id, label: botAccountLabel(c) }))}
            />
          </FBlock>
        )
      ) : null}
    </>
  );

  /* ПРАВИЛА ЗАПУСКА — ОДНИМ пузырём (владелец 2026-08-11; урок приёмки этапа 0: имя
     тумблера остаётся ПРИ нём жирным, пузырю — имя группы). «Работать без прерывания»
     (К11): пока человек внутри этого сценария, чужие кодовые слова и «любое сообщение»
     его не перехватывают; вечного замка не бывает — рядом тайм-аут молчания.
     В простыне пузырь стоял ВЫШЕ способа запуска; в мастере он — ПОСЛЕДНИЙ шаг
     (CABINET-UX-FIXLIST п.6: сначала «кто и что запускает», потом «как ведёт себя
     дальше»). Сам пузырь и вся его начинка — один в один. */
  const rulesNode =
    // У рассылки правил диалога нет: она никого не ждёт и никого не перехватывает.
    m.channel === 'pay' || m.channel === 'broadcast' ? null : (
        <>
          <FBlock label={t('chatStartRules')}>
            {dialogChannel ? (
              <KSwitch
                label={t('chatStartHold')}
                hint={t('chatStartHoldHint')}
                checked={meta.holdDialog}
                onChange={() =>
                  apply(m, {
                    ...meta,
                    holdDialog: !meta.holdDialog,
                    holdTimeoutMin: holdMinutes(holdAmount, holdUnit),
                  })
                }
              />
            ) : null}
            {dialogChannel && meta.holdDialog ? (
              <FIn label={t('chatStartHoldTimeout')} hint={t('chatStartHoldTimeoutHint')}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="number"
                    min={1}
                    value={holdAmount}
                    aria-label={t('chatStartHoldTimeout')}
                    style={{ maxWidth: 110 }}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, '');
                      setHoldAmount(v);
                      apply(m, { ...meta, holdTimeoutMin: holdMinutes(v, holdUnit) });
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <SmartSelect
                      value={holdUnit}
                      onChange={(v) => {
                        const unit = v as 'hours' | 'minutes';
                        setHoldUnit(unit);
                        apply(m, { ...meta, holdTimeoutMin: holdMinutes(holdAmount, unit) });
                      }}
                      options={[
                        { value: 'hours', label: t('chatStartHoldHours') },
                        { value: 'minutes', label: t('chatStartHoldMinutes') },
                      ]}
                      ariaLabel={t('chatStartHoldTimeout')}
                    />
                  </div>
                </div>
              </FIn>
            ) : null}
            <KSwitch
              label={t('chatStartAntiRepeat')}
              hint={t('chatStartAntiRepeatHint')}
              checked={antiOn}
              onChange={() => {
                const next = !antiOn;
                setAntiOn(next);
                apply(m, { ...meta, antiRepeatMin: next ? clamp(mins) || START_ANTI_REPEAT_DEFAULT : 0 });
              }}
            />
            {antiOn ? (
              /* Пустое поле (0 минут) — ограничения нет, хотя тумблер включён: честно
                 говорим об этом красным, а поле не прячем (Ф9). */
              <FIn
                label={t('chatStartAntiRepeatMin')}
                {...(clamp(mins) === 0 ? { error: t('chatStartAntiRepeatErr') } : {})}
              >
                <input
                  value={mins}
                  inputMode="numeric"
                  maxLength={5}
                  aria-label={t('chatStartAntiRepeatMin')}
                  onChange={(e) => setMinsText(e.target.value)}
                />
              </FIn>
            ) : null}
          </FBlock>
        </>
      );

  /* ЧТО ЗАПУСКАЕТ — событие Инстаграма и публикации под ним. Каждый блок — пузырь
     набора со своим заголовком («заголовок у пузыря — каждый раз»).
     У Телеграма своего события нет — шаг мастера с этим куском не заводится вовсе
     (как в мастере запуска канала: шаг без единого видимого поля пропускается). */
  const entryNode =
    m.channel !== 'pay' && m.channel !== 'broadcast' ? (
        <>
          {/* ТИПЫ ЗАПУСКА ИНСТАГРАМА (К37-Z3): список — ДАННЫМИ из свода IG_START_EVENTS
              (@scriptera/ui): за каждым типом стоит своё поле вебхука Меты. Строками с
              галочкой, а не выпадашкой (владелец 2026-08-10). */}
          {m.channel === 'instagram' ? (
            <FChoiceManyBlock
              label={t('chatStartEvent')}
              values={m.events}
              onChange={(v) => {
                // Порядок свода, и хотя бы один вход обязан остаться: «Старт» без входа
                // не запускается никогда, а молча мёртвый бот — худшее, что бывает.
                const next = IG_START_EVENTS.map((r) => r.event as string).filter((e) => v.includes(e));
                const events = next.length > 0 ? next : m.events;
                // Слов не осталось ни у одного входа («Реакция», «Отметка») — режим
                // «содержит» молча глушил бы бота, поэтому сбрасываем его сами.
                const wordy = events.some((e) => igStartEventRule(e).words);
                const kept = wordy ? meta : { ...meta, kind: 'message' as const, keywords: [] };
                // Убрали комментарий — выбранные публикации теряют смысл (В1: выбор бывает
                // только у входа-комментария). Оставить их = молча слушать чужие посты.
                const posts = events.includes('comment') ? kept.mediaIds : [];
                if (posts.length === 0) {
                  setPostsMode('all');
                  setPickPosts(false);
                }
                apply({ ...m, events }, { ...kept, mediaIds: posts });
              }}
              options={IG_START_EVENTS.map((r) => ({
                value: r.event,
                label: t(r.nameKey),
                hint: t(r.hintKey),
              }))}
            />
          ) : null}

          {/* ВЫБОР ПУБЛИКАЦИЙ — СРАЗУ после события (владелец 2026-08-04: «комментарий
              под публикацией → следующий вопрос — под какой»). К37 Z4, В1/В5, правило
              ЧП 10500036: только когда единственный триггер — комментарий. Выбранные —
              маленькими фото прямо в форме; сам выбор — отдельным листом поверх
              (приложение — родной слой, канон §11; веб — BottomSheet). */}
          {igHasComment ? (
            <FBlock label={t('chatStartPosts')}>
              <SmartSelect
                wrapValue
                className="selbox"
                value={postsMode}
                ariaLabel={t('chatStartPosts')}
                onChange={(v) => {
                  setPostsMode(v as 'all' | 'some');
                  if (v === 'all') {
                    setPickPosts(false);
                    apply(m, { ...meta, mediaIds: [] });
                  } else {
                    openPostsSheet();
                  }
                }}
                options={[
                  { value: 'all', label: t('chatStartPostsAll') },
                  { value: 'some', label: t('chatStartPostsSome') },
                ]}
              />
              {postsMode === 'some' ? (
                <>
                  <div className="igm-row">
                    <IgMediaChosen credId={credId} ids={meta.mediaIds} />
                    <button type="button" className="btn sm" onClick={openPostsSheet}>
                      {meta.mediaIds.length > 0 ? t('chatStartPostsEdit') : t('chatStartPostsChoose')}
                    </button>
                  </div>
                  {pickPosts ? (
                    <BottomSheet title={t('chatStartPosts')} onClose={() => setPickPosts(false)}>
                      {(close) => (
                        <PostsSheetBody
                          credId={credId}
                          initial={meta.mediaIds}
                          onPick={(ids) => apply(m, { ...meta, mediaIds: ids })}
                          onOk={close}
                        />
                      )}
                    </BottomSheet>
                  ) : null}
                </>
              ) : null}
            </FBlock>
          ) : null}
        </>
      ) : null;

  /* КОГДА ЗАПУСКАТЬ. У инстаграмных типов без слов (ответ на историю, отметка в
     истории) поля нет вовсе: Инстаграм там не даёт отбирать по словам (ЧП 10500163),
     а мёртвая настройка врёт сильнее, чем её отсутствие — в мастере такой шаг не
     заводится. Пояснение режима стоит В САМОЙ строке выбора (KChoice). */
  const whenNode =
    m.channel !== 'pay' && m.channel !== 'broadcast' && igWords ? (
            <>
              <FChoiceBlock
                label={t('chatStartWhen')}
                value={meta.kind}
                onChange={(v) => apply(m, { ...meta, kind: v as ChatStartMeta['kind'] })}
                options={[
                  { value: 'message', label: t('chatStartAny'), hint: t('chatStartAnyHint') },
                  // «Команда /start» и «Переход по ссылке» — ТОЛЬКО Телеграм (владелец
                  // 2026-08-04, IMG_1267); сохранённый чужой kind не прячем, чтобы
                  // список не врал.
                  ...(channel === 'telegram' || meta.kind === 'start'
                    ? [{ value: 'start', label: t('chatStartCommand'), hint: t('chatStartCommandHint') }]
                    : []),
                  // «Определённое сообщение» — совпадение целиком (К8).
                  { value: 'exact', label: t('chatStartExact'), hint: t('chatStartExactHint') },
                  { value: 'keyword', label: t('chatStartKeyword'), hint: t('chatStartWordsHint') },
                  // К37 А4: «Переход по ссылке» — запускает собственная ссылка
                  // t.me/<бот>?start=<код>.
                  ...(channel === 'telegram'
                    ? [{ value: 'link', label: t('chatStartLinkKind'), hint: t('chatStartLinkKindHint') }]
                    : []),
                ]}
              />
              {meta.kind === 'keyword' || meta.kind === 'exact' ? (
                /* Кодовые слова — тот же блок, что в мастере (FField cap + ChipsInput):
                   поле подсвечивается красным, а ТЕКСТ ошибки стоит СВЕРХУ над формой
                   (DError) — заказ владельца 2026-08-06. */
                <FField cap label={t('chatStartWords')} bad={submitTried && meta.keywords.length === 0}>
                  <ChipsInput
                    values={meta.keywords}
                    onChange={setWords}
                    placeholder={t('chatStartWordsPh')}
                    ariaLabel={t('chatStartWords')}
                    removeLabel={t('chatStartWordRemove')}
                    addLabel={t('chatStartWordAdd')}
                    invalid={submitTried && meta.keywords.length === 0}
                  />
                  {/* К18: «Также запускать при переходе по ссылке» — система сама делает
                      из слова латинский хвост и заводит ссылку в реестре. Только Телеграм. */}
                  {channel === 'telegram' && draftId ? (
                    <StartRefLink draftId={draftId} word={meta.keywords[0] ?? ''} />
                  ) : null}
                </FField>
              ) : null}
              {/* «Переход по ссылке» в шторке СОЗДАНИЯ (черновика ещё нет — списка
                  реестра нет, владелец 2026-08-04, IMG_1261): подпись, из неё латинский
                  хвост, снизу — готовая ссылка. Саму ссылку заведёт «Создать». */}
              {meta.kind === 'link' && channel === 'telegram' && !draftId
                ? (() => {
                    const chosenAcc =
                      credRefById(account?.options ?? [], credId) ??
                      (account?.options.length === 1 ? account.options[0] : undefined);
                    const botHandle = chosenAcc?.handle?.replace(/^@/, '') ?? null;
                    return (
                      <StartLinkFields
                        value={linkCode}
                        onChange={setLink}
                        botHandle={botHandle}
                      />
                    );
                  })()
                : null}
              {/* «Переход по ссылке» (К37 А4): вместо поля слов — СПИСОК ССЫЛОК этого
                  сценария. Каждая ссылка ведёт сюда, переходы считаются по каждой
                  отдельно — так один бот разводит людей по разным сценариям. */}
              {meta.kind === 'link' && channel === 'telegram' && draftId ? (
                <StartLinkList
                  draftId={draftId}
                  botHandle={(() => {
                    const chosenAcc =
                      credRefById(account?.options ?? [], credId) ??
                      (account?.options.length === 1 ? account.options[0] : undefined);
                    return chosenAcc?.handle?.replace(/^@/, '') ?? null;
                  })()}
                />
              ) : null}
            </>
    ) : null;

  /* ── РАССЫЛКА: «КОМУ» и «КОГДА» (ТЗ 12) ─────────────────────────────────────────────
     Рассылка — обычный бот с особым «Началом»: отправитель у неё тот же телеграм-бот
     (кусок accountNode выше — он общий), а своего у неё два вопроса.

     «КОМУ». У видов «вручную» и «в дату» — ОДНА строка «Кто получит» (И3,
     audience-screen): за ней экран живых людей со своим счётчиком, поиском, листом
     фильтра и режимом выбора; второй копии отбора не бывает. У вида «по метке»
     аудитории нет вовсе: кому уйдёт, решает сама метка — вместо строки стоит выбор
     одной метки вертикальным листом (тот же список меток кабинета).

     «КОГДА». Здесь же выбирается САМ ВИД начала: рассылка, созданная из раздела, всегда
     рождается «вручную», и без этого выбора «в дату» и «по метке» были бы недостижимы.
     У «в дату» под выбором — календарь с временем в ПОЯСЕ КЛИЕНТА (канон LOCALE:
     DateTimePick, никаких родных date-инпутов); пояс подставляется сам и уезжает на узел
     рядом с моментом. У «вручную» и «по метке» полей нет — только подводка словами. */
  const bcastRule = broadcastModeRule(m.events[0] ?? '');
  const bcastRunAt = runAtToDate(bc.runAt);
  const bcastPast = !!bcastRunAt && bcastRunAt.getTime() <= Date.now();

  const bcastWhoNode =
    m.channel === 'broadcast' ? (
      <>
        {accountNode}
        {bcastRule.needsAudience ? (
          // ОДНА СТРОКА «Кто получит», за ней общий экран живых людей (И3): полей отбора
          // на форме больше нет ни у мастера, ни здесь — экран один на все места.
          <AudienceRow
            label={tAud('whoTitle')}
            value={bc.audience}
            onChange={(v) => applyBcast({ audience: v })}
            emptyWords={tAud('allPeople')}
            // БОТ-ОТПРАВИТЕЛЬ (К27.2 п.5): «кто получит» считается по СВОИМ людям этого
            // бота — карточка человека одна на кабинет, а ботов у клиента бывает несколько.
            {...(credId ? { cred: credId } : {})}
            countKey="start"
          />
        ) : (
          <TagPickRow
            value={bc.tag}
            onChange={(tag) => applyBcast({ tag })}
            label={t('bcastTag')}
            hint={t('bcastTagHint')}
          />
        )}
      </>
    ) : null;

  const bcastWhenNode =
    m.channel === 'broadcast' ? (
      <>
        <FChoiceBlock
          label={t('bcastWhen')}
          value={bcastRule.mode}
          onChange={(v) => applyBcast({}, { ...m, events: [broadcastModeRule(v).mode] })}
          // ВИДЫ, КОТОРЫЕ ЭКРАН ПРЕДЛАГАЕТ (владелец 31.08.2026): «по метке» из выбора
          // убран — рассылка теперь только разовая. Уже настроенную рассылку по метке
          // строка не теряет: свой вид она показывает, иначе выбор молча переехал бы
          // на «вручную» и работающая рассылка сменила бы смысл.
          options={BROADCAST_MODES.filter(
            (r) => BROADCAST_UI_MODES.includes(r) || r.mode === bcastRule.mode,
          ).map((r) => ({
            value: r.mode,
            label: t(r.nameKey),
            hint: t(r.hintKey),
          }))}
        />
        {bcastRule.needsDate ? (
          <FBlock
            label={t('bcastRunAt')}
            {...(bcastPast
              ? { error: t('bcastDatePastErr') }
              : { hint: bc.runAt ? t('bcastRunAtHint') : t('bcastDateErr') })}
          >
            <DateTimePick
              value={bcastRunAt}
              clearable={false}
              onChange={(d) =>
                applyBcast(d ? { runAt: wallClock(d), tz: bc.tz || ownTimeZone() } : { runAt: '' })
              }
            />
          </FBlock>
        ) : null}
        {bcastRule.mode === 'tag' ? <FHint>{t('bcastTagWhenHint')}</FHint> : null}
        {bcastRule.mode === 'manual' ? (
          <>
            <FHint>{t('bcastManualWhenHint')}</FHint>
            {/* КНОПКА СОСТОЯНИЯ ЗАПУСКА (ТЗ 12): «Отправить», а пока рассылка идёт —
                «Остановить». Тот же набор и та же ручка, что на странице рассылки в
                разделе; у ещё не созданного черновика (шторка создания) отправлять
                нечего — кнопки там нет. */}
            {draftId ? (
              <>
                <BroadcastActs
                  mode={bcastRule.mode}
                  status={launchCtl.state?.status ?? 'draft'}
                  launch={launchCtl}
                />
                {/* Запуск рассылки не прошёл — это ошибка ВСЕГО окна, не поля
                    (канон: UI-TEMPLATE-CANON «Ошибка всего окна», П128). Строка стоит
                    при кнопке, которая её вызвала, и гаснет со следующей попыткой. */}
                {launchCtl.err ? <FFormError>{launchCtl.err}</FFormError> : null}
              </>
            ) : null}
          </>
        ) : null}
      </>
    ) : null;

  // ── МАСТЕР: ХИРО + ШАГИ (CABINET-UX-FIXLIST п.6, владелец 2026-08-20 по IMG_2261) ──
  //
  // Простыня «Событие в Инстаграме» новичку не читалась: он видел десяток настроек сразу
  // и не понимал, что от него хотят. Теперь настройка запуска идёт ТЕМ ЖЕ путём, что
  // мастер быстрой настройки, и ТОЙ ЖЕ оболочкой (wizard-shell): первый экран — крупный
  // знак сети и одно предложение, дальше по одному вопросу с подводкой и планка
  // «Назад / Шаг N из M / Дальше».
  //
  // Нарезка повторяет мастера запуска канала (lib/template-wizards/trigger-ig.ts,
  // trigger-tg.ts — они правят ТОТ ЖЕ узел через readChatStart/writeChatStart): у них
  // шаги «Аккаунт» → «Что запускает» → «Какое сообщение». Четвёртый шаг, «Правила
  // запуска», есть только здесь: тумблеры удержания и антиповтора мастера канала не
  // трогают. Тексты вопросов, вариантов и подсказок — ИЗ ТОГО ЖЕ СЛОВАРЯ (chatStart* в
  // messages/*.json), который мастера канала цитируют дословно; второго словаря нет.
  //
  // Мастер живёт ТОЛЬКО у диалоговых каналов (Телеграм и Инстаграм) — они и есть весь
  // заказ. Запуск «по оплате» не трогаем (там своя короткая форма), у формы и чата на
  // сайте условий запуска почти нет — эти печатаются простынёй, как раньше.
  //
  // КАПКАН ДЛЯ СЛЕДУЮЩЕГО (владелец 2026-08-20). Рядом по файлу живёт правило «вводного
  // описания под заголовком НЕТ» (владелец 2026-08-11: «под заголовком модалки ничего не
  // пишем»). Оно про АБЗАЦ ПОД ЗАГОЛОВКОМ ОКНА и остаётся в силе: подзаголовка у шапки
  // как не было, так и нет. Хиро мастера — ДРУГОЕ и им НЕ отменяется: это подводка ШАГА
  // внутри тела, крупный знак сети и предложение по центру, заказанные тем же владельцем
  // (10.08 для мастера шаблона, 20.08 — сюда). Выпиливать его «по правилу 11.08» нельзя.
  //
  // РАССЫЛКА (ТЗ 12) идёт ТЕМ ЖЕ мастером, но своими двумя шагами: «Кому» (бот-отправитель
  // + аудитория или метка-повод) и «Когда» (вид начала и, у «в дату», сам момент). Знак
  // сети на первом экране — телеграмный: рассылку везёт телеграм-бот клиента.
  const wizOn = (dialogChannel && m.channel !== 'pay') || m.channel === 'broadcast';
  const heroChannel = m.channel === 'instagram' ? 'instagram' : 'telegram';
  const wizSteps: Array<{ key: string; title: string; intro: string; node: ReactNode }> = !wizOn
    ? []
    : m.channel === 'broadcast'
      ? [
          {
            key: 'bcastWho',
            title: t('bcastWho'),
            intro: t(bcastRule.needsAudience ? 'bcastWizWhoIntro' : 'bcastWizWhoTagIntro'),
            node: bcastWhoNode,
          },
          {
            key: 'bcastWhen',
            title: t('bcastWhen'),
            intro: t(
              bcastRule.mode === 'date'
                ? 'bcastWizWhenDateIntro'
                : bcastRule.mode === 'tag'
                  ? 'bcastWizWhenTagIntro'
                  : 'bcastWizWhenManualIntro',
            ),
            node: bcastWhenNode,
          },
        ]
      : [
        {
          key: 'account',
          // Название шага — существующая подпись поля («Бот» / «Аккаунт Instagram»).
          title: accLabel || t('chatStartAccount'),
          intro: t(m.channel === 'instagram' ? 'chatStartWizIntroIg' : 'chatStartWizIntroTg'),
          node: (
            <>
              {tilesNode}
              {accountNode}
            </>
          ),
        },
        // Шаг «Что запускает» — только у Инстаграма: у Телеграма событие одно, и пустой
        // экран «Шаг 2 из 4» был бы враньём (тот же фильтр, что у мастера канала).
        ...(m.channel === 'instagram'
          ? [
              {
                key: 'entry',
                title: t('chatStartEvent'),
                intro: t('chatStartWizEntryIntro'),
                node: entryNode,
              },
            ]
          : []),
        ...(whenNode
          ? [
              {
                key: 'when',
                title: t('chatStartWhen'),
                intro: t('chatStartWizWhenIntro'),
                node: whenNode,
              },
            ]
          : []),
        {
          key: 'rules',
          title: t('chatStartRules'),
          intro: t('chatStartWizRulesIntro'),
          node: rulesNode,
        },
      ];
  // Шагов могло стать меньше прямо под пальцем (сменили событие Инстаграма на «Ответ на
  // историю» — шаг про слова исчез): держим указатель в границах.
  const wizAt = Math.min(wizIdx, Math.max(0, wizSteps.length - 1));
  const wizStep = wizSteps[wizAt];
  const wizLast = wizAt >= wizSteps.length - 1;
  /** Почему с этого шага нельзя уйти дальше. Только то, что человек правит сам
   *  (CLAUDE.md запрет №7). Оба правила — из мастера запуска канала (trigger-ig
   *  stepBlocked) и из «Старта» (слова): вторых проверок не заводим. */
  const wizBlocked = (key: string): string | null => {
    // РАССЫЛКА: причина одна на оба шага (bcastIssue), но встаёт она на СВОЁМ экране —
    // бот и метка спрашиваются на «Кому», момент отправки на «Когда».
    if (key === 'bcastWho' || key === 'bcastWhen') {
      const why = bcastIssue(m, credId, bc);
      if (why === null) return null;
      const onWhen = why === t('bcastDateErr') || why === t('bcastDatePastErr');
      return (key === 'bcastWhen') === onWhen ? why : null;
    }
    if (
      key === 'entry' &&
      igHasComment &&
      postsMode === 'some' &&
      meta.mediaIds.length === 0
    ) {
      return t('chatStartPostsErr');
    }
    if (key === 'when' && wordsMissing(meta)) return t('chatStartWordsErr');
    return null;
  };
  /** Красная тряска шага (владелец 2026-08-06: слабой подписи мало). Саму причину
   *  рисует строка в теле шага — она считается живо из настроек (wizBlocked), поэтому
   *  здесь остаётся только отметка «попытка была». */
  const wizFail = () => {
    setSubmitTried(true);
    setWizTried(true);
    setWizShake((n) => n + 1);
    if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
  };
  const wizNext = () => {
    const why = wizStep ? wizBlocked(wizStep.key) : null;
    if (why !== null) {
      wizFail();
      return;
    }
    setWizTried(false);
    if (!wizLast) {
      setWizIdx(wizAt + 1);
      return;
    }
    // ФИНАЛ. Недозаполненный шаг мог остаться ПОЗАДИ (слова запуска на втором экране, а
    // человек уже на «Правилах») — тогда галочка не «молча не работает», а ВОЗВРАЩАЕТ на
    // виноватый шаг и называет причину. Валидацию «без слов не сохранить» так и не теряем.
    const bad = wizSteps.findIndex((s) => wizBlocked(s.key) !== null);
    if (bad !== -1) {
      setWizIdx(bad);
      wizFail();
      return;
    }
    onFinish?.();
  };

  // Причина отказа ТЕКУЩЕГО шага — считается живо: устранили → строка гаснет сама.
  const wizWhy = wizTried && wizStep ? wizBlocked(wizStep.key) : null;

  if (wizOn && wizStep) {
    return (
      <>
        {/* Д5: шапке — знать, финальный ли шаг (галочка только на последнем). Зонд —
            ДОЧЕРНИМ компонентом: у StartEditor выше есть ранние return, хук здесь
            ломал бы порядок хуков (ловил линт-гейт прод-сборки). */}
        {onWizStage ? <WizStageProbe last={wizLast} onStage={onWizStage} /> : null}
        {/* Маркер для ВЕБ-шторки: пока шаг не последний, CSS прячет кнопку применения
            в её шапке (в приложении то же делает stack.update у открывателя). */}
        {!wizLast ? <span className="wiz-apply-off" hidden /> : null}
        <WizHero
          // Знак сети — только на ПЕРВОМ экране; дальше просто предложение-подводка.
          {...(wizAt === 0 ? { channel: heroChannel } : {})}
          text={wizStep.intro}
        />
        {/* ОШИБКА ВСЕГО ШАГА — ОДНА строка первой строкой тела, сразу под вступлением
            (канон: UI-TEMPLATE-CANON «Ошибка всего окна» (П128)). Причина шага главнее
            общей строки запуска — двух красных строк разом в окне не бывает. */}
        {wizWhy !== null ? <FFormError>{wizWhy}</FFormError> : errStrip}
        {/* key со счётчиком тряски: неудачная попытка перемонтирует шаг, и подсветённые
            поля (.wiz-bad) проигрывают анимацию заново — как в мастере шаблона. */}
        <div key={`${wizStep.key}-${wizShake}`}>{wizStep.node}</div>
        <WizFoot
          backLabel={ta('wizBack')}
          nextLabel={wizLast ? (finishLabel ?? ta('wizSave')) : ta('wizNext')}
          onBack={
            wizAt > 0
              ? () => {
                  setWizTried(false);
                  setWizIdx(wizAt - 1);
                }
              : undefined
          }
          onNext={wizNext}
        />
      </>
    );
  }

  // Каналы без мастера (запуск «по оплате», форма и чат на сайте) — простынёй, как было:
  // порядок кусков тот же, что стоял до мастера.
  return (
    <>
      {errStrip}
      {tilesNode}
      {payNode}
      {accountNode}
      {rulesNode}
      {entryNode}
      {whenNode}
    </>
  );
}

// ── «Задержка»: сколько подождать перед следующим шагом ─────────────────────────────

const DELAY_UNITS: ChatDelayUnit[] = ['seconds', 'minutes', 'hours', 'days'];

function DelayEditor({ node, push, onRemove }: { node: WorkflowNode; push: Push; onRemove?: RemoveRow }) {
  const t = useTranslations('cabinet.scenario');
  const initial = useMemo(() => readChatDelay(node), [node]);
  const [m, setM] = useState<ChatDelayModel | null>(initial);
  const [openedAt] = useState(() => Date.now());
  if (!initial || !m) return null;

  const apply = (patch: Partial<ChatDelayModel>) => {
    const next = { ...m, ...patch };
    setM(next);
    push(writeChatDelay(node, next));
  };
  const unitLabel: Record<ChatDelayUnit, string> = {
    seconds: t('unitSeconds'),
    minutes: t('unitMinutes'),
    hours: t('unitHours'),
    days: t('unitDays'),
  };

  // «Ждать до» узел держит моментом с московским смещением — календарю нужен Date.
  const untilValue = untilToDate(m.until);
  // Часы читаем ОДИН раз при открытии шторки (в рендере их звать нельзя): для подсказки
  // «дата уже прошла» этой точности с избытком.
  const untilPast = untilValue !== null && untilValue.getTime() < openedAt;

  return (
    /* Ряд «Подождать»: шапка со значком песочных часов, под ней выбор «Длительность или
       дата» и поля выбранного способа. Вводного описания под заголовком шторки НЕТ
       (владелец 2026-08-11): что делает шаг, говорит подсказка под первым пузырём. */
    <div className="scn-erow">
      <RowHead
        icon={m.mode === 'until' ? 'date' : 'delay'}
        label={t('rowDelay')}
        {...removeProps(onRemove, t)}
      />
      {/* Способов два — раскрытым списком с галочкой, а не вкладками (канон формы:
          выбор до шести вариантов стоит строками). Имя пузыря — «Как задать», не
          «Подождать»: слово уже стоит в шапке ряда, дубль под дублем читался неряшливо. */}
      <FChoiceBlock
        label={t('chatDelayMode')}
        hint={t('chatDelayHint')}
        value={m.mode}
        onChange={(v: string) => apply({ mode: v as ChatDelayMode })}
        options={[
          { value: 'interval', label: t('chatDelayTabInterval') },
          { value: 'until', label: t('chatDelayTabDate') },
        ]}
      />
      {m.mode === 'until' ? (
        /* Дата в прошлом = задержки не будет вовсе: шаг пропустит ожидание и пойдёт
           дальше сразу. Говорим об этом прямо (приёмка К7). Одно поле, имя которого
           совпадает с именем пузыря, — без FIn (канон). */
        <FBlock
          label={t('chatDelayUntil')}
          {...(untilPast
            ? { error: t('chatDelayUntilPast') }
            : { hint: !untilValue ? t('chatDelayUntilEmpty') : t('chatDelayUntilHint') })}
        >
          <DateTimePick
            value={untilValue}
            clearable={false}
            onChange={(d) => apply({ until: d ? chatUntilValue(wallClock(d)) : '' })}
          />
        </FBlock>
      ) : (
        /* Число и единица — одно значение («5 минут»), поэтому одной строкой и без FIn. */
        <FBlock label={t('chatDelayTabInterval')}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="number"
              min={1}
              value={m.amount}
              onChange={(e) => apply({ amount: Number(e.target.value) })}
              aria-label={t('chatDelayHow')}
              style={{ maxWidth: 110 }}
            />
            <div style={{ flex: 1 }}>
              <SmartSelect
                className="scn-sched-sel"
                value={m.unit}
                ariaLabel={t('chatDelayUnit')}
                onChange={(v: string) => apply({ unit: v as ChatDelayUnit })}
                options={DELAY_UNITS.map((u) => ({ value: u, label: unitLabel[u] }))}
              />
            </div>
          </div>
        </FBlock>
      )}
    </div>
  );
}

// ── «Интеграция с API»: запрос к другому сервису (К6 + «Свой сервис», 2026-08-03) ────
// Первое поле — «Сервис»: «Разовый запрос» (прежний свободный режим) или один из СВОИХ
// сервисов клиента (подключения generic-типов с метой в сейфе, custom-service.ts).
// Выбран свой сервис → показываются ТОЛЬКО его входные поля по человеческим именам,
// адресов и JSON человек не видит; на выход шаг отдаёт поля ответа по именам (довесок
// «Разбор ответа» — companion в ChatStepState, вставляет commit в use-draft-editor).

/** Способы обращения (те же, что понимает маппинг chat-step.ts). */
const HTTP_METHODS: ChatHttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/** Пустая модель разового запроса (переключение из сервисного режима). */
const emptyHttpModel = (): ChatHttpModel => ({
  method: 'GET',
  url: chatValueOf(),
  headers: [],
  body: chatValueOf(),
});

/** Ключ «разового» пункта селекта «Сервис» — с настоящим credId не совпадёт никогда. */
const ONCE_KEY = '{once}';

function HttpEditor({
  node,
  services,
  companion,
  onHttp,
  onRemove,
}: {
  node: WorkflowNode;
  /** «Свои сервисы» клиента; null — список ещё не приехал. */
  services: CustomServiceMeta[] | null;
  /** Шаг-довесок «Разбор ответа» этого запроса (если уже стоит в каноне). */
  companion: WorkflowNode | null;
  /** Правки шага: параметры + (опц.) доступ сервиса и довесок «Разбор ответа». */
  onHttp: (
    params: Record<string, unknown>,
    extra?: { credentialId?: string | null; companion?: Record<string, unknown> | null },
  ) => void;
  onRemove?: RemoveRow;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const svcCredId = useMemo(() => serviceHttpCredId(node), [node]);
  const [chosen, setChosen] = useState<string>(svcCredId ?? ONCE_KEY);
  // Сервис, созданный «Сохранить как сервис» прямо отсюда: списка services (слепок на
  // открытие панели) он ещё не достиг — держим рядом.
  const [localMeta, setLocalMeta] = useState<CustomServiceMeta | null>(null);
  const meta =
    chosen === ONCE_KEY
      ? null
      : ((services ?? []).find((s) => s.credId === chosen) ?? (localMeta?.credId === chosen ? localMeta : null));

  // Разовый режим — прежняя модель знакомой формы.
  const initial = useMemo(() => readChatHttp(node, lang), [node, lang]);
  const [m, setM] = useState<ChatHttpModel>(initial ?? emptyHttpModel());
  // Сервисный режим — значения входов по ключам меты.
  const [vals, setVals] = useState<Record<string, ChatValue>>(() =>
    meta ? (readServiceValues(node, meta, lang) ?? {}) : {},
  );
  // «Поля ответа» разового режима — из уже стоящего довеска «Разбор ответа».
  const [outRows, setOutRows] = useState<CustomServiceOutput[]>(() =>
    companion ? (readResponseFields(companion) ?? []) : [],
  );
  // «Сохранить как сервис» (по образцу «Создать товар» шага «Оплата»): имя + кнопка.
  const [saveOpen, setSaveOpen] = useState(false);
  const [svcName, setSvcName] = useState('');
  const [svcBusy, setSvcBusy] = useState(false);
  const [svcErr, setSvcErr] = useState<string | null>(null);

  // Параметры разового запроса: у сервисного узла знакомой формы нет (readChatHttp null,
  // авторизация кредом) — при переключении собираем с чистого листа.
  const onceParams = (model: ChatHttpModel): Record<string, unknown> => {
    if (readChatHttp(node)) return writeChatHttp(node, model);
    return writeChatHttp(
      { type: 'n8n-nodes-base.httpRequest', parameters: { method: 'GET', url: '', options: {} } },
      model,
    );
  };

  const pushService = (target: CustomServiceMeta, values: Record<string, ChatValue>) => {
    const raw: Record<string, string> = {};
    for (const input of target.inputs) raw[input.key] = writeChatValue(values[input.key] ?? chatValueOf());
    onHttp(serviceHttpParams(target, raw), {
      credentialId: target.credId,
      companion: responseFieldsParams(target.outputs),
    });
  };

  const choose = (v: string) => {
    if (v === chosen) return;
    if (v === ONCE_KEY) {
      // Из сервиса в разовый: адрес и способ переносим из меты — контекст не теряется.
      const base: ChatHttpModel = meta
        ? { ...emptyHttpModel(), method: meta.method, url: readChatValue(meta.url, lang) }
        : m;
      setM(base);
      setChosen(ONCE_KEY);
      onHttp(onceParams(base), { companion: responseFieldsParams(outRows) });
      return;
    }
    const target = (services ?? []).find((s) => s.credId === v) ?? (localMeta?.credId === v ? localMeta : null);
    if (!target) return;
    // Значения входов нового сервиса — с чистого листа (у прежнего сервиса свои ключи);
    // тот же сервис — прежние значения из узла.
    const values = v === svcCredId ? (readServiceValues(node, target, lang) ?? {}) : {};
    setVals(values);
    setChosen(v);
    pushService(target, values);
  };

  const options: SmartOption[] = [
    { value: ONCE_KEY, label: t('chatHttpOnce') },
    // Сервис, привязанный к шагу, но пропавший из списка (отключён) — показываем как
    // есть, чтобы выбор не «слетал» молча (правило то же, что у товара в «Оплате»).
    ...(chosen !== ONCE_KEY && !meta ? [{ value: chosen, label: t('chatHttpSvcGone') }] : []),
    // Сервисы клиента — с глифом «Своего сервиса» (тот же штекер, что на плитках
    // «Подключений»), а не буквой: значок сервиса один по всему кабинету.
    ...(services ?? []).map((s) => ({ value: s.credId, label: s.name, icon: CUSTOM_SERVICE_DEF.icon })),
    ...(localMeta && !(services ?? []).some((s) => s.credId === localMeta.credId)
      ? [{ value: localMeta.credId, label: localMeta.name, icon: CUSTOM_SERVICE_DEF.icon }]
      : []),
  ];

  // Сервисов у клиента бывает сколько угодно (больше шести) — здесь SmartSelect в пузыре,
  // а не раскрытый список (канон формы). Вводного описания под заголовком шторки НЕТ
  // (владелец 2026-08-11): что делает шаг в выбранном режиме, говорит подсказка ПОД этим
  // пузырём — она же сообщает про отключённый сервис и незагруженный список.
  const serviceHint =
    chosen === ONCE_KEY
      ? t('chatHttpHint')
      : meta
        ? t('chatHttpSvcHint')
        : services === null
          ? t('chatHttpSvcLoading')
          : t('chatHttpSvcGoneHint');
  const serviceField = (
    <FBlock label={t('chatHttpService')} hint={serviceHint}>
      <SmartSelect
        className="scn-sched-sel"
        value={chosen}
        ariaLabel={t('chatHttpService')}
        onChange={choose}
        options={options}
      />
    </FBlock>
  );

  // ── Сервисный режим: только входные поля по именам ─────────────────────────────────
  if (chosen !== ONCE_KEY) {
    if (!meta) {
      return (
        <div className="scn-erow">
          <RowHead icon="api" label={t('chatActHttp')} {...removeProps(onRemove, t)} />
          {serviceField}
        </div>
      );
    }
    const applyVal = (key: string, text: string) => {
      const next = { ...vals, [key]: { ...(vals[key] ?? chatValueOf()), text } };
      setVals(next);
      pushService(meta, next);
    };
    return (
      <div className="scn-erow">
        <RowHead icon="api" label={t('chatActHttp')} {...removeProps(onRemove, t)} />
        {serviceField}
        {meta.inputs.length === 0 ? (
          <FHint>{t('chatHttpSvcNoInputs')}</FHint>
        ) : (
          /* Входы сервиса — ОДНИМ пузырём с именем сервиса, у каждого входа свой
             заголовок (канон: заголовок у пузыря и у каждого поля). */
          <FBlock label={meta.name}>
            {meta.inputs.map((input) => (
              <FIn key={input.key} label={input.label}>
                <input
                  value={(vals[input.key] ?? chatValueOf()).text}
                  aria-label={input.label}
                  onChange={(e) => applyVal(input.key, e.target.value)}
                />
                <ValueHint value={vals[input.key] ?? chatValueOf()} />
              </FIn>
            ))}
          </FBlock>
        )}
        {meta.outputs.length > 0 && (
          <FHint>{t('chatHttpSvcOutputs', { list: meta.outputs.map((o) => o.label).join(', ') })}</FHint>
        )}
      </div>
    );
  }

  // ── Разовый режим: прежняя знакомая форма + поля ответа + «Сохранить как сервис» ───
  const apply = (patch: Partial<ChatHttpModel>) => {
    const next = { ...m, ...patch };
    setM(next);
    onHttp(onceParams(next));
  };
  const setVal = (key: 'url' | 'body', text: string) => apply({ [key]: { ...m[key], text } as ChatValue });
  const patchHeader = (i: number, patch: Partial<ChatHttpHeader>) =>
    apply({ headers: m.headers.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) });
  // «Поля ответа» разового запроса: имя + путь → довесок «Разбор ответа» (companion).
  const applyOut = (rows: CustomServiceOutput[]) => {
    setOutRows(rows);
    onHttp(onceParams(m), { companion: responseFieldsParams(rows) });
  };
  // Тело показываем там, где оно бывает; уже написанное не прячем никогда — иначе правка
  // способа молча увезла бы его из виду (а из канона оно бы не делось).
  const bodyShown = m.method !== 'GET' || m.body.text.trim() !== '';
  // Тело не разбирается как JSON — красная подпись; в канон такое не уходит (writeChatHttp
  // оставляет прежнее целое значение). Подстановки «{…}» ошибкой не считаются.
  const bodyErr = chatHttpBodyError(m.body);

  // «Сохранить как сервис»: введённое (адрес, способ, авторизация из заголовка
  // Authorization, входы из тела и заголовков, поля ответа) уезжает в «Подключения»,
  // шаг сразу переключается на созданный сервис.
  const saveAsService = async () => {
    setSvcErr(null);
    const urlRaw = writeChatValue(m.url).trim();
    if (urlRaw.startsWith('=') || !/^https?:\/\/\S+$/i.test(urlRaw)) {
      setSvcErr(t('chatHttpSaveUrlErr'));
      return;
    }
    if (!CUSTOM_SERVICE_METHODS.includes(m.method as (typeof CUSTOM_SERVICE_METHODS)[number])) {
      setSvcErr(t('chatHttpSaveMethodErr'));
      return;
    }
    const bodyRaw = writeChatValue(m.body).trim();
    const entries = bodyRaw === '' ? {} : bodyTopEntries(bodyRaw);
    if (entries === null) {
      setSvcErr(t('chatHttpSaveBodyErr'));
      return;
    }
    const authHeader = m.headers.find((h) => h.name.trim().toLowerCase() === 'authorization') ?? null;
    const authValue = authHeader ? writeChatValue(authHeader.value) : '';
    if (authHeader && (authValue.startsWith('=') || authValue.trim() === '')) {
      setSvcErr(t('chatHttpSaveAuthErr'));
      return;
    }
    const restHeaders = m.headers.filter((h) => h !== authHeader && h.name.trim() !== '');
    const inputs: CustomServiceMeta['inputs'] = [
      ...Object.keys(entries).map((k) => ({ label: k, place: 'body' as const, key: k })),
      ...restHeaders.map((h) => ({ label: h.name.trim(), place: 'header' as const, key: h.name.trim() })),
    ];
    const outputs = outRows.filter((r) => r.label.trim() !== '' && r.path.trim() !== '');
    const auth = authHeader ? 'header' : 'none';
    const name = svcName.trim() || t('chatHttpSvcNamePh');
    setSvcBusy(true);
    const r = await fetch('/api/cabinet/accounts', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        name,
        type: customServiceCredType(auth),
        data: {
          serviceKind: 'custom',
          serviceUrl: urlRaw,
          serviceMethod: m.method,
          serviceAuth: auth,
          serviceParamName: authHeader ? authHeader.name.trim() : '',
          serviceInputs: JSON.stringify(inputs),
          serviceOutputs: JSON.stringify(outputs),
          ...(authHeader ? { apiKey: authValue } : {}),
        },
      }),
    })
      .then((res) => res.json())
      .catch(() => ({ ok: false }));
    setSvcBusy(false);
    if (!r.ok || !r.id) {
      setSvcErr(t('chatHttpSaveErr'));
      return;
    }
    refreshCustomServices();
    const created: CustomServiceMeta = {
      credId: String(r.id),
      credType: customServiceCredType(auth),
      name,
      url: urlRaw,
      method: m.method as CustomServiceMeta['method'],
      auth,
      inputs,
      outputs,
    };
    // Введённые значения переезжают во входы созданного сервиса — шаг продолжает
    // делать РОВНО тот же запрос, только теперь под именем сервиса.
    const values: Record<string, ChatValue> = {};
    for (const input of inputs) {
      const raw =
        input.place === 'body'
          ? (entries[input.key] ?? '')
          : writeChatValue(restHeaders.find((h) => h.name.trim() === input.key)?.value ?? chatValueOf());
      const body = raw.startsWith('=') ? raw : raw.includes('{{') ? `=${raw}` : raw;
      values[input.key] = readChatValue(body, lang);
    }
    setLocalMeta(created);
    setVals(values);
    setSaveOpen(false);
    setChosen(created.credId);
    pushService(created, values);
  };

  return (
    <div className="scn-erow">
      <RowHead icon="api" label={t('chatActHttp')} {...removeProps(onRemove, t)} />
      {serviceField}
      {/* Способов пять — раскрытым списком с галочкой, а не выпадашкой (канон формы:
          выбор до шести вариантов стоит строками). */}
      <FChoiceBlock
        label={t('chatHttpMethod')}
        value={m.method}
        onChange={(v: string) => apply({ method: v as ChatHttpMethod })}
        options={HTTP_METHODS.map((k) => ({ value: k, label: `${k} · ${t(`chatHttpM_${k}`)}` }))}
      />
      {/* Одно поле, имя которого совпадает с именем пузыря — без FIn (канон).
          Без адреса запрос отправлять некуда — красным, пока не вписан (Ф9). */}
      <FBlock
        label={t('chatHttpUrl')}
        {...(m.url.text.trim() === '' ? { error: t('chatHttpUrlErr') } : { hint: t('chatHttpUrlHint') })}
      >
        <input
          value={m.url.text}
          placeholder={t('chatHttpUrlPh')}
          aria-label={t('chatHttpUrl')}
          onChange={(e) => setVal('url', e.target.value)}
        />
        <ValueHint value={m.url} />
      </FBlock>

      {/* Заголовки — ОДНИМ пузырём на все строки (образец — «Кнопки» сообщения и строки
          заявки CRM): пузыря в пузыре не бывает, у каждого поля строки свой заголовок,
          конец строки отбивает её «Убрать». */}
      <FBlock label={t('chatHttpHeaders')} hint={t('chatHttpHeadersHint')}>
        {m.headers.map((h, i) => (
          <Fragment key={i}>
            <FIn label={t('chatHttpHeaderName')}>
              <input
                value={h.name}
                placeholder={t('chatHttpHeaderNamePh')}
                aria-label={t('chatHttpHeaderName')}
                onChange={(e) => patchHeader(i, { name: e.target.value })}
              />
            </FIn>
            <FIn label={t('chatHttpHeaderValue')}>
              <input
                value={h.value.text}
                placeholder={t('chatHttpHeaderValuePh')}
                aria-label={t('chatHttpHeaderValue')}
                onChange={(e) => patchHeader(i, { value: { ...h.value, text: e.target.value } })}
              />
              <ValueHint value={h.value} />
            </FIn>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => apply({ headers: m.headers.filter((_, idx) => idx !== i) })}
              >
                {t('chatHttpHeaderRemove')}
              </button>
            </div>
          </Fragment>
        ))}
        <AddDashed
          label={t('chatHttpHeaderAdd')}
          onClick={() => apply({ headers: [...m.headers, { name: '', value: chatValueOf() }] })}
        />
      </FBlock>

      {bodyShown ? (
        /* Дыра приёмки К6, правило К5: недописанное тело в сценарий НЕ уходит — оно
           остаётся на экране красным, а в шаге живёт прежнее целое значение. Молча
           уехавший «{сломано,» падал бы только на прогоне, чужими словами. */
        <FBlock
          label={t('chatHttpBody')}
          {...(bodyErr ? { error: t('chatHttpBodyErr') } : { hint: t('chatHttpBodyHint') })}
        >
          <textarea
            rows={5}
            value={m.body.text}
            placeholder={t('chatHttpBodyPh')}
            aria-label={t('chatHttpBody')}
            aria-invalid={bodyErr || undefined}
            onChange={(e) => setVal('body', e.target.value)}
          />
          <ValueHint value={m.body} />
        </FBlock>
      ) : null}

      {/* «Поля ответа» (владелец 2026-08-03): имя + путь в JSON ответа — и разовый
          запрос раскладывает ответ по человеческим именам (довесок «Разбор ответа»,
          спрятанный в эту же карточку). Следующие шаги видят поля по именам. */}
      <FBlock label={t('chatHttpOutTitle')} hint={t('chatHttpOutHint')}>
        {outRows.map((row, i) => (
          <Fragment key={i}>
            <FIn label={t('chatHttpOutName')}>
              <input
                value={row.label}
                placeholder={t('chatHttpOutNamePh')}
                aria-label={t('chatHttpOutName')}
                onChange={(e) => applyOut(outRows.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))}
              />
            </FIn>
            <FIn label={t('chatHttpOutPath')}>
              <input
                value={row.path}
                placeholder="data.result.price"
                aria-label={t('chatHttpOutPath')}
                onChange={(e) => applyOut(outRows.map((x, idx) => (idx === i ? { ...x, path: e.target.value } : x)))}
              />
            </FIn>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => applyOut(outRows.filter((_, idx) => idx !== i))}
              >
                {t('chatHttpHeaderRemove')}
              </button>
            </div>
          </Fragment>
        ))}
        <AddDashed
          label={t('chatHttpOutAdd')}
          onClick={() => applyOut([...outRows, { label: '', path: '' }])}
        />
      </FBlock>

      {/* «Сохранить как сервис» — по образцу «Создать товар» шага «Оплата»: минимум —
          имя + кнопка на месте; после сохранения шаг сразу говорит с сервисом. */}
      {saveOpen ? (
        <FBlock
          label={t('chatHttpSaveAsSvc')}
          hint={t('chatHttpSaveHint')}
          {...(svcErr ? { error: svcErr } : {})}
        >
          <FIn label={t('chatHttpSvcNamePh')}>
            <input
              value={svcName}
              maxLength={80}
              placeholder={t('chatHttpSvcNamePh')}
              aria-label={t('chatHttpSvcNamePh')}
              onChange={(e) => setSvcName(e.target.value)}
            />
          </FIn>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn sm primary" disabled={svcBusy} onClick={() => void saveAsService()}>
              {svcBusy ? t('chatHttpSaveBusy') : t('chatHttpSaveDo')}
            </button>
            <button type="button" className="btn sm" onClick={() => setSaveOpen(false)}>
              {t('chatHttpSaveCancel')}
            </button>
          </div>
        </FBlock>
      ) : (
        <AddDashed label={t('chatHttpSaveAsSvc')} onClick={() => setSaveOpen(true)} />
      )}
    </div>
  );
}

// ── «Запрос к ИИ»: вопрос настоящей модели через аккаунт Scriptera (К6) ──────────────

function AiEditor({
  node,
  modelNode,
  onOpenNode,
  push,
  onRemove,
}: {
  node: WorkflowNode;
  /** Шаг модели в гнезде помощника (там выбирают саму модель); null — его нет. */
  modelNode?: WorkflowNode | null;
  onOpenNode?: (nodeId: string) => void;
  push: Push;
  onRemove?: RemoveRow;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatAi(node, lang), [node, lang]);
  const [m, setM] = useState<ChatAiModel | null>(initial);
  if (!initial || !m) return null;

  const apply = (patch: Partial<ChatAiModel>) => {
    const next = { ...m, ...patch };
    setM(next);
    push(writeChatAi(node, next));
  };
  const setVal = (key: 'question' | 'role', text: string) => apply({ [key]: { ...m[key], text } as ChatValue });
  // Доктрина SCRIPTERA-AI-DOCTRINE: показываем НАСТОЯЩЕЕ имя модели, а не «Scriptera AI».
  const modelName = readAiModelName(modelNode);

  return (
    <div className="scn-erow">
      <RowHead icon="ai" label={t('chatActAskAi')} {...removeProps(onRemove, t)} />
      {/* Вводного описания под заголовком шторки НЕТ (владелец 2026-08-11): что делает
          шаг и что ответы тратят токены — подсказкой под первым пузырём. Одно поле,
          имя которого совпадает с именем пузыря, — без FIn (канон).
          Пустой вопрос — модель получит пустоту и ответит невпопад (Ф9). */}
      <FBlock
        label={t('chatAiQuestion')}
        hint={
          <>
            {t('chatAiHint')} {t('chatAiQuestionHint')}
          </>
        }
        {...(m.question.text.trim() === '' ? { error: t('chatAiQuestionErr') } : {})}
      >
        <textarea
          rows={4}
          value={m.question.text}
          placeholder={t('chatAiQuestionPh')}
          aria-label={t('chatAiQuestion')}
          onChange={(e) => setVal('question', e.target.value)}
        />
        <ValueHint value={m.question} />
      </FBlock>
      <FBlock label={t('chatAiRole')} hint={t('chatAiRoleHint')}>
        <textarea
          rows={3}
          value={m.role.text}
          placeholder={t('chatAiRolePh')}
          aria-label={t('chatAiRole')}
          onChange={(e) => setVal('role', e.target.value)}
        />
        <ValueHint value={m.role} />
      </FBlock>
      {/* Какая модель отвечает — видно прямо тут, менять её в её же шаге (там живой
          список моделей аккаунта с ценами). Шага модели нет — говорим об этом прямо:
          без него помощник не ответит. Поля здесь нет, поэтому строкой, не пузырём. */}
      {modelName ? (
        <FHint>
          {t('chatAiModelIs', { model: modelName })}
          {onOpenNode && modelNode ? (
            <>
              {' '}
              <button type="button" className="btn sm" onClick={() => onOpenNode(modelNode.id)}>
                {t('chatAiModelOpen')}
              </button>
            </>
          ) : null}
        </FHint>
      ) : (
        <FError>{t('chatAiNoModel')}</FError>
      )}
    </div>
  );
}

// ── «Действие в чате»: «печатает…» (К6) ─────────────────────────────────────────────

function ChatActionEditor({ node, push, onRemove }: { node: WorkflowNode; push: Push; onRemove?: RemoveRow }) {
  const t = useTranslations('cabinet.scenario');
  const initial = useMemo(() => readChatAction(node), [node]);
  const [action, setAction] = useState(initial?.action ?? 'typing');
  if (!initial) return null;

  const known = (CHAT_TG_ACTIONS as readonly string[]).includes(action);
  const options = [
    // Значение из чужой схемы показываем как есть — молча подменять выбор нельзя.
    ...(known ? [] : [{ value: action, label: action }]),
    ...CHAT_TG_ACTIONS.map((a) => ({ value: a, label: t(`chatTgAct_${a}`) })),
  ];
  return (
    <div className="scn-erow">
      <RowHead icon="typing" label={t('chatActTyping')} {...removeProps(onRemove, t)} />
      {/* Вводного описания под заголовком шторки НЕТ (владелец 2026-08-11): что показывает
          бот и сколько это держится — подсказкой под пузырём. Действий десять (больше
          шести) — SmartSelect в пузыре, а не раскрытый список (канон формы); одно поле,
          имя которого совпадает с именем пузыря, — без FIn. */}
      <FBlock label={t('chatActionWhat')} hint={t('chatActionHint')}>
        <SmartSelect
          className="scn-sched-sel"
          value={action}
          ariaLabel={t('chatActionWhat')}
          onChange={(v: string) => {
            setAction(v);
            push(writeChatAction(node, { action: v }));
          }}
          options={options}
        />
      </FBlock>
    </div>
  );
}

// ── «Поставить реакцию»: эмодзи на сообщении человека (Ф5.1) ────────────────────────

function ReactionEditor({ node, push, onRemove }: { node: WorkflowNode; push: Push; onRemove?: RemoveRow }) {
  const t = useTranslations('cabinet.scenario');
  const initial = useMemo(() => readChatReaction(node), [node]);
  const [emoji, setEmoji] = useState(initial?.emoji ?? '👍');
  if (!initial) return null;

  const known = CHAT_REACTIONS.some((r) => r.emoji === emoji);
  const options = [
    // Значение из чужой схемы показываем как есть — молча подменять выбор нельзя.
    ...(known ? [] : [{ value: emoji, label: emoji }]),
    // Эмодзи в подписи — не оформление, а КОНТЕНТ клиента: ровно он уедет собеседнику.
    ...CHAT_REACTIONS.map((r) => ({ value: r.emoji, label: `${r.emoji} ${t(r.labelKey)}` })),
  ];
  return (
    <div className="scn-erow">
      <RowHead icon="react" label={t('chatActReaction')} {...removeProps(onRemove, t)} />
      {/* Вводного описания под заголовком шторки НЕТ (владелец 2026-08-11): на что встанет
          реакция — подсказкой под пузырём. Реакций восемь (больше шести) — SmartSelect
          в пузыре; одно поле, имя которого совпадает с именем пузыря, — без FIn. */}
      <FBlock label={t('chatReactionWhat')} hint={t('chatReactionHint')}>
        <SmartSelect
          className="scn-sched-sel"
          value={emoji}
          ariaLabel={t('chatReactionWhat')}
          onChange={(v: string) => {
            setEmoji(v);
            push(writeChatReaction(node, { emoji: v }));
          }}
          options={options}
        />
      </FBlock>
    </div>
  );
}

// ── «Изменить сообщение» и «Сохранить сообщение» (ACTION-STACK Э3, Телеграм) ────────
// Бот переписывает СВОЙ уже отправленный пузырь: по умолчанию последний, либо тот, на
// который раньше поставили закладку. Закладка — обычная переменная карточки человека,
// поэтому имя выбирается тем же списком переменных, что и везде (Ф4).

function EditMsgEditor({
  node,
  push,
  onRemove,
}: {
  node: WorkflowNode;
  push: Push;
  onRemove?: RemoveRow;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatEditMsg(node, lang), [node, lang]);
  const [m, setM] = useState<ChatEditMsgModel | null>(initial);
  if (!initial || !m) return null;

  const apply = (patch: Partial<ChatEditMsgModel>) => {
    const next = { ...m, ...patch };
    setM(next);
    push(writeChatEditMsg(node, next));
  };
  const applyMsg = (patch: Partial<ChatMessageModel>) => apply({ message: { ...m.message, ...patch } });

  return (
    <div className="scn-erow">
      <RowHead icon="edit" label={t('chatActEditMsg')} {...removeProps(onRemove, t)} />
      {/* Два варианта — раскрытым списком с галочкой (канон формы: выбор до шести
          вариантов стоит строками). Подсказка блока объясняет механику словами. */}
      <FChoiceBlock
        label={t('chatEditMsgWhich')}
        hint={t('chatEditMsgWhichHint')}
        value={m.target}
        onChange={(v) => apply({ target: v === 'bookmark' ? 'bookmark' : 'last' })}
        options={[
          { value: 'last', label: t('chatEditMsgLast') },
          { value: 'bookmark', label: t('chatEditMsgByMark') },
        ]}
      />
      {m.target === 'bookmark' ? (
        /* Без имени закладки править нечего — шаг сработал бы вхолостую (Ф9). */
        <FBlock
          label={t('chatEditMsgMark')}
          {...(m.bookmark.trim() === '' ? { error: t('chatEditMsgMarkErr') } : { hint: t('chatEditMsgMarkHint') })}
        >
          <VariablePicker
            value={m.bookmark}
            ariaLabel={t('chatEditMsgMark')}
            onChange={(name) => apply({ bookmark: name })}
          />
        </FBlock>
      ) : null}
      {/* Новый текст — то же поле, что у обычного сообщения (с вставкой «{…}»). */}
      <MsgTextBlock
        label={t('chatEditMsgText')}
        placeholder={t('chatMsgTextPh')}
        hint={t('chatEditMsgTextHint')}
        value={m.message.text}
        onChange={(text) => applyMsg({ text })}
        lang={lang}
      />
      {/* Кнопки — тем же блоком, что у сообщения. Правка отправленного заменяет
          клавиатуру целиком: убрал кнопки здесь — они пропадут и в переписке
          (площадка правит сообщение вместе с кнопками), об этом и говорит подсказка. */}
      <MsgButtonsBlock
        node={node}
        model={m.message}
        channel="telegram"
        onChange={(buttons) => applyMsg({ buttons })}
        hint={t('chatEditMsgButtonsHint')}
      />
    </div>
  );
}

function SaveMsgEditor({
  node,
  push,
  onRemove,
}: {
  node: WorkflowNode;
  push: Push;
  onRemove?: RemoveRow;
}) {
  const t = useTranslations('cabinet.scenario');
  const initial = useMemo(() => readChatSaveMsg(node), [node]);
  const [m, setM] = useState<ChatSaveMsgModel | null>(initial);
  if (!initial || !m) return null;

  const apply = (bookmark: string) => {
    const next = { bookmark };
    setM(next);
    push(writeChatSaveMsg(node, next));
  };

  return (
    <div className="scn-erow">
      <RowHead icon="bookmark" label={t('chatActSaveMsg')} {...removeProps(onRemove, t)} />
      {/* Без имени закладку не найти потом — говорим красным сразу (Ф9). Подсказка
          объясняет механику простыми словами и видима всегда (не тултип). */}
      <FBlock
        label={t('chatSaveMsgMark')}
        hint={t('chatSaveMsgHint')}
        {...(m.bookmark.trim() === '' ? { error: t('chatSaveMsgMarkErr') } : {})}
      >
        <VariablePicker value={m.bookmark} ariaLabel={t('chatSaveMsgMark')} onChange={apply} />
      </FBlock>
    </div>
  );
}

// ── «Отправить заявку»: селект «Куда» + формы приёмников (Ф7.1) ─────────────────────

/** Ответ списка собеседников бота (Ф9): личка из переписки + группы/каналы. */
interface TgRecipientsResp {
  ok?: boolean;
  items?: Array<{
    chatId: string;
    title: string;
    username: string;
    kind: 'person' | 'group' | 'channel';
  }>;
}

function LeadEditor({
  node,
  push,
  creds,
  botCredId,
  onSwitchStep,
  onConnect,
}: {
  node: WorkflowNode;
  push: Push;
  creds: readonly InstanceCredRef[];
  botCredId: string | null;
  onSwitchStep?: ((nodeType: string, params: Record<string, unknown> | null) => void) | undefined;
  onConnect?: (() => void) | undefined;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatLead(node, lang), [node, lang]);
  const [m, setM] = useState<ChatLeadModel | null>(initial);
  // Ф9: получатель «Заявки в Телеграм» выбирается из реальных собеседников бота —
  // обычный человек свой числовой ID чата не знает. Список наш (bot_dialogs/bot_channels):
  // спросить Телеграм «с кем ты общался» нельзя, методов у Bot API нет.
  const [tgManual, setTgManual] = useState(false);
  const { data: rcp } = usePersistentData<TgRecipientsResp>(
    `cabinet:tg-recipients:${botCredId ?? 'none'}`,
    m?.receiver === 'telegram' && botCredId
      ? `/api/cabinet/bot-recipients?credId=${encodeURIComponent(botCredId)}`
      : '',
    { accept: (raw) => !!(raw as TgRecipientsResp)?.ok },
  );
  if (!initial || !m) return null;

  const apply = (patch: Partial<ChatLeadModel>) => {
    const next = { ...m, ...patch };
    setM(next);
    push(writeChatLead(node, next));
  };
  const patchRow = (i: number, patch: Partial<ChatLeadRow>) =>
    apply({ rows: m.rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });

  // Смена «Куда» = замена узла шага на приёмник (по образцу смены типа запуска):
  // общие поля заявки переезжают в параметры нового узла, специфичные человек
  // заполняет на месте. Вебхук — такой же полноценный приёмник (Ф9): общие поля те же,
  // JSON-тело POST-запроса собирает writeChatLead сам.
  const switchTo = (id: ChatLeadReceiver): void => {
    if (id === m.receiver || !onSwitchStep) return;
    // Письмо: адрес отправителя подставляем из SMTP-подключения (его опознаватель —
    // этот же адрес), чтобы обязательное поле не оставалось пустым молча.
    const fromEmail =
      id === 'email' && m.fromEmail.trim() === ''
        ? (creds.find((c) => c.type === 'smtp')?.handle ?? '')
        : m.fromEmail;
    const spec = chatLeadReceiverSpec(id, { ...m, fromEmail });
    onSwitchStep(spec.nodeType, spec.params);
  };

  // Ф7.3: приёмник без подключённого доступа — жёлтая плашка с кнопкой в «Подключения».
  const credType = chatLeadReceiverMeta(m.receiver).credType;
  const connected = credType === null || creds.some((c) => c.type === credType);

  return (
    <>
      {/* Вводного описания под заголовком шторки НЕТ (владелец 2026-08-11) — что делает
          шаг, говорит подсказка ПОД выбором «Куда отправлять». Приёмников семь (больше
          шести) — здесь SmartSelect в пузыре, а не раскрытый список (канон формы). */}
      <FBlock label={t('chatLeadWhere')} hint={t('chatLeadHint')}>
        <SmartSelect
          className="scn-sched-sel"
          value={m.receiver}
          ariaLabel={t('chatLeadWhere')}
          onChange={(v: string) => switchTo(v as ChatLeadReceiver)}
          options={CHAT_LEAD_RECEIVERS.map((r) => ({ value: r.id, label: t(r.nameKey) }))}
        />
      </FBlock>
      {!connected ? <ConnectNote text={t('chatLeadNoCred')} onConnect={onConnect} /> : null}

      {/* Специфичные поля приёмника — сразу под селектом «Куда». */}
      {m.receiver === 'telegram'
        ? (() => {
            const items = rcp?.items ?? [];
            const chosen = items.find((i) => i.chatId === m.chatId.trim()) ?? null;
            // Получатель вписан, но в списке его нет (сценарий из шаблона, ручной ID) —
            // это «вручную»: человек видит своё значение, а не пустую выпадашку.
            const unknown = m.chatId.trim() !== '' && !chosen;
            const manualMode = tgManual || unknown;
            const kindWord = (k: 'person' | 'group' | 'channel') =>
              t(k === 'person' ? 'chatLeadTgKindPerson' : k === 'group' ? 'chatLeadTgKindGroup' : 'chatLeadTgKindChannel');
            const options: SmartOption[] = [
              ...items.map((i) => ({
                value: i.chatId,
                label: i.title || (i.username ? `@${i.username}` : i.chatId),
                desc: `${i.username ? '@' + i.username + ' · ' : ''}${kindWord(i.kind)}`,
              })),
              // «Указать ID вручную» — последним, для продвинутых (Ф9).
              { value: OTHER, label: t('chatLeadTgManual'), desc: t('chatLeadTgManualHint') },
            ];
            return (
              /* Одно поле, имя которого совпадает с именем пузыря — без FIn (канон):
                 выбор собеседника и ручной ID — один и тот же «Получатель». */
              <FBlock
                label={t('chatLeadTgChatId')}
                hint={t(!botCredId || manualMode ? 'chatLeadTgChatIdHint' : 'chatLeadTgManualHint')}
                {...(m.chatId.trim() === ''
                  ? // Получателя нет — заявке некуда идти: красным, а не молчанием (Ф9).
                    { error: t('chatLeadTgChatIdErr') }
                  : {})}
              >
                {/* Бот у сценария не выбран — искать собеседников не у кого: остаётся
                    ручной ID, как раньше. */}
                {botCredId ? (
                  <SmartSelect
                    options={options}
                    value={manualMode ? OTHER : (chosen?.chatId ?? '')}
                    onChange={(v: string) => {
                      if (v === OTHER) {
                        setTgManual(true);
                        return;
                      }
                      setTgManual(false);
                      apply({ chatId: v });
                    }}
                    placeholder={t('chatLeadTgPickPh')}
                    ariaLabel={t('chatLeadTgChatId')}
                  />
                ) : null}
                {!botCredId || manualMode ? (
                  <input
                    value={m.chatId}
                    inputMode="numeric"
                    placeholder="123456789"
                    onChange={(e) => apply({ chatId: e.target.value })}
                    aria-label={t('chatLeadTgChatId')}
                  />
                ) : null}
              </FBlock>
            );
          })()
        : null}
      {m.receiver === 'sheets' ? (
        <FBlock
          label={t('chatLeadSheet')}
          hint={t('chatLeadSheetHint')}
          {...(m.spreadsheet.trim() === '' ? { error: t('chatLeadSheetErr') } : {})}
        >
          <input
            value={m.spreadsheet}
            placeholder="https://docs.google.com/spreadsheets/d/…"
            onChange={(e) => apply({ spreadsheet: e.target.value })}
          />
        </FBlock>
      ) : null}
      {m.receiver === 'email' ? (
        <>
          <FBlock label={t('chatLeadFrom')} hint={t('chatLeadFromHint')}>
            <input
              value={m.fromEmail}
              placeholder="you@site.ru"
              onChange={(e) => apply({ fromEmail: e.target.value })}
            />
          </FBlock>
          <FBlock
            label={t('chatLeadTo')}
            {...(m.toEmail.text.trim() === '' ? { error: t('chatLeadToErr') } : {})}
          >
            <input
              value={m.toEmail.text}
              placeholder="you@site.ru"
              onChange={(e) => apply({ toEmail: { ...m.toEmail, text: e.target.value } })}
            />
            <ValueHint value={m.toEmail} />
          </FBlock>
        </>
      ) : null}
      {m.receiver === 'getCourse' ? (
        <FBlock
          label={t('chatLeadGcEmail')}
          hint={t('chatLeadGcEmailHint')}
          {...(m.gcEmail.text.trim() === '' ? { error: t('chatLeadGcEmailErr') } : {})}
        >
          <input
            value={m.gcEmail.text}
            placeholder={t('chatLeadGcEmailPh')}
            onChange={(e) => apply({ gcEmail: { ...m.gcEmail, text: e.target.value } })}
          />
          <ValueHint value={m.gcEmail} />
        </FBlock>
      ) : null}
      {m.receiver === 'webhook' ? (
        /* Без адреса заявке некуда идти; явный брак формата — туда она тоже не дойдёт (Ф9). */
        <FBlock
          label={t('chatLeadWebhookUrl')}
          hint={t('chatLeadWebhookUrlHint')}
          {...(m.url.text.trim() === ''
            ? { error: t('chatLeadWebhookUrlErr') }
            : chatUrlBroken(m.url.text)
              ? { error: t('chatLeadWebhookUrlFmtErr') }
              : {})}
        >
          <input
            value={m.url.text}
            placeholder="https://example.com/hook"
            onChange={(e) => apply({ url: { ...m.url, text: e.target.value } })}
          />
          <ValueHint value={m.url} />
        </FBlock>
      ) : null}

      {m.supportsTitle ? (
        <FBlock label={m.receiver === 'email' ? t('chatLeadSubject') : t('chatLeadTitle')}>
          <input
            value={m.title.text}
            onChange={(e) => apply({ title: { ...m.title, text: e.target.value } })}
            placeholder={t('chatLeadTitlePh')}
          />
          <ValueHint value={m.title} />
        </FBlock>
      ) : null}

      {/* ОДИН пузырь на все строки заявки (образец — «Кнопки» сообщения): карточек-пузырей
          на каждую строку больше нет («пузырь в пузыре» запрещён, владелец 2026-08-11).
          Строки идут стопкой полей со своими заголовками, конец строки отбивает её «Убрать». */}
      <FBlock label={t('chatLeadRows')}>
        {m.rows.map((r, i) => (
          <Fragment key={i}>
            <FIn label={t('chatLeadRowLabel')}>
              <input
                value={r.label}
                onChange={(e) => patchRow(i, { label: e.target.value })}
                placeholder={t('chatLeadRowLabelPh')}
                aria-label={t('chatLeadRowLabel')}
              />
            </FIn>
            <FIn label={t('chatLeadRowValue')}>
              <input
                value={r.value.text}
                onChange={(e) => patchRow(i, { value: { ...r.value, text: e.target.value } })}
                placeholder={t('chatLeadRowValuePh')}
                aria-label={t('chatLeadRowValue')}
              />
              <ValueHint value={r.value} />
            </FIn>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() => apply({ rows: m.rows.filter((_, idx) => idx !== i) })}
              >
                {t('chatLeadRowRemove')}
              </button>
            </div>
          </Fragment>
        ))}
        <AddDashed
          label={t('chatLeadRowAdd')}
          onClick={() => apply({ rows: [...m.rows, { label: '', value: chatValueOf() }] })}
        />
      </FBlock>

      {m.supportsProfile ? (
        /* Прикладывать анкету или нет — раскрытым списком с галочкой (два варианта,
           канон формы). Вебхук прикладывает канал и логин (в JSON-теле), не всю
           память о человеке — об этом и говорит подсказка снизу. */
        <FChoiceBlock
          label={t('chatLeadProfile')}
          hint={t(m.receiver === 'webhook' ? 'chatLeadProfileHookHint' : 'chatLeadProfileHint')}
          value={m.attachProfile ? 'on' : 'off'}
          onChange={(v) => apply({ attachProfile: v === 'on' })}
          options={[
            { value: 'on', label: t('chatLeadProfileOn') },
            { value: 'off', label: t('chatLeadProfileOff') },
          ]}
        />
      ) : null}
    </>
  );
}

// ── «Оплата»: платёжка из подключённых + что продаём (Ф3, Ф7.2) ──────────────────────
// Робокасса — полный узел «Оплата» (товары раздела «Продукты», счета в нашей базе);
// остальные платёжки — узел «ссылка на оплату» провайдера с названием и ценой прямо в
// шаге. Смена платёжки меняет узел шага (chatPayProviderSpec) с переносом имени/цены.

function PayEditor({
  node,
  onState,
  creds,
  onSwitchStep,
  onConnect,
}: {
  node: WorkflowNode;
  /** Слепок шага с оценкой «готов ли» — галочка шапки не закроет шторку, пока не задан
   *  товар/название/цена (П128 п.11, владелец 19.09.2026); голого `push` для этого мало. */
  onState: (s: ChatStepState) => void;
  creds: readonly InstanceCredRef[];
  onSwitchStep?: ((nodeType: string, params: Record<string, unknown> | null) => void) | undefined;
  onConnect?: (() => void) | undefined;
}) {
  const t = useTranslations('cabinet.scenario');
  const robo = useMemo(() => readChatPay(node), [node]);
  const link = useMemo(() => readChatPayLink(node), [node]);
  const [model, setModel] = useState<ChatPayModel>(robo ?? chatPayModelOf());
  const [linkModel, setLinkModel] = useState<ChatPayLinkModel | null>(link);
  // ПОПЫТКА БЫЛА — только после неё пустой товар/название/цена краснеют; до неё
  // красного в шторке нет (канон: UI-TEMPLATE-CANON «Ошибка всего окна» (П128)).
  const [tried, setTried] = useState(false);
  /** Что мешает шагу: у Робокассы — товар/название/цена, у ссылки — название, цена и
   *  адрес возврата Stripe (по API он обязателен, Ф9). */
  const issueOf = (m: ChatPayModel, l: ChatPayLinkModel | null): boolean =>
    robo
      ? chatPayIssue(m) !== null
      : !!l &&
        (l.name.trim() === '' ||
          chatPayPriceNumber(l.price) === null ||
          (l.provider === 'stripe' && l.successUrl.trim() === ''));
  const emit = (params: Record<string, unknown>, m: ChatPayModel, l: ChatPayLinkModel | null): void =>
    onState({ name: node.name, params, hasIssues: issueOf(m, l), onInvalid: () => setTried(true) });
  // Слепок с оценкой «готов ли шаг» нужен галочке СРАЗУ — до первой правки (иначе шторка
  // закрылась бы на пустом товаре, как было до П128).
  useEffect(() => {
    if (!robo && !link) return;
    emit(robo ? writeChatPay(node, model) : writeChatPayLink(node, linkModel!), model, linkModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- один раз при открытии
  }, []);
  if (!robo && !link) return null;

  const provider: ChatPayProvider = robo ? 'robokassa' : linkModel!.provider;
  // Подключённые платёжки + текущая (даже неподключённая — селект не должен врать).
  const options = CHAT_PAY_PROVIDERS.filter(
    (p) => p.id === provider || creds.some((c) => c.type === p.credType),
  );
  const connected = creds.some((c) => c.type === chatPayProviderMeta(provider).credType);

  const switchTo = (id: ChatPayProvider): void => {
    if (id === provider || !onSwitchStep) return;
    // Имя и цена переезжают: у Робокассы — из разовой оплаты, у провайдеров — из шага.
    const carry = robo
      ? { name: model.customName, price: model.customPriceRub }
      : { name: linkModel?.name ?? '', price: linkModel?.price ?? '' };
    const spec = chatPayProviderSpec(id, carry);
    onSwitchStep(spec.nodeType, spec.params);
  };

  const applyLink = (patch: Partial<ChatPayLinkModel>): void => {
    if (!linkModel) return;
    const next = { ...linkModel, ...patch };
    setLinkModel(next);
    emit(writeChatPayLink(node, next), model, next);
  };
  // Пустая цена — тоже ошибка (Ф9): в узел уехал бы ноль, и счёт не выставился бы молча.
  const linkPriceBad = tried && !!linkModel && chatPayPriceNumber(linkModel.price) === null;

  return (
    <>
      {/* Вводного описания под заголовком шторки НЕТ (владелец 2026-08-11): что продаём
          и откуда берутся имя с ценой, говорят сами пузыри и подсказка снизу. */}
      {/* Ф7.2: текущая платёжка не подключена (в т.ч. не подключено ни одной) — жёлтая
          плашка; одна подключена — выбрана сама и выбора не показываем; несколько — выбор. */}
      {!connected ? <ConnectNote text={t('chatPayNoProvider')} onConnect={onConnect} /> : null}
      {options.length > 1 ? (
        /* Платёжек не больше шести — раскрытый список с галочкой, не выпадашка. */
        <FChoiceBlock
          label={t('chatPayProvider')}
          value={provider}
          onChange={(v: string) => switchTo(v as ChatPayProvider)}
          options={options.map((p) => ({ value: p.id, label: t(p.nameKey) }))}
        />
      ) : null}
      {robo ? (
        <PayProductPicker
          value={model}
          tried={tried}
          onChange={(m) => {
            setModel(m);
            emit(writeChatPay(node, m), m, linkModel);
          }}
        />
      ) : linkModel ? (
        <>
          <FBlock label={t('chatPayCustomName')}>
            <input
              value={linkModel.name}
              maxLength={250}
              placeholder={t('chatPayCustomNamePh')}
              onChange={(e) => applyLink({ name: e.target.value })}
            />
          </FBlock>
          <FBlock
            label={linkModel.provider === 'stripe' ? t('chatPayAmount') : t('chatPayCustomPrice')}
            {...(linkPriceBad ? { error: t('chatPayCustomPriceErr') } : {})}
          >
            <input
              value={linkModel.price}
              inputMode="decimal"
              placeholder="990"
              onChange={(e) => applyLink({ price: e.target.value })}
            />
          </FBlock>
          {/* Чек 54-ФЗ (П2): ЮKassa шлёт чек только на почту покупателя — поле живёт
              в шаге (значение чаще берётся выражением из анкеты). Чек включается в
              настройках подключения ЮKassa; пусто — платёж без чека. */}
          {linkModel.provider === 'yooKassa' ? (
            <FBlock label={t('chatPayEmail')} hint={t('chatPayEmailHint')}>
              <input
                value={linkModel.email}
                placeholder="ivan@example.com"
                onChange={(e) => applyLink({ email: e.target.value })}
              />
            </FBlock>
          ) : null}
          {linkModel.provider === 'stripe' ? (
            <>
              <FBlock label={t('chatPayCurrency')} hint={t('chatPayCurrencyHint')}>
                <input
                  value={linkModel.currency}
                  maxLength={3}
                  placeholder="USD"
                  onChange={(e) => applyLink({ currency: e.target.value })}
                />
              </FBlock>
              {/* По API Stripe адрес возврата обязателен — без него ссылки не будет (Ф9). */}
              <FBlock
                label={t('chatPayReturnUrl')}
                {...(tried && linkModel.successUrl.trim() === ''
                  ? { error: t('chatPayReturnUrlErr') }
                  : { hint: t('chatPayReturnUrlHint') })}
              >
                <input
                  value={linkModel.successUrl}
                  placeholder="https://mysite.com/thanks"
                  onChange={(e) => applyLink({ successUrl: e.target.value })}
                />
              </FBlock>
            </>
          ) : null}
          {/* Товары раздела «Продукты» живут на конвейере Робокассы (счета и страница
              оплаты — наша база): у других платёжек имя и цена — прямо здесь. */}
          <FHint>{t('chatPayLinkHint')}</FHint>
        </>
      ) : null}
    </>
  );
}
